import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Badge, Button, Card, Carousel, Container, Dropdown, Form, InputGroup, Modal, Offcanvas, Spinner } from 'react-bootstrap';
import { FaArrowLeft, FaCheck, FaClock, FaExclamationTriangle, FaGlobe, FaGoogle, FaHome, FaInstagram, FaList, FaMapMarkerAlt, FaMinus, FaPhoneAlt, FaPlus, FaShoppingCart, FaSlidersH, FaTag, FaTelegramPlane, FaTh, FaTrash, FaUser, FaUtensils, FaWhatsapp } from 'react-icons/fa';

import { backendUrl, canSwitchTestDomainBackendToProd, logoImagesPath, productImagesPath, switchTestDomainBackendToProd } from '../App';
import { AuthProfile, useAuthData } from '../contexts/AuthData';
import { ComboSection, FoodMakerMenu, MenuItem, getProductDeliveryConditions, type PriceLevelPriceMap } from '../contexts/MenuData';
import { isMenuLoyaltyCardNumberEnabled, type PartnerDataType, type ProductCardViewMode, usePartnerData, usePartnerDataRefresh } from '../contexts/PartnerData';
import { normalizeLoyaltyCardNumber, sanitizeLoyaltyCardNumberInput, SessionDataType, useSessionData } from '../contexts/SessionData';
import { ContactMethods, UserDataType, useUserData } from '../contexts/UserData';
import {
  fetchPageTranslations,
  PageTextsDataContext,
  TranslationsType,
  usePageTextsData,
} from './utilities';
import { isSimpleOrderPlacementHost, shouldShowFullStockOnMenu } from '../wholesaleBottleShop';
import {
  readShoppingCartStorage,
  saveShoppingCartStorage,
  type ShoppingCartStorage,
  type StoredCartProductItem,
} from '../storageRecovery';
import {
  DATA_LAYER_EVENT_NAMES,
  pushDataLayerEvent,
  type DataLayerEcommercePayload,
  type DataLayerItem,
} from '../analyticsDataLayer';
import { applySimpleOrderPlacementDefaults, calculateTotalProductsCost, normalizeDeliveryNeeded } from '../checkoutOrder';
import {
  formatDeliveryDateValue,
  getCandidateExactTimeOptions,
  getCartFulfilmentConstraints,
  getDeliveryScheduleDayMatch,
  getExactDateTimeForCandidate,
  getProductFulfilmentConstraints,
  isCandidateSelection,
  isExactTimeOptionAvailable,
  resolveFulfilmentSchedule,
  resolveLocationSchedule,
  toTimeInMinutes,
  type DeliveryScheduleDayMatch,
  type DeliveryScheduleTimeSlot,
  type ScheduledIntervalCandidate,
} from '../fulfilmentScheduling';
import { APP_VERSION, BUILD_TIME, PAGE_VERSIONS, formatVersion } from '../versionInfo';
import { SharedOrderData, submitOrderRequest } from '../orderSubmission';
import { fetchDeliveryCost } from '../deliveryCost';
import {
  buildTelegramSupportHref,
  buildTelegramSupportReport,
  resolveTelegramSupportUrl,
  stripTelegramSupportUrls,
} from '../telegramSupport';
import DeliveryMapPicker from '../components/DeliveryMapPicker';

declare global {
  interface Window {
    handleIfoodTelegramAuth?: (telegramPayload: Record<string, unknown>) => void;
  }
}

const DELIVERY_MAP_DEFAULT_CENTER: [number, number] = [42.4396699, 19.2588421];

type MenuCardData = {
  productKey: string;
  name: string;
  description: string;
  descriptionPlain: string;
  categoryLabel: string;
  price: string;
  priceValue: number;
  hasMultipleVersions: boolean;
  unitLabel: string;
  displayImageUrl: string;
  fullImageUrl: string;
  additionalProperties: string[];
  lowStockQuantity: number | null;
  isPurchasable: boolean;
  nextDeliveryText?: string | null;
  isNextDeliveryUnavailable?: boolean;
};

type CategoryPanelItem = {
  key: string;
  label: string;
  count: number;
  displayImageUrl: string;
};

type GroupedMenuCards = {
  categoryLabel: string;
  anchorId: string;
  cards: MenuCardData[];
};

type CategoryScrollOptions = {
  updateHash?: boolean;
  behavior?: ScrollBehavior;
};

type FilterAttributeKey =
  | 'brand_name_code'
  | 'category_code'
  | 'color_name_code'
  | 'manufacturer_name_code'
  | 'production_country_name_code'
  | 'style_name_code'
  | 'sweetness_name_code'
  | 'volume_name_code'
  | 'weight_name_code';

type PromoSlideData = {
  title: string;
  subtitle: string;
  buttonText: string;
  imageUrl: string;
};

type ImagePreviewState = {
  src: string;
  alt: string;
};

const CATEGORY_ANCHOR_PREFIX = 'menu-category-';
const CATEGORY_ANCHOR_TRANSLITERATION: Record<string, string> = {
  а: 'a',
  б: 'b',
  в: 'v',
  г: 'g',
  д: 'd',
  ђ: 'dj',
  е: 'e',
  ё: 'e',
  ж: 'zh',
  з: 'z',
  и: 'i',
  ј: 'j',
  й: 'i',
  к: 'k',
  л: 'l',
  љ: 'lj',
  м: 'm',
  н: 'n',
  њ: 'nj',
  о: 'o',
  п: 'p',
  р: 'r',
  с: 's',
  т: 't',
  ћ: 'c',
  у: 'u',
  ф: 'f',
  х: 'h',
  ц: 'c',
  ч: 'ch',
  џ: 'dz',
  ш: 'sh',
  ы: 'y',
  э: 'e',
  ю: 'yu',
  я: 'ya',
  ь: '',
  ъ: '',
  đ: 'd',
};

/**
 * Category anchors must survive translated names, spaces, punctuation, and
 * duplicate-looking labels. The slug stays human-readable where possible and
 * falls back to stable code points for scripts without a local transliteration.
 */
const buildCategoryAnchorSlug = (categoryLabel: string): string => {
  const transliterated = Array.from(categoryLabel.normalize('NFD'))
    .map((char) => CATEGORY_ANCHOR_TRANSLITERATION[char.toLowerCase()] ?? char)
    .join('')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
  const asciiSlug = transliterated
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  if (asciiSlug.length > 0) {
    return asciiSlug;
  }

  const codePointSlug = Array.from(categoryLabel.trim())
    .map((char) => char.codePointAt(0)?.toString(36))
    .filter((value): value is string => Boolean(value))
    .join('-');

  return codePointSlug.length > 0 ? `category-${codePointSlug}` : 'category';
};

const buildCategoryAnchorIds = (categoryLabels: string[]): Record<string, string> => {
  const slugCounts = new Map<string, number>();

  return categoryLabels.reduce<Record<string, string>>((anchorIds, categoryLabel) => {
    const baseSlug = buildCategoryAnchorSlug(categoryLabel);
    const nextCount = slugCounts.get(baseSlug) ?? 0;
    slugCounts.set(baseSlug, nextCount + 1);
    anchorIds[categoryLabel] = `${CATEGORY_ANCHOR_PREFIX}${nextCount === 0 ? baseSlug : `${baseSlug}-${nextCount + 1}`}`;
    return anchorIds;
  }, {});
};

const getCategoryAnchorIdFromHash = (hash: string): string | null => {
  const rawHash = hash.startsWith('#') ? hash.slice(1) : hash;
  if (rawHash.trim().length === 0) {
    return null;
  }

  try {
    return decodeURIComponent(rawHash);
  } catch {
    return rawHash;
  }
};

type ImageVariant = 'thumb' | 'card' | 'detail' | 'galleryPreview' | 'full';

type PartnerGalleryPhoto = {
  displayImageUrl: string;
  fullImageUrl: string;
};

type LanguageOption = {
  code: string;
  label: string;
};

const WEEKDAY_KEYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'] as const;

type PartnerScheduleWeekday = typeof WEEKDAY_KEYS[number];

type PartnerDailyScheduleDay = {
  local_open?: string | null;
  local_close?: string | null;
};

type PartnerDailyScheduleLocationSettings = {
  is_cashless_terminal_available?: boolean | null;
};

type PartnerDailySchedule = Record<
  string,
  PartnerDailyScheduleDay | PartnerDailyScheduleLocationSettings | string | null | undefined
> & {
  schedule_description_text_code?: string | null;
};

type PartnerNextOpeningInfo = {
  date: Date;
  openTime: string;
};

type SocialLinkItem = {
  key: string;
  href: string;
  label: string;
  icon: React.ReactNode;
};

type VersionPriceMap = Record<string, number | string>;
type PriceLevelValue = number | string | VersionPriceMap | null | undefined;
type ProductVersionLabels = string[] | Record<string, string> | null | undefined;
type MenuItemWithKey = Omit<MenuItem, 'price' | 'version_price' | 'product_versions' | 'no_version_price'> & {
  product_key: string;
  price?: number | string | VersionPriceMap | null;
  version_price?: VersionPriceMap | null;
  version_prices_by_levels?: PriceLevelPriceMap | null;
  product_versions: ProductVersionLabels;
  no_version_price?: number | string | null;
};

type MenuItemWithPrice = Omit<MenuItem, 'price' | 'version_price' | 'product_versions' | 'no_version_price'> & {
  price?: number | string | VersionPriceMap | null;
  version_price?: VersionPriceMap | null;
  version_prices_by_levels?: PriceLevelPriceMap | null;
  product_versions: ProductVersionLabels;
  no_version_price?: number | string | null;
};

type CartProductItem = StoredCartProductItem;

type ShoppingCart = CartProductItem[];

type PromoData = {
  promo_code: string;
  promo_name_code: string;
  partner_code: string | null;
  promo_type: string;
  condition_amount: number | null;
  condition_products: string[];
  promo_amount: number;
  promo_products: string[];
  for_new_clients: boolean;
};

type MenuOrderData = SharedOrderData;

type PartialOrderDataUpdater = (patch: Partial<MenuOrderData>) => void;

type OrderFormErrors = {
  name: boolean;
  countryCode: boolean;
  phone: boolean;
  telegramName: boolean;
  deliveryPoint: boolean;
  deliveryAddress: boolean;
  deliveryDate: boolean;
  deliveryTime: boolean;
  deliveryInterval: boolean;
  changeAmount: boolean;
};

type CheckoutStep = 'cart' | 'checkout' | 'confirm' | 'receipt';

type ProfileModalLabels = {
  profile: string;
  signIn: string;
  email: string;
  sendMagicLink: string;
  magicLinkSent: string;
  google: string;
  telegram: string;
  authUnavailable: string;
  name: string;
  preferredMethod: string;
  countryCode: string;
  phone: string;
  telegramUsername: string;
  language: string;
  location: string;
  address: string;
  save: string;
  logout: string;
  close: string;
  saving: string;
};

type DeliveryZoneMatch = {
  zoneKey: string;
  price: number;
  free_delivery_amount: number;
  name: string;
};

type DeliveryCoordinates = [number, number];

type DeliveryDayOption = {
  label: string;
  value: string;
  date: Date;
};

type TranslationResolver = (key: string, fallback: string) => string;

type DeliveryScheduleSelection = {
  isAsapOnly: boolean;
  isExactTimeAllowed: boolean;
  dayOptions: DeliveryDayOption[];
  selectedDayIntervals: string[];
  selectedDayCandidates: ScheduledIntervalCandidate[];
  selectedIntervalCandidate: ScheduledIntervalCandidate | null;
  readinessTime: Date;
};

type DeliveryMapContext = {
  activeLocation: string;
  partnerLocation: string;
  initialCenter: DeliveryCoordinates;
  selectedPosition: DeliveryCoordinates | null;
  effectiveCoordinates: DeliveryCoordinates | null;
};

type ReceiptSummary = {
  subtotal: number;
  deliveryCost: number;
  total: number;
};

type ReceiptNotice = {
  type: 'success' | 'error';
  message: string;
  supportHref?: string | null;
};

type CountryCodeOption = {
  value: string;
  label: string;
};

type CheckoutLabels = {
  [key: string]: string | CountryCodeOption[];
  yourCart: string;
  checkout: string;
  reviewOrder: string;
  receipt: string;
  cartEmpty: string;
  remove: string;
  quantity: string;
  unitPrice: string;
  enterPromo: string;
  promoApplied: string;
  promoInvalid: string;
  promoFailed: string;
  adds: string;
  combo: string;
  promoPlaceholder: string;
  applying: string;
  apply: string;
  subtotal: string;
  proceedToCheckout: string;
  continueShopping: string;
  yourName: string;
  preferredMethod: string;
  selectCountryCode: string;
  phoneNumber: string;
  phoneMethod: string;
  telegramUsername: string;
  countryCodeOptions: CountryCodeOption[];
  howToGet: string;
  selfPickup: string;
  delivery: string;
  deliveryPoint: string;
  tapToChoose: string;
  selectDeliveryPoint: string;
  expandMap: string;
  collapseMap: string;
  address: string;
  entrance: string;
  code: string;
  floor: string;
  apartment: string;
  paymentMethod: string;
  cash: string;
  bankCard: string;
  needChange: string;
  yes: string;
  no: string;
  changeAmount: string;
  deliveryDay: string;
  deliveryInterval: string;
  pickupDay: string;
  pickupInterval: string;
  deliveryTime: string;
  whenReady: string;
  atTheTime: string;
  selectTime: string;
  noDeliveryTimesAvailable: string;
  noPickupIntervalsAvailable: string;
  loyaltyCardNumber: string;
  enterLoyaltyCardNumber: string;
  additionalInstructions: string;
  anythingElse: string;
  orderDetails: string;
  courierInstructions: string;
  prepareChangeAmount: string;
  mapLoading: string;
  mapLoadFailed: string;
  mapUnavailableDescription: string;
  retry: string;
  reset: string;
  deliveryInstructionsPlaceholder: string;
  back: string;
  confirm: string;
  deliveryCost: string;
  calculatingDeliveryCost: string;
  deliveryCostFailed: string;
  deliveryCostMissingData: string;
  managerDefining: string;
  total: string;
  deliveryZone: string;
  cartItemsUnavailableTitle: string;
  cartItemsUnavailableBody: string;
  connectWithSupport: string;
};

type DeliveryCostState = {
  status: 'idle' | 'loading' | 'success' | 'error';
  cost: number | null;
  minimumOrderAmount: number;
  message: string | null;
};

const LANGUAGES: LanguageOption[] = [
  { code: 'cn', label: 'Crnogorski (CN)' },
  { code: 'en', label: 'English (EN)' },
  { code: 'ru', label: 'Русский (RU)' },
  { code: 'tr', label: 'Türkçe (TR)' },
  { code: 'ua', label: 'Українська (UA)' },
];

const SHOW_PROMO_SLIDER = false;
const SECTION_SHADOW = '0 0.5rem 1.25rem rgba(30, 30, 30, 0.08)';
const PRODUCT_CARD_SHADOW = '0 0.5rem 1.5rem rgba(30, 30, 30, 0.10)';
const CART_STORAGE_TTL_MS = 8 * 60 * 60 * 1000;
const MENU_CARD_PROPERTIES: Array<'volume_name_code' | 'weight_name_code' | 'sweetness_name_code' | 'style_name_code' | 'brand_name_code' | 'color_name_code'> = [
  'volume_name_code',
  'weight_name_code',
  'sweetness_name_code',
  'style_name_code',
  'brand_name_code',
  'color_name_code',
];

const FALLBACK_PARTNER = {
  name: 'I-FOOD',
  description:
    'Organic, locally-sourced ingredients served in a sustainable, cozy atmosphere. The best brunch spot in Green City.',
  schedule: '08:00 - 22:00',
  phone: '+1 234 567 890',
  address: '123 Eco Street, Green City',
};

const FALLBACK_HERO_IMAGE =
  'https://lh3.googleusercontent.com/aida-public/AB6AXuAwdHygf-8Orz9rudpksn0PCqB1aPljImMDR0Qv2mINP5fxio4VCHnb9-mvAHaFTkM5MqM2XvVY6PSulcqT5j1RkMUjOt6xHtv7DW0pkr3uRnFdboYDTDUT5f51jtUz4GgJcvwnY6io7rINIT_zmA801tGfB4tyKo2YGs_I_0b4yyYlVnsN05uLxISZMrPCXlqhvssOaFn9xXhJc_IOEY39xZoY43szB6L0go1ghVEszCPKG6dhvlNYe35ZrGDnPvDPPZGau7waMtd2';

const DEFAULT_CATEGORY_IMAGE =
  'https://placehold.co/96x96/E9ECEF/6C757D?text=Food';

const FILTER_ATTRIBUTE_KEYS: FilterAttributeKey[] = [
  'brand_name_code',
  'category_code',
  'color_name_code',
  'manufacturer_name_code',
  'production_country_name_code',
  'style_name_code',
  'sweetness_name_code',
  'volume_name_code',
  'weight_name_code',
];

const FILTER_ATTRIBUTE_LABELS: Record<FilterAttributeKey, string> = {
  brand_name_code: 'Brand',
  category_code: 'Category',
  color_name_code: 'Color',
  manufacturer_name_code: 'Manufacturer',
  production_country_name_code: 'Production country',
  style_name_code: 'Style',
  sweetness_name_code: 'Sweetness',
  volume_name_code: 'Volume',
  weight_name_code: 'Weight',
};

const BOOTSTRAP_SM_BREAKPOINT = 576;
const PRODUCT_CARD_VIEW_MODES: ProductCardViewMode[] = ['compact', 'grid', 'list'];
const TILDA_HOST_PATTERN = /(^|\.)tildacdn\.(com|one|cloud)$/i;
const IMAGE_VARIANT_WIDTHS: Record<Exclude<ImageVariant, 'full'>, number> = {
  thumb: 128,
  card: 360,
  detail: 960,
  galleryPreview: 960,
};

const getResponsiveDefaultProductView = (): ProductCardViewMode => (
  typeof window !== 'undefined' && window.innerWidth >= BOOTSTRAP_SM_BREAKPOINT
    ? 'grid'
    : 'compact'
);

const getMenuFrontendPageParams = (partnerData: PartnerDataType | null | undefined) => (
  partnerData?.frontend_pages_param?.Menu ?? null
);

const resolveMenuDefaultProductView = (partnerData: PartnerDataType | null | undefined): ProductCardViewMode | null => {
  const rawDefaultView = getMenuFrontendPageParams(partnerData)?.default_product_view;

  return typeof rawDefaultView === 'string' && PRODUCT_CARD_VIEW_MODES.includes(rawDefaultView as ProductCardViewMode)
    ? (rawDefaultView as ProductCardViewMode)
    : null;
};

const resolveDoProductParametersShow = (partnerData: PartnerDataType | null | undefined): boolean => (
  getMenuFrontendPageParams(partnerData)?.do_product_parameters_show !== false
);

const FALLBACK_MENU_CARDS: MenuCardData[] = [
  {
    productKey: 'fallback_1',
    name: 'Classic Avo Toast',
    description: 'Sourdough, avocado, poached egg',
    descriptionPlain: 'Sourdough, avocado, poached egg',
    categoryLabel: 'Breakfast',
    price: '\u20AC 12.5',
    priceValue: 12.5,
    hasMultipleVersions: false,
    unitLabel: '',
    displayImageUrl:
      'https://lh3.googleusercontent.com/aida-public/AB6AXuB9KNs62O--HK8N2J8DWlXRol9sDHAk6ahnBmI19IfmGFQDUufBNsnzCf80yD0EKwkrOSxsVYoNV9Z4HVY2GJcSsTGXSBlPjw87jqHsSUn4JSlR8_RhzjlwIDYME8r8OCOtL9vvXuRJ_2dInJJiElv586losljp7v7wzGcMiMoZ55sKTnGHl8R-2mPwXMpbeUbvPF2rJYffXFAHwRYXI07DThq8LrT5_7v5QrjSlJT4UCG341bRhIQDqJRIR08hzU__K3n13tlQ1upL',
    fullImageUrl:
      'https://lh3.googleusercontent.com/aida-public/AB6AXuB9KNs62O--HK8N2J8DWlXRol9sDHAk6ahnBmI19IfmGFQDUufBNsnzCf80yD0EKwkrOSxsVYoNV9Z4HVY2GJcSsTGXSBlPjw87jqHsSUn4JSlR8_RhzjlwIDYME8r8OCOtL9vvXuRJ_2dInJJiElv586losljp7v7wzGcMiMoZ55sKTnGHl8R-2mPwXMpbeUbvPF2rJYffXFAHwRYXI07DThq8LrT5_7v5QrjSlJT4UCG341bRhIQDqJRIR08hzU__K3n13tlQ1upL',
    additionalProperties: [],
    lowStockQuantity: null,
    isPurchasable: true,
  },
  {
    productKey: 'fallback_2',
    name: 'Garden Super Bowl',
    description: 'Quinoa, kale, roasted chickpeas',
    descriptionPlain: 'Quinoa, kale, roasted chickpeas',
    categoryLabel: 'Main dishes',
    price: '\u20AC 14.0',
    priceValue: 14,
    hasMultipleVersions: false,
    unitLabel: '',
    displayImageUrl:
      'https://lh3.googleusercontent.com/aida-public/AB6AXuChFG55JYHEoNikHOC7-j1JzGvLnsmsPYk7fV1EC0Yi9mHSqIK-5bsqhzvoMmIkdQ9CCi3VGoLEkOqmytnnUg58RVIMjGpYCH_krcgFc2uzZWNoDeMJyaYnj_LGaDVIAwwBVJCd37XsfQJmicfIC4Hr_ulxSdKYgluby1PsLhpfH5e0HoFBhvyVzzwLCFy73i9O4d3aFric8GBLvIp5ahgobiGksBushTyoNkKOfFhPFq_8vsXKW4rMQoxthtPQwRMn9BDn748H5NL6',
    fullImageUrl:
      'https://lh3.googleusercontent.com/aida-public/AB6AXuChFG55JYHEoNikHOC7-j1JzGvLnsmsPYk7fV1EC0Yi9mHSqIK-5bsqhzvoMmIkdQ9CCi3VGoLEkOqmytnnUg58RVIMjGpYCH_krcgFc2uzZWNoDeMJyaYnj_LGaDVIAwwBVJCd37XsfQJmicfIC4Hr_ulxSdKYgluby1PsLhpfH5e0HoFBhvyVzzwLCFy73i9O4d3aFric8GBLvIp5ahgobiGksBushTyoNkKOfFhPFq_8vsXKW4rMQoxthtPQwRMn9BDn748H5NL6',
    additionalProperties: [],
    lowStockQuantity: null,
    isPurchasable: true,
  },
  {
    productKey: 'fallback_3',
    name: 'House Cappuccino',
    description: 'Double shot, steamed milk',
    descriptionPlain: 'Double shot, steamed milk',
    categoryLabel: 'Drinks',
    price: '\u20AC 4.5',
    priceValue: 4.5,
    hasMultipleVersions: false,
    unitLabel: '',
    displayImageUrl:
      'https://lh3.googleusercontent.com/aida-public/AB6AXuA0ep1Vfmq9hsivh6efjwc92TLfSB_GG6GZ8sjCPa3Aae0DKAJeJV9QQ9yCSUgkzsyzvfpJR5TFLBaFF1tUz6ZBN7NZMDYrzFTqmPSiVWsOrQozxsEeZnQbO5Xj6YOboVwRwYwzyg3_6coiFZd7uaQ3BwsvHelmqN1Tb1vI2vvRJI_8PVcoJeXaiRSXVtD4tFEGQwicvk-C8DUNbjk8a0ZUE3C00KZy6IRnZVt3aVAMfxzgHy8u18Rovl_ACeoF_pKOI-lS74AMHe5z',
    fullImageUrl:
      'https://lh3.googleusercontent.com/aida-public/AB6AXuA0ep1Vfmq9hsivh6efjwc92TLfSB_GG6GZ8sjCPa3Aae0DKAJeJV9QQ9yCSUgkzsyzvfpJR5TFLBaFF1tUz6ZBN7NZMDYrzFTqmPSiVWsOrQozxsEeZnQbO5Xj6YOboVwRwYwzyg3_6coiFZd7uaQ3BwsvHelmqN1Tb1vI2vvRJI_8PVcoJeXaiRSXVtD4tFEGQwicvk-C8DUNbjk8a0ZUE3C00KZy6IRnZVt3aVAMfxzgHy8u18Rovl_ACeoF_pKOI-lS74AMHe5z',
    additionalProperties: [],
    lowStockQuantity: null,
    isPurchasable: true,
  },
  {
    productKey: 'fallback_4',
    name: 'Wild Mushroom Pizza',
    description: 'Truffle oil, arugula, mozzarella',
    descriptionPlain: 'Truffle oil, arugula, mozzarella',
    categoryLabel: 'Main dishes',
    price: '\u20AC 18.0',
    priceValue: 18,
    hasMultipleVersions: false,
    unitLabel: '',
    displayImageUrl:
      'https://lh3.googleusercontent.com/aida-public/AB6AXuA3V30rJxLV1wH_yqzqvO84TV-jciT8zLwOMfPz2coIPKvQ930ldb0_aVNF3vZ8SjjmDFpcOz566_kkiw5KXArfTxv1UCQS8UE8hFvj1_G8oJwKARgmhQeW1ptSQCByeoBS6u6xkEwhLjJLIzTxb8lrY5EMPD1TtTEMl4MbRS9mP5zj0okARvrOB97oanM322sSLcdbevHa0bG9jNkRq27WA_7kaqKw8iNeymmSUsusRwsW1bxKLGbMT5f7GxgNj8FaI8i5G2dvz5c5',
    fullImageUrl:
      'https://lh3.googleusercontent.com/aida-public/AB6AXuA3V30rJxLV1wH_yqzqvO84TV-jciT8zLwOMfPz2coIPKvQ930ldb0_aVNF3vZ8SjjmDFpcOz566_kkiw5KXArfTxv1UCQS8UE8hFvj1_G8oJwKARgmhQeW1ptSQCByeoBS6u6xkEwhLjJLIzTxb8lrY5EMPD1TtTEMl4MbRS9mP5zj0okARvrOB97oanM322sSLcdbevHa0bG9jNkRq27WA_7kaqKw8iNeymmSUsusRwsW1bxKLGbMT5f7GxgNj8FaI8i5G2dvz5c5',
    additionalProperties: [],
    lowStockQuantity: null,
    isPurchasable: true,
  },
];

const encodeAssetPathSegment = (segment: string): string => {
  try {
    return encodeURIComponent(decodeURIComponent(segment));
  } catch {
    return encodeURIComponent(segment);
  }
};

/**
 * Backend-served asset filenames may contain spaces, percent signs, or
 * non-ASCII symbols. The customer menu must percent-encode each path segment
 * before requesting those files; otherwise some valid product photos fall back
 * to the cafe logo because the browser requests a malformed URL.
 */
const normalizeRelativeAssetPath = (value: string): string => (
  value
    .split('/')
    .filter((segment) => segment.length > 0)
    .map(encodeAssetPathSegment)
    .join('/')
);

const toAbsoluteAssetUrl = (value: string | null | undefined, basePath: string): string | null => {
  if (!value) {
    return null;
  }
  if (value.startsWith('http')) {
    return value;
  }

  return `${basePath}${normalizeRelativeAssetPath(value)}`;
};

const isTransformableTildaHost = (hostname: string): boolean => TILDA_HOST_PATTERN.test(hostname);

const buildTildaVariantUrl = (sourceUrl: string, width: number): string => {
  try {
    const parsedUrl = new URL(sourceUrl);
    const pathSegments = parsedUrl.pathname.split('/').filter(Boolean);
    if (pathSegments.length === 0) {
      return sourceUrl;
    }

    const fileName = pathSegments[pathSegments.length - 1];
    const firstTransformMarkerIndex = pathSegments.indexOf('-');
    const baseSegments = firstTransformMarkerIndex >= 0
      ? pathSegments.slice(0, firstTransformMarkerIndex)
      : pathSegments.slice(0, -1);

    if (baseSegments.length === 0) {
      return sourceUrl;
    }

    parsedUrl.pathname = `/${baseSegments.join('/')}/-/resize/${width}x/${fileName}`;
    return parsedUrl.toString();
  } catch {
    return sourceUrl;
  }
};

/**
 * Customer-facing menu previews should request lighter CDN variants wherever
 * the upstream Tilda URL format supports size transforms. Full-size assets are
 * preserved for explicit zoom/slider actions, while backend-served `/files/...`
 * assets stay untouched until a real thumbnail API exists.
 */
const getImageVariantUrl = (sourceUrl: string, variant: ImageVariant): string => {
  if (variant === 'full') {
    return sourceUrl;
  }

  try {
    const parsedUrl = new URL(sourceUrl);
    if (parsedUrl.pathname.includes('/files/') || !isTransformableTildaHost(parsedUrl.hostname)) {
      return sourceUrl;
    }

    return buildTildaVariantUrl(sourceUrl, IMAGE_VARIANT_WIDTHS[variant]);
  } catch {
    return sourceUrl;
  }
};

const getItemImageUrl = (
  item: { image?: string | null } | undefined | null,
  variant: ImageVariant = 'full'
): string | null => {
  const sourceUrl = toAbsoluteAssetUrl(item?.image, productImagesPath);
  return sourceUrl ? getImageVariantUrl(sourceUrl, variant) : null;
};

const toDisplayPrice = (item: MenuItemWithPrice, priceLevel: number | string | null | undefined): string => {
  return formatEuro(getEffectiveItemPrice(item, priceLevel));
};

const isMenuItemPurchasable = (item: MenuItemWithPrice, priceLevel: number | string | null | undefined): boolean => (
  item.is_available && getEffectiveItemPrice(item, priceLevel) > 0
);

type CartAvailabilityCheckResult = {
  validCart: ShoppingCart;
  unavailableItemNames: string[];
};

const toPlainText = (value: string | null | undefined): string => {
  if (!value) {
    return '';
  }
  return value.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
};

const toSearchableText = (value: string): string => value.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

const getHtmlOrFallback = (value: string | null | undefined, fallback: string): string => {
  if (!value || value.trim().length === 0) {
    return fallback;
  }
  return value;
};

const toModalHtml = (value: string | null | undefined): string => {
  if (!value || value.trim().length === 0) {
    return '';
  }

  const trimmedValue = value.trim();
  if (/<[a-z][\s\S]*>/i.test(trimmedValue)) {
    return trimmedValue;
  }

  return trimmedValue
    .split(/\r?\n\r?\n/)
    .map((paragraph) => `<p>${paragraph.trim().replace(/\r?\n/g, '<br />')}</p>`)
    .join('');
};

const joinModalHtmlSections = (...sections: Array<string | null | undefined>): string => (
  sections
    .filter((section): section is string => Boolean(section && section.trim().length > 0))
    .join('<p>&nbsp;</p>')
);

const toPartnerPhotoUrl = (photo: string, photoBase: string, variant: ImageVariant = 'full'): string => {
  const sourceUrl = toAbsoluteAssetUrl(photo, photoBase);
  return sourceUrl ? getImageVariantUrl(sourceUrl, variant) : '';
};

const isUnknownRecord = (value: unknown): value is Record<string, unknown> => (
  typeof value === 'object' && value !== null && !Array.isArray(value)
);

const getWeekdayScheduleEntry = (
  schedule: PartnerDailySchedule | null | undefined,
  weekday: PartnerScheduleWeekday
): PartnerDailyScheduleDay | null => {
  if (!schedule) {
    return null;
  }

  const rawEntry = schedule[weekday];
  if (!rawEntry || Array.isArray(rawEntry) || typeof rawEntry !== 'object') {
    return null;
  }

  const recordEntry = rawEntry as Record<string, unknown>;
  const localOpen = typeof recordEntry.local_open === 'string' ? recordEntry.local_open : null;
  const localClose = typeof recordEntry.local_close === 'string' ? recordEntry.local_close : null;

  if (!localOpen && !localClose) {
    return null;
  }

  return {
    local_open: localOpen,
    local_close: localClose,
  };
};

const getScheduleDescriptionTextCode = (
  schedule: PartnerDailySchedule | null | undefined
): string | null => {
  if (!schedule) {
    return null;
  }

  const rawValue = schedule.schedule_description_text_code;
  return typeof rawValue === 'string' && rawValue.trim().length > 0 ? rawValue.trim() : null;
};

const isCurrentTimeInsideScheduleWindow = (
  currentMinutes: number,
  openMinutes: number,
  closeMinutes: number
): boolean => {
  if (closeMinutes > openMinutes) {
    return currentMinutes >= openMinutes && currentMinutes < closeMinutes;
  }

  return currentMinutes >= openMinutes || currentMinutes < closeMinutes;
};

const formatDeliveryDayOptionLabel = (
  date: Date,
  language: string,
  translate: TranslationResolver,
  now: Date = new Date(),
): string => {
  const todayValue = formatDeliveryDateValue(now);
  const tomorrowDate = new Date(now);
  tomorrowDate.setHours(0, 0, 0, 0);
  tomorrowDate.setDate(now.getDate() + 1);

  const optionValue = formatDeliveryDateValue(date);
  if (optionValue === todayValue) {
    return translate('today', 'Today');
  }

  if (optionValue === formatDeliveryDateValue(tomorrowDate)) {
    return translate('tomorrow', 'Tomorrow');
  }

  return new Intl.DateTimeFormat(toIntlLanguage(language), {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  }).format(date);
};

const isDeliverySlotValid = ([from, to]: DeliveryScheduleTimeSlot): boolean => {
  const fromMinutes = toTimeInMinutes(from);
  const toMinutes = toTimeInMinutes(to);
  return fromMinutes != null && toMinutes != null && fromMinutes !== toMinutes;
};

const isDeliverySlotStillAvailableToday = ([from, to]: DeliveryScheduleTimeSlot, currentMinutes: number): boolean => {
  const fromMinutes = toTimeInMinutes(from);
  const toMinutes = toTimeInMinutes(to);
  if (fromMinutes == null || toMinutes == null || fromMinutes === toMinutes) {
    return false;
  }

  return toMinutes > fromMinutes
    ? currentMinutes < toMinutes
    : true;
};

const getDeliveryIntervalsForDate = (
  rawSchedule: unknown,
  date: Date,
  now: Date = new Date(),
): string[] => {
  const match = getDeliveryScheduleDayMatch(rawSchedule, date);
  if (!match) {
    return [];
  }

  if (match.allDay) {
    return ['00:00 - 23:59'];
  }

  const currentMinutes = (now.getHours() * 60) + now.getMinutes();
  const isToday = formatDeliveryDateValue(date) === formatDeliveryDateValue(now);
  return match.slots
    .filter(isDeliverySlotValid)
    .filter((slot) => !isToday || isDeliverySlotStillAvailableToday(slot, currentMinutes))
    .map(([from, to]) => `${from} - ${to}`);
};

const formatFulfilmentDateTime = (date: Date, language: string): string => new Intl.DateTimeFormat(
  toIntlLanguage(language),
  { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' },
).format(date);

const formatTimeInputValue = (date: Date): string => (
  `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
);

/**
 * Scheduled delivery can be requested only for concrete days that still have a
 * valid schedule match. The helper scans the next two weeks but keeps only the
 * first four selectable days so the checkout schedule stays compact on mobile.
 * behavior while keeping interval filtering in one place for validation and UI.
 */
const getAvailableDeliveryDayOptions = (
  rawSchedule: unknown,
  language: string,
  translate: TranslationResolver,
  now: Date = new Date(),
): DeliveryDayOption[] => {
  if (!isUnknownRecord(rawSchedule)) {
    return [];
  }

  const options: DeliveryDayOption[] = [];
  for (let dayOffset = 0; dayOffset < 14 && options.length < 4; dayOffset += 1) {
    const candidateDate = new Date(now);
    candidateDate.setHours(0, 0, 0, 0);
    candidateDate.setDate(now.getDate() + dayOffset);

    const match = getDeliveryScheduleDayMatch(rawSchedule, candidateDate);
    if (!match) {
      continue;
    }

    if (!match.allDay && getDeliveryIntervalsForDate(rawSchedule, candidateDate, now).length === 0) {
      continue;
    }

    options.push({
      date: candidateDate,
      value: formatDeliveryDateValue(candidateDate),
      label: formatDeliveryDayOptionLabel(candidateDate, language, translate, now),
    });
  }

  return options;
};

const isDeliveryDayMatchOpenAtTime = (
  match: DeliveryScheduleDayMatch,
  currentMinutes: number,
  previousDayOvernightOnly = false,
): boolean => {
  if (match.allDay) {
    return !previousDayOvernightOnly;
  }

  return match.slots.some(([from, to]) => {
    const openMinutes = toTimeInMinutes(from);
    const closeMinutes = toTimeInMinutes(to);
    if (openMinutes == null || closeMinutes == null || openMinutes === closeMinutes) {
      return false;
    }

    if (previousDayOvernightOnly) {
      return closeMinutes < openMinutes && currentMinutes < closeMinutes;
    }

    return isCurrentTimeInsideScheduleWindow(currentMinutes, openMinutes, closeMinutes);
  });
};

const isDeliveryScheduleOpenNow = (
  rawSchedule: unknown,
  now: Date = new Date(),
): boolean | null => {
  if (!isUnknownRecord(rawSchedule)) {
    return null;
  }

  const currentMinutes = (now.getHours() * 60) + now.getMinutes();
  const todayMatch = getDeliveryScheduleDayMatch(rawSchedule, now);
  if (todayMatch && isDeliveryDayMatchOpenAtTime(todayMatch, currentMinutes)) {
    return true;
  }

  const previousDate = new Date(now);
  previousDate.setDate(previousDate.getDate() - 1);
  const previousDayMatch = getDeliveryScheduleDayMatch(rawSchedule, previousDate);
  if (
    previousDayMatch
    && isDeliveryDayMatchOpenAtTime(previousDayMatch, currentMinutes, true)
  ) {
    return true;
  }

  return false;
};

const isPartnerOpenNow = (
  schedule: PartnerDailySchedule | null | undefined,
  now: Date = new Date()
): boolean => {
  if (!schedule) {
    return true;
  }

  const currentMinutes = (now.getHours() * 60) + now.getMinutes();
  const todaySchedule = getWeekdayScheduleEntry(schedule, WEEKDAY_KEYS[now.getDay()]);
  if (todaySchedule) {
    const openMinutes = toTimeInMinutes(todaySchedule.local_open);
    const closeMinutes = toTimeInMinutes(todaySchedule.local_close);
    if (
      openMinutes != null
      && closeMinutes != null
      && openMinutes !== closeMinutes
      && isCurrentTimeInsideScheduleWindow(currentMinutes, openMinutes, closeMinutes)
    ) {
      return true;
    }
  }

  const previousDate = new Date(now);
  previousDate.setDate(previousDate.getDate() - 1);
  const previousDaySchedule = getWeekdayScheduleEntry(schedule, WEEKDAY_KEYS[previousDate.getDay()]);
  if (!previousDaySchedule) {
    return false;
  }

  const previousDayOpenMinutes = toTimeInMinutes(previousDaySchedule.local_open);
  const previousDayCloseMinutes = toTimeInMinutes(previousDaySchedule.local_close);
  if (
    previousDayOpenMinutes == null
    || previousDayCloseMinutes == null
    || previousDayOpenMinutes === previousDayCloseMinutes
    || previousDayCloseMinutes > previousDayOpenMinutes
  ) {
    return false;
  }

  return currentMinutes < previousDayCloseMinutes;
};

const getPartnerNextOpening = (
  schedule: PartnerDailySchedule | null | undefined,
  now: Date = new Date()
): PartnerNextOpeningInfo | null => {
  if (!schedule || isPartnerOpenNow(schedule, now)) {
    return null;
  }

  const currentMinutes = (now.getHours() * 60) + now.getMinutes();

  for (let dayOffset = 0; dayOffset < 8; dayOffset += 1) {
    const candidateDate = new Date(now);
    candidateDate.setHours(0, 0, 0, 0);
    candidateDate.setDate(candidateDate.getDate() + dayOffset);

    const weekdaySchedule = getWeekdayScheduleEntry(schedule, WEEKDAY_KEYS[candidateDate.getDay()]);
    if (!weekdaySchedule) {
      continue;
    }

    const openMinutes = toTimeInMinutes(weekdaySchedule.local_open);
    const closeMinutes = toTimeInMinutes(weekdaySchedule.local_close);
    if (openMinutes == null || closeMinutes == null || openMinutes === closeMinutes) {
      continue;
    }

    if (dayOffset === 0) {
      const opensLaterToday = closeMinutes > openMinutes
        ? currentMinutes < openMinutes
        : currentMinutes >= closeMinutes && currentMinutes < openMinutes;

      if (!opensLaterToday) {
        continue;
      }
    }

    return {
      date: candidateDate,
      openTime: weekdaySchedule.local_open ?? '',
    };
  }

  return null;
};

const toIntlLanguage = (language: string): string => {
  switch (language) {
    case 'cn':
      return 'sr-ME';
    case 'ru':
      return 'ru-RU';
    case 'tr':
      return 'tr-TR';
    case 'ua':
      return 'uk-UA';
    default:
      return 'en-US';
  }
};

const formatNextOpeningLabel = (
  nextOpening: PartnerNextOpeningInfo | null,
  language: string
): string => {
  if (!nextOpening) {
    return '';
  }

  const weekdayLabel = new Intl.DateTimeFormat(toIntlLanguage(language), { weekday: 'long' }).format(nextOpening.date);
  return `${weekdayLabel}, ${nextOpening.openTime}`;
};

/**
 * Delivery payment options depend on the active delivery schedule entry, not on
 * the daily opening-hours structure. This keeps checkout aligned with the
 * legacy flow and the backend `delivery_schedule` contract.
 */
const resolveDeliveryScheduleEntryForLocation = (
  partnerData: PartnerDataType | null,
  activeLocation?: string | null,
) => {
  const deliverySchedule = partnerData?.delivery_schedule;
  if (!deliverySchedule) {
    return undefined;
  }

  const candidateKeys = [
    activeLocation,
    'Whole_country',
    partnerData?.location,
  ].filter((key): key is string => Boolean(key && key.trim().length > 0));

  for (const key of candidateKeys) {
    const entry = deliverySchedule[key];
    if (entry) {
      return entry;
    }
  }

  return undefined;
};

const hasCashlessTerminalForPartnerLocation = (
  partnerData: PartnerDataType | null,
  activeLocation?: string | null,
): boolean => (
  resolveDeliveryScheduleEntryForLocation(partnerData, activeLocation)?.is_cashless_terminal_available === true
);

const getCartStorage = (): Record<string, ShoppingCart> => {
  const { storage, wasChanged } = readShoppingCartStorage(CART_STORAGE_TTL_MS);
  if (wasChanged) {
    saveShoppingCartStorage(storage);
  }

  return storage;
};

const saveCartStorage = (storage: Record<string, ShoppingCart>) => {
  saveShoppingCartStorage(storage as ShoppingCartStorage);
};

const areArraysEqual = (first: string[] | null, second: string[] | null): boolean => {
  if (first === second) {
    return true;
  }
  if (!first || !second) {
    return !first && !second;
  }
  if (first.length !== second.length) {
    return false;
  }
  return first.every((value, index) => value === second[index]);
};

const getRecordNumber = (
  record: Record<string, number | string> | null | undefined,
  key: string | null | undefined
): number => {
  if (!record || !key) {
    return 0;
  }
  const value = record[key];
  return parseMenuPriceValue(value);
};

const parseMenuPriceValue = (value: number | string | null | undefined): number => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }
  if (typeof value === 'string') {
    const parsed = Number(value.replace(',', '.'));
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
};

const getVisibleStockQuantity = (value: number | string | null | undefined, showFullStock: boolean): number | null => {
  const parsed = parseMenuPriceValue(value);
  return parsed > 0 && (showFullStock || parsed < 6) ? parsed : null;
};

const toVersionPriceMap = (
  value: number | string | VersionPriceMap | null | undefined
): VersionPriceMap | null => {
  if (!value || Array.isArray(value) || typeof value !== 'object') {
    return null;
  }
  return value;
};

/**
 * Public `/menu` payloads can provide versioned prices directly in `price` and
 * version labels in `product_versions`, while editing payloads still use
 * `version_price`. This helper normalizes both shapes so card availability,
 * price display, and modal options all read the same product source.
 */
const getVersionPriceMap = (item: MenuItemWithPrice): VersionPriceMap | null => {
  if (item.version_price && Object.keys(item.version_price).length > 0) {
    return item.version_price;
  }
  return toVersionPriceMap(item.price);
};

const normalizePriceLevelKey = (value: number | string | null | undefined): string | null => {
  if (value === null || value === undefined) {
    return null;
  }

  const normalizedValue = `${value}`.trim();
  return normalizedValue.length > 0 && normalizedValue !== '0' ? normalizedValue : null;
};

const hasSpecialPriceLevel = (value: number | string | null | undefined): boolean => (
  normalizePriceLevelKey(value) !== null
);

const getFirstDefinedPriceLevel = (
  ...values: Array<number | string | null | undefined>
): number | string | null | undefined => (
  values.find((value) => value !== null && value !== undefined && `${value}`.trim().length > 0)
);

const isVersionPriceMap = (value: PriceLevelValue): value is VersionPriceMap => (
  Boolean(value && !Array.isArray(value) && typeof value === 'object')
);

const getPriceLevelValue = (
  item: MenuItemWithPrice,
  priceLevel: number | string | null | undefined
): PriceLevelValue => {
  const priceLevelKey = normalizePriceLevelKey(priceLevel);
  if (!priceLevelKey || !item.version_prices_by_levels || Array.isArray(item.version_prices_by_levels)) {
    return undefined;
  }

  return Object.prototype.hasOwnProperty.call(item.version_prices_by_levels, priceLevelKey)
    ? item.version_prices_by_levels[priceLevelKey] as PriceLevelValue
    : undefined;
};

const getLowestPositivePriceFromMap = (priceMap: VersionPriceMap | null | undefined): number => {
  if (!priceMap) {
    return 0;
  }

  const values = Object.values(priceMap)
    .map((value) => parseMenuPriceValue(value))
    .filter((value) => value > 0);
  return values.length > 0 ? Math.min(...values) : 0;
};

const getEffectiveVersionPriceMap = (
  item: MenuItemWithPrice,
  priceLevel: number | string | null | undefined
): VersionPriceMap | null => {
  const priceLevelValue = getPriceLevelValue(item, priceLevel);
  return isVersionPriceMap(priceLevelValue) ? priceLevelValue : getVersionPriceMap(item);
};

const getPriceFromLevelVersionMap = (
  item: MenuItemWithPrice,
  priceLevelMap: VersionPriceMap,
  versionKey: string | null | undefined
): number => {
  if (versionKey && Object.prototype.hasOwnProperty.call(priceLevelMap, versionKey)) {
    return parseMenuPriceValue(priceLevelMap[versionKey]);
  }

  const baseVersionPrice = getRecordNumber(getVersionPriceMap(item), versionKey);
  return baseVersionPrice > 0 ? baseVersionPrice : getBaseItemPrice(item);
};

const getLowestEffectivePriceFromLevelVersionMap = (
  item: MenuItemWithPrice,
  priceLevelMap: VersionPriceMap
): number => {
  const versionEntries = getProductVersionEntries(item.product_versions);
  if (versionEntries.length === 0) {
    return getLowestPositivePriceFromMap(priceLevelMap);
  }

  const prices = versionEntries
    .map(({ key }) => getPriceFromLevelVersionMap(item, priceLevelMap, key))
    .filter((value) => value > 0);
  return prices.length > 0 ? Math.min(...prices) : 0;
};

const getProductVersionEntries = (
  productVersions: ProductVersionLabels
): Array<{ key: string; label: string }> => {
  if (!productVersions) {
    return [];
  }
  if (Array.isArray(productVersions)) {
    return productVersions.map((versionKey) => ({
      key: versionKey,
      label: versionKey,
    }));
  }
  return Object.entries(productVersions).map(([key, label]) => ({
    key,
    label,
  }));
};

const hasProductVersions = (item: Pick<MenuItemWithPrice, 'product_versions'>): boolean => (
  getProductVersionEntries(item.product_versions).length > 0
);

const getProductVersionLabelFallback = (
  item: Pick<MenuItemWithPrice, 'product_versions'> | null | undefined,
  versionKey: string | null | undefined
): string | null => {
  if (!versionKey) {
    return null;
  }
  const versions = item?.product_versions;
  if (versions && !Array.isArray(versions) && typeof versions === 'object') {
    const directLabel = versions[versionKey];
    if (typeof directLabel === 'string' && directLabel.trim().length > 0) {
      return directLabel;
    }
  }
  return versionKey;
};

const getBaseItemPrice = (item: MenuItemWithPrice): number => {
  const directPrice = parseMenuPriceValue(
    typeof item.price === 'number' || typeof item.price === 'string' ? item.price : null
  );
  if (directPrice > 0) {
    return directPrice;
  }

  const fallbackPrice = parseMenuPriceValue(item.no_version_price);
  if (fallbackPrice > 0) {
    return fallbackPrice;
  }

  const versionPriceMap = getVersionPriceMap(item);
  const lowestVersionPrice = getLowestPositivePriceFromMap(versionPriceMap);
  if (lowestVersionPrice > 0) {
    return lowestVersionPrice;
  }
  return 0;
};

const getEffectiveItemPrice = (
  item: MenuItemWithPrice,
  priceLevel: number | string | null | undefined
): number => {
  const priceLevelValue = getPriceLevelValue(item, priceLevel);
  if (isVersionPriceMap(priceLevelValue)) {
    return getLowestEffectivePriceFromLevelVersionMap(item, priceLevelValue);
  }
  if (priceLevelValue !== undefined && priceLevelValue !== null) {
    return parseMenuPriceValue(priceLevelValue);
  }
  return getBaseItemPrice(item);
};

const getEffectiveVersionPrice = (
  item: MenuItemWithPrice,
  priceLevel: number | string | null | undefined,
  versionKey: string | null | undefined
): number => {
  const priceLevelValue = getPriceLevelValue(item, priceLevel);
  if (isVersionPriceMap(priceLevelValue)) {
    return getPriceFromLevelVersionMap(item, priceLevelValue, versionKey);
  }
  if (priceLevelValue !== undefined && priceLevelValue !== null) {
    return parseMenuPriceValue(priceLevelValue);
  }

  const versionPrice = getRecordNumber(getVersionPriceMap(item), versionKey);
  return versionPrice > 0 ? versionPrice : getEffectiveItemPrice(item, priceLevel);
};

const getCartItemEffectiveUnitPrice = (
  item: CartProductItem,
  menu: FoodMakerMenu,
  priceLevel: number | string | null | undefined
): number => {
  const menuItem = menu[item.product_key] as MenuItemWithPrice | undefined;
  if (!menuItem) {
    return parseMenuPriceValue(item.total_cost);
  }

  const basePrice = item.product_version
    ? getEffectiveVersionPrice(menuItem, priceLevel, item.product_version)
    : getEffectiveItemPrice(menuItem, priceLevel);
  const addsTotal = (item.adds ?? []).reduce((sum, addKey) => {
    const addItem = menu[addKey] as MenuItemWithPrice | undefined;
    return sum + (addItem ? getEffectiveItemPrice(addItem, priceLevel) : 0);
  }, 0);

  return basePrice + addsTotal;
};

const hasSelectedProductVersion = (
  menuItem: MenuItemWithPrice,
  versionKey: string | null,
  priceLevel: number | string | null | undefined
): boolean => {
  if (!versionKey) {
    return true;
  }

  const listedVersionExists = getProductVersionEntries(menuItem.product_versions)
    .some((version) => version.key === versionKey);
  const effectiveVersionPriceMap = getEffectiveVersionPriceMap(menuItem, priceLevel);
  const pricedVersionExists = Boolean(
    effectiveVersionPriceMap
    && Object.prototype.hasOwnProperty.call(effectiveVersionPriceMap, versionKey)
  );

  return (listedVersionExists || pricedVersionExists)
    && getEffectiveVersionPrice(menuItem, priceLevel, versionKey) > 0;
};

const isCartMenuItemAvailable = (
  menuItem: MenuItemWithPrice | undefined,
  priceLevel: number | string | null | undefined
): menuItem is MenuItemWithPrice => (
  Boolean(menuItem?.is_active && menuItem.is_available && getEffectiveItemPrice(menuItem, priceLevel) > 0)
);

const getCartItemDisplayName = (
  item: CartProductItem,
  refreshedMenu: FoodMakerMenu,
  fallbackMenu: FoodMakerMenu,
  translations: Record<string, Record<string, string>>,
  language: string
): string => {
  const menuItem = refreshedMenu[item.product_key] ?? fallbackMenu[item.product_key];
  const fallbackName = menuItem?.name_code ?? item.product_key;

  return resolveMenuText(translations, fallbackName, language, fallbackName);
};

const validateCartAgainstMenu = (
  shoppingCart: ShoppingCart,
  refreshedMenu: FoodMakerMenu,
  fallbackMenu: FoodMakerMenu,
  priceLevel: number | string | null | undefined,
  translations: Record<string, Record<string, string>>,
  language: string
): CartAvailabilityCheckResult => {
  const unavailableItemNames: string[] = [];
  const validCart = shoppingCart.reduce<ShoppingCart>((cart, item) => {
    const menuItem = refreshedMenu[item.product_key] as MenuItemWithPrice | undefined;
    const componentKeys = [...(item.adds ?? []), ...(item.combo_product_list ?? [])];
    const areComponentsAvailable = componentKeys.every((componentKey) => (
      isCartMenuItemAvailable(refreshedMenu[componentKey] as MenuItemWithPrice | undefined, priceLevel)
    ));
    const isAvailable = isCartMenuItemAvailable(menuItem, priceLevel)
      && hasSelectedProductVersion(menuItem, item.product_version, priceLevel)
      && areComponentsAvailable;

    if (!isAvailable) {
      unavailableItemNames.push(getCartItemDisplayName(item, refreshedMenu, fallbackMenu, translations, language));
      return cart;
    }

    const totalCost = getCartItemEffectiveUnitPrice(item, refreshedMenu, priceLevel);
    if (totalCost <= 0) {
      unavailableItemNames.push(getCartItemDisplayName(item, refreshedMenu, fallbackMenu, translations, language));
      return cart;
    }

    cart.push({ ...item, total_cost: totalCost });
    return cart;
  }, []);

  return { validCart, unavailableItemNames };
};

const getVersionOptions = (
  item: MenuItemWithKey,
  versionPrices: Record<string, number | string> | null | undefined,
  priceLevel: number | string | null | undefined
): Array<{ key: string; label: string; price: number }> => {
  const priceLevelValue = getPriceLevelValue(item, priceLevel);
  const scalarPriceLevelPrice = priceLevelValue !== undefined && priceLevelValue !== null && !isVersionPriceMap(priceLevelValue)
    ? parseMenuPriceValue(priceLevelValue)
    : null;
  const effectiveVersionPrices = isVersionPriceMap(priceLevelValue)
    ? priceLevelValue
    : versionPrices ?? getVersionPriceMap(item);

  return getProductVersionEntries(item.product_versions)
    .map(({ key, label }) => ({
      key,
      label,
      price: scalarPriceLevelPrice ?? (
        isVersionPriceMap(priceLevelValue)
          ? getPriceFromLevelVersionMap(item, priceLevelValue, key)
          : getRecordNumber(effectiveVersionPrices, key)
      ),
    }))
    .filter((option) => option.price > 0);
};

const formatEuro = (value: number): string => `€ ${value.toFixed(2)}`;
const formatEuroOneDecimal = (value: number): string => `€ ${value.toFixed(1)}`;
const roundCurrencyValue = (value: number): number => Number(value.toFixed(2));

const normalizeYesNo = (value: string | null | undefined): 'Yes' | 'No' => (
  value === 'Yes' ? 'Yes' : 'No'
);

const haversineDistanceKm = (first: [number, number], second: [number, number]): number => {
  const toRadians = (angle: number) => (angle * Math.PI) / 180;
  const deltaLat = toRadians(second[0] - first[0]);
  const deltaLon = toRadians(second[1] - first[1]);
  const lat1 = toRadians(first[0]);
  const lat2 = toRadians(second[0]);
  const a = Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2)
    + Math.cos(lat1) * Math.cos(lat2) * Math.sin(deltaLon / 2) * Math.sin(deltaLon / 2);
  return 6371 * (2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
};

const isPointInPolygon = (point: [number, number], polygon: [number, number][]): boolean => {
  let isInside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i][1];
    const yi = polygon[i][0];
    const xj = polygon[j][1];
    const yj = polygon[j][0];
    const intersect = ((yi > point[0]) !== (yj > point[0]))
      && (point[1] < ((xj - xi) * (point[0] - yi)) / ((yj - yi) || 1e-9) + xi);
    if (intersect) {
      isInside = !isInside;
    }
  }
  return isInside;
};

const resolveMenuText = (
  translations: Record<string, Record<string, string>>,
  key: string | null | undefined,
  language: string,
  fallback?: string
): string => {
  if (!key) {
    return fallback ?? '';
  }
  return translations[key]?.[language] ?? fallback ?? key;
};

const EMPTY_MENU_TRANSLATIONS: Record<string, Record<string, string>> = {};

const findDeliveryZone = (
  partnerData: PartnerDataType | null,
  orderData: MenuOrderData,
  language: string,
  effectiveCoordinates?: DeliveryCoordinates | null,
): DeliveryZoneMatch | null => {
  const point = effectiveCoordinates === undefined
    ? getValidCoordinates(orderData.loc_latitude, orderData.loc_longitude)
    : effectiveCoordinates;
  if (!partnerData?.delivery_zones || !point) {
    return null;
  }

  for (const [zoneKey, zone] of Object.entries(partnerData.delivery_zones)) {
    const isMatch = zone.coordinates
      ? isPointInPolygon(point, zone.coordinates)
      : Boolean(zone.circle_zone && haversineDistanceKm(point, zone.circle_zone.center) <= zone.circle_zone.radius_km);

    if (isMatch) {
      return {
        zoneKey,
        price: zone.price,
        free_delivery_amount: zone.free_delivery_amount,
        name: zone.name[language] ?? zone.name.en ?? zoneKey,
      };
    }
  }

  return null;
};

const buildReceiptSummary = (
  shoppingCart: ShoppingCart,
  partnerData: PartnerDataType | null,
  orderData: MenuOrderData,
  language: string,
  promoData: PromoData | null,
  deliveryCostOverride?: number | null,
  effectiveDeliveryCoordinates?: DeliveryCoordinates | null,
): ReceiptSummary => {
  const rawSubtotal = calculateTotalProductsCost(shoppingCart);
  let subtotal = rawSubtotal;

  if (promoData?.promo_type === 'x_%_discount') {
    subtotal = rawSubtotal * (100 - promoData.promo_amount) / 100;
  } else if (promoData?.promo_type === 'fixed_amount') {
    subtotal = Math.max(0, rawSubtotal - promoData.promo_amount);
  }

  const zone = orderData.deliveryNeeded === 'Yes'
    ? findDeliveryZone(partnerData, orderData, language, effectiveDeliveryCoordinates)
    : null;
  const deliveryCost = orderData.deliveryNeeded === 'Yes' && deliveryCostOverride != null
    ? deliveryCostOverride
    : (zone ? (subtotal >= zone.free_delivery_amount ? 0 : zone.price) : 0);
  /**
   * Negative delivery values are backend status markers such as -1/-2/-3.
   * They must remain visible to the UI layer, but they must not reduce the
   * payable total inside checkout and review screens.
   */
  const totalDeliveryCost = deliveryCost < 0 ? 0 : deliveryCost;

  return {
    subtotal,
    deliveryCost,
    total: subtotal + totalDeliveryCost,
  };
};

const buildMenuDataLayerItems = (
  shoppingCart: ShoppingCart,
  menu: FoodMakerMenu,
  translations: Record<string, Record<string, string>>,
  language: string,
  priceLevel: number | string | null | undefined,
): DataLayerItem[] => (
  shoppingCart.map((item) => {
    const menuItem = menu[item.product_key] as MenuItemWithPrice | undefined;
    const fallbackName = menuItem?.name_code ?? item.product_key;
    const itemName = resolveMenuText(translations, fallbackName, language, fallbackName);
    const categoryCode = menuItem?.category_code;
    const categoryLabel = categoryCode
      ? resolveMenuText(translations, categoryCode, language, categoryCode)
      : undefined;
    const versionLabel = item.product_version
      ? resolveMenuText(
        translations,
        item.product_version,
        language,
        getProductVersionLabelFallback(menuItem, item.product_version) ?? item.product_version
      )
      : null;
    const addLabels = (item.adds ?? []).map((addKey) => {
      const addItem = menu[addKey];
      return resolveMenuText(translations, addItem?.name_code ?? addKey, language, addItem?.name_code ?? addKey);
    });
    const comboLabels = (item.combo_product_list ?? []).map((comboKey) => {
      const comboItem = menu[comboKey];
      return resolveMenuText(translations, comboItem?.name_code ?? comboKey, language, comboItem?.name_code ?? comboKey);
    });
    const variantParts = [
      versionLabel,
      addLabels.length > 0 ? `Adds: ${addLabels.join(', ')}` : null,
      comboLabels.length > 0 ? `Combo: ${comboLabels.join(', ')}` : null,
    ].filter((value): value is string => Boolean(value));

    return {
      item_id: item.product_key,
      item_name: itemName,
      ...(categoryLabel ? { item_category: categoryLabel } : {}),
      ...(variantParts.length > 0 ? { item_variant: variantParts.join(' | ') } : {}),
      price: roundCurrencyValue(getCartItemEffectiveUnitPrice(item, menu, priceLevel)),
      quantity: item.quantity,
    };
  })
);

const buildMenuDataLayerEcommercePayload = (
  shoppingCart: ShoppingCart,
  menu: FoodMakerMenu,
  translations: Record<string, Record<string, string>>,
  language: string,
  priceLevel: number | string | null | undefined,
  value: number,
): DataLayerEcommercePayload => ({
  currency: 'EUR',
  value: roundCurrencyValue(value),
  items: buildMenuDataLayerItems(shoppingCart, menu, translations, language, priceLevel),
});

const resolveDeliveryCostPartnerCode = (partnerData: PartnerDataType | null, orderData: MenuOrderData): string => {
  return partnerData?.food_maker_key ?? orderData.food_maker ?? '';
};

const setLanguage = (lang: string) => {
  const url = new URL(window.location.href);
  url.searchParams.set('lang', lang);
  window.location.replace(url.toString());
};

const isEmptyProfileValue = (value: unknown): boolean => (
  value === null || value === undefined || `${value}`.trim().length === 0
);

const getFirstFilledString = (...values: Array<string | null | undefined>): string | undefined => {
  const value = values.find((candidate) => !isEmptyProfileValue(candidate));
  return typeof value === 'string' ? value : undefined;
};

const getFirstDefinedNumber = (...values: Array<number | null | undefined>): number | undefined => (
  values.find((candidate): candidate is number => typeof candidate === 'number' && Number.isFinite(candidate))
);

const getValidCoordinates = (
  latitude: number | null | undefined,
  longitude: number | null | undefined,
): DeliveryCoordinates | null => (
  typeof latitude === 'number'
    && Number.isFinite(latitude)
    && typeof longitude === 'number'
    && Number.isFinite(longitude)
    ? [latitude, longitude]
    : null
);

const getPartnerCoordinates = (partnerData: PartnerDataType | null): DeliveryCoordinates | null => (
  getValidCoordinates(partnerData?.pickup_point?.[0], partnerData?.pickup_point?.[1])
);

const getActiveOrderLocation = (
  orderData: MenuOrderData,
  sessionData: SessionDataType | null,
  userData: UserDataType | null,
  partnerData: PartnerDataType | null,
): string => (
  getFirstFilledString(orderData.location, sessionData?.location, userData?.location, partnerData?.location) ?? ''
);

const isSameLocationKey = (left: string | null | undefined, right: string | null | undefined): boolean => (
  Boolean(left && right && left.trim() === right.trim())
);

/**
 * Delivery coordinates saved in the user/session state are only valid for the
 * current partner when both locations match. Otherwise the partner pickup point
 * is only a map center, not a preselected delivery point.
 */
const resolveDeliveryMapContext = (
  orderData: MenuOrderData,
  sessionData: SessionDataType | null,
  userData: UserDataType | null,
  partnerData: PartnerDataType | null,
  usePartnerPointAsDeliveryPoint = false,
): DeliveryMapContext => {
  const activeLocation = getActiveOrderLocation(orderData, sessionData, userData, partnerData);
  const partnerLocation = partnerData?.location ?? '';
  const partnerCoordinates = getPartnerCoordinates(partnerData);
  const storedCoordinates = getValidCoordinates(orderData.loc_latitude, orderData.loc_longitude)
    ?? getValidCoordinates(sessionData?.loc_latitude, sessionData?.loc_longitude)
    ?? getValidCoordinates(userData?.loc_latitude, userData?.loc_longitude);
  const canReuseStoredCoordinates = Boolean(
    storedCoordinates && isSameLocationKey(activeLocation, partnerLocation)
  );
  const fixedPartnerCoordinates = usePartnerPointAsDeliveryPoint ? partnerCoordinates : null;

  return {
    activeLocation,
    partnerLocation,
    initialCenter: fixedPartnerCoordinates
      ?? (canReuseStoredCoordinates && storedCoordinates
      ? storedCoordinates
      : (partnerCoordinates ?? DELIVERY_MAP_DEFAULT_CENTER)),
    selectedPosition: fixedPartnerCoordinates
      ?? (canReuseStoredCoordinates && storedCoordinates ? storedCoordinates : null),
    effectiveCoordinates: fixedPartnerCoordinates
      ?? (canReuseStoredCoordinates && storedCoordinates ? storedCoordinates : null),
  };
};

const applyEffectiveDeliveryCoordinates = (
  orderData: MenuOrderData,
  coordinates: DeliveryCoordinates | null,
): MenuOrderData => (
  orderData.deliveryNeeded === 'Yes'
    ? {
      ...orderData,
      loc_latitude: coordinates?.[0] ?? null,
      loc_longitude: coordinates?.[1] ?? null,
    }
    : orderData
);

const getDefaultLanguageByChannel = (channel: string | null | undefined): string => {
  if (channel === 'telegram') {
    return 'ru';
  }
  if (channel === 'viber') {
    return 'cn';
  }
  return 'en';
};

const buildAnonymousWebUserId = (countryCode: string | null | undefined, phone: string | null | undefined): string => {
  if (isEmptyProfileValue(countryCode) || isEmptyProfileValue(phone)) {
    return '';
  }

  return `${countryCode ?? ''}${phone ?? ''}`.replace(/\s+/g, '').replace(/\+/g, '');
};

/**
 * Checkout state may contain both the backend-authenticated profile id and the
 * Mini App runtime Telegram id. Orders created from Telegram must always
 * prefer the Telegram identity so the backend can address the customer
 * through Telegram rather than a web-profile surrogate id.
 */
const resolveTelegramOrderUserId = (
  orderData: MenuOrderData,
  sessionData: SessionDataType | null,
  userData: UserDataType | null,
): string => (
  getFirstFilledString(
    orderData.telegram_user_id,
    sessionData?.telegram_user_id,
    userData?.telegram_user_id,
    orderData.channel === 'telegram' ? orderData.user_id : undefined,
    sessionData?.channel === 'telegram' ? sessionData.user_id : undefined,
    userData?.channel === 'telegram' ? userData.user_id : undefined,
  ) ?? ''
);

const resolveRawOrderChannel = (
  orderData: MenuOrderData,
  sessionData: SessionDataType | null,
  userData: UserDataType | null,
): string => (
  getFirstFilledString(orderData.channel, sessionData?.channel, userData?.channel, 'web') ?? 'web'
);

/**
 * Telegram display data can be present after a user opens a normal browser
 * link from Telegram, but that is not enough for a Telegram order. Without the
 * numeric Telegram id the checkout must fall back to the web contact flow.
 */
const resolveEffectiveOrderChannel = (
  orderData: MenuOrderData,
  sessionData: SessionDataType | null,
  userData: UserDataType | null,
): string => {
  const rawChannel = resolveRawOrderChannel(orderData, sessionData, userData);
  if (rawChannel !== 'telegram') {
    return rawChannel;
  }

  return resolveTelegramOrderUserId(orderData, sessionData, userData) ? 'telegram' : 'web';
};

/**
 * Telegram Mini App orders can carry a numeric Telegram id without a public
 * username. That case stays a Telegram order for identity, but checkout must
 * collect a phone number for the callback channel.
 */
const resolveTelegramOrderUsername = (
  orderData: MenuOrderData,
  sessionData: SessionDataType | null,
  userData: UserDataType | null,
): string => (
  getFirstFilledString(
    orderData.channel_user_name,
    sessionData?.channel_user_name,
    userData?.channel_user_name,
  ) ?? ''
);

const shouldUseWebPhoneContactForMissingTelegramId = (
  orderData: MenuOrderData,
  sessionData: SessionDataType | null,
  userData: UserDataType | null,
): boolean => (
  resolveRawOrderChannel(orderData, sessionData, userData) === 'telegram'
  && resolveEffectiveOrderChannel(orderData, sessionData, userData) !== 'telegram'
);

const shouldUsePhoneOnlyContactForTelegramWithoutUsername = (
  orderData: MenuOrderData,
  sessionData: SessionDataType | null,
  userData: UserDataType | null,
): boolean => (
  resolveRawOrderChannel(orderData, sessionData, userData) === 'telegram'
  && resolveEffectiveOrderChannel(orderData, sessionData, userData) === 'telegram'
  && !resolveTelegramOrderUsername(orderData, sessionData, userData)
);

/**
 * The public menu checkout form stores only the fields the user touched, while
 * the legacy cart flow sent a fully normalized order payload. This builder
 * restores that parity at send time so backend order creation always receives
 * the current partner location, language, coordinates, and contact channels
 * even when the user did not re-enter those values in the modal.
 */
const buildOrderDataForSubmission = (
  orderData: MenuOrderData,
  sessionData: SessionDataType | null,
  userData: UserDataType | null,
  partnerData: PartnerDataType | null,
  effectiveDeliveryCoordinates?: DeliveryCoordinates | null,
): MenuOrderData => {
  const rawChannel = resolveRawOrderChannel(orderData, sessionData, userData);
  const normalizedChannel = resolveEffectiveOrderChannel(orderData, sessionData, userData);
  const isTelegramOrder = normalizedChannel === 'telegram';
  const shouldForcePhoneContact = rawChannel === 'telegram' && !isTelegramOrder;
  const shouldUsePhoneOnlyTelegramContact = shouldUsePhoneOnlyContactForTelegramWithoutUsername(
    orderData,
    sessionData,
    userData,
  );
  const normalizedCountryCode = getFirstFilledString(orderData.countryCode, sessionData?.countryCode, userData?.countryCode);
  const normalizedPhone = getFirstFilledString(orderData.phone, sessionData?.phone, userData?.phone);
  const webUserId = normalizedChannel === 'web'
    ? buildAnonymousWebUserId(normalizedCountryCode, normalizedPhone)
    : '';
  const telegramUserId = isTelegramOrder
    ? resolveTelegramOrderUserId(orderData, sessionData, userData)
    : '';
  const normalizedChannelUserName = getFirstFilledString(
    orderData.channel_user_name,
    sessionData?.channel_user_name,
    userData?.channel_user_name,
  );
  const fullPhoneNumber = normalizedCountryCode && normalizedPhone
    ? `${normalizedCountryCode}${normalizedPhone}`
    : undefined;
  const isDeliveryOrder = orderData.deliveryNeeded === 'Yes';
  const normalizedLatitude = isDeliveryOrder
    ? (effectiveDeliveryCoordinates?.[0] ?? null)
    : (getFirstDefinedNumber(
      orderData.loc_latitude,
      sessionData?.loc_latitude,
      userData?.loc_latitude,
      partnerData?.pickup_point?.[0],
    ) ?? null);
  const normalizedLongitude = isDeliveryOrder
    ? (effectiveDeliveryCoordinates?.[1] ?? null)
    : (getFirstDefinedNumber(
      orderData.loc_longitude,
      sessionData?.loc_longitude,
      userData?.loc_longitude,
      partnerData?.pickup_point?.[1],
    ) ?? null);
  const baseContacts: Exclude<ContactMethods, undefined> = {
    ...(userData?.contacts ?? {}),
    ...(sessionData?.contacts ?? {}),
    ...(orderData.contacts ?? {}),
  };
  const preferredContactMethod = shouldForcePhoneContact || shouldUsePhoneOnlyTelegramContact
    ? 'phone'
    : (getFirstFilledString(
      orderData.contacts?.preferableMethod,
      sessionData?.contacts?.preferableMethod,
      userData?.contacts?.preferableMethod,
      isTelegramOrder ? 'telegram' : 'phone',
    ) ?? (isTelegramOrder ? 'telegram' : 'phone'));
  const normalizedContacts: Exclude<ContactMethods, undefined> = {
    ...baseContacts,
    preferableMethod: preferredContactMethod,
  };

  if (isTelegramOrder && !shouldUsePhoneOnlyTelegramContact) {
    normalizedContacts.preferableMethod = 'telegram';
    normalizedContacts.telegram = {
      ...(normalizedContacts.telegram ?? {}),
      userName: normalizedChannelUserName ?? normalizedContacts.telegram?.userName,
    };
  } else if (fullPhoneNumber) {
    if (preferredContactMethod === 'phone') {
      normalizedContacts.phone = { phoneNumber: fullPhoneNumber };
    } else if (preferredContactMethod === 'viber') {
      normalizedContacts.viber = { phoneNumber: fullPhoneNumber };
    } else if (preferredContactMethod === 'whatsapp') {
      normalizedContacts.whatsapp = { phoneNumber: fullPhoneNumber };
    }
  }

  const normalizedLoyaltyCardNumber = normalizeLoyaltyCardNumber(
    orderData.loyalty_card_number ?? sessionData?.loyalty_card_number,
  );
  const normalizedOrderData: MenuOrderData = { ...orderData };
  delete normalizedOrderData.loyalty_card_number;

  return {
    ...normalizedOrderData,
    ...(isMenuLoyaltyCardNumberEnabled(partnerData) && normalizedLoyaltyCardNumber
      ? { loyalty_card_number: normalizedLoyaltyCardNumber }
      : {}),
    user_id: isTelegramOrder
      ? telegramUserId
      : webUserId || (shouldForcePhoneContact ? '' : getFirstFilledString(orderData.user_id, sessionData?.user_id, userData?.user_id)) || '',
    telegram_user_id: isTelegramOrder
      ? telegramUserId || undefined
      : undefined,
    channel: normalizedChannel,
    channel_name: getFirstFilledString(orderData.channel_name, sessionData?.channel_name, userData?.channel_name),
    channel_first_name: getFirstFilledString(orderData.channel_first_name, sessionData?.channel_first_name, userData?.channel_first_name),
    channel_last_name: getFirstFilledString(orderData.channel_last_name, sessionData?.channel_last_name, userData?.channel_last_name),
    channel_user_name: getFirstFilledString(orderData.channel_user_name, sessionData?.channel_user_name, userData?.channel_user_name),
    lang: getFirstFilledString(orderData.lang, sessionData?.lang, userData?.lang, getDefaultLanguageByChannel(normalizedChannel)),
    price_level: getFirstDefinedPriceLevel(orderData.price_level, sessionData?.price_level, userData?.price_level),
    loc_latitude: normalizedLatitude,
    loc_longitude: normalizedLongitude,
    location: partnerData?.location ?? getFirstFilledString(orderData.location, sessionData?.location, userData?.location) ?? '',
    food_maker: partnerData?.food_maker_key ?? getFirstFilledString(orderData.food_maker, sessionData?.food_maker) ?? '',
    deliveryAddress: getFirstFilledString(orderData.deliveryAddress, sessionData?.deliveryAddress) ?? '',
    countryCode: normalizedCountryCode,
    phone: normalizedPhone,
    contacts: normalizedContacts,
  };
};

const mergeOrderDataWithAuthProfile = (orderData: MenuOrderData, profile: AuthProfile): MenuOrderData => {
  const nextOrderData: MenuOrderData = { ...orderData };
  const isTelegramProfile = profile.channel === 'telegram';

  if (isTelegramProfile) {
    nextOrderData.channel = 'telegram';
    if (!isEmptyProfileValue(profile.channel_name)) nextOrderData.channel_name = profile.channel_name;
    if ('channel_first_name' in profile || 'display_name' in profile) {
      nextOrderData.channel_first_name = profile.channel_first_name ?? profile.display_name ?? null;
    }
    if ('channel_last_name' in profile) nextOrderData.channel_last_name = profile.channel_last_name ?? null;
    if ('channel_user_name' in profile || profile.contacts?.telegram) {
      nextOrderData.channel_user_name = profile.channel_user_name ?? profile.contacts?.telegram?.userName ?? null;
    }
    nextOrderData.contacts = {
      ...(nextOrderData.contacts ?? {}),
      ...(profile.contacts ?? {}),
      preferableMethod: 'telegram',
      telegram: {
        ...(nextOrderData.contacts?.telegram ?? {}),
        ...(profile.contacts?.telegram ?? {}),
        userName: profile.channel_user_name ?? profile.contacts?.telegram?.userName ?? undefined,
      },
    };
  }

  if (!isTelegramProfile && isEmptyProfileValue(nextOrderData.user_id) && !isEmptyProfileValue(profile.user_id)) nextOrderData.user_id = profile.user_id;
  if (isEmptyProfileValue(nextOrderData.channel) && !isEmptyProfileValue(profile.channel)) nextOrderData.channel = profile.channel;
  if (isEmptyProfileValue(nextOrderData.channel_name) && !isEmptyProfileValue(profile.channel_name)) nextOrderData.channel_name = profile.channel_name;
  if (isEmptyProfileValue(nextOrderData.channel_first_name) && !isEmptyProfileValue(profile.channel_first_name ?? profile.display_name)) {
    nextOrderData.channel_first_name = profile.channel_first_name ?? profile.display_name;
  }
  if (isEmptyProfileValue(nextOrderData.channel_last_name) && !isEmptyProfileValue(profile.channel_last_name)) nextOrderData.channel_last_name = profile.channel_last_name;
  if (isEmptyProfileValue(nextOrderData.channel_user_name) && !isEmptyProfileValue(profile.channel_user_name)) nextOrderData.channel_user_name = profile.channel_user_name;
  if (isEmptyProfileValue(nextOrderData.lang) && !isEmptyProfileValue(profile.lang)) nextOrderData.lang = profile.lang;
  if (isEmptyProfileValue(nextOrderData.price_level) && !isEmptyProfileValue(profile.price_level)) nextOrderData.price_level = profile.price_level;
  if (isEmptyProfileValue(nextOrderData.location) && !isEmptyProfileValue(profile.location)) nextOrderData.location = profile.location;
  if (nextOrderData.loc_latitude == null && profile.loc_latitude != null) nextOrderData.loc_latitude = profile.loc_latitude;
  if (nextOrderData.loc_longitude == null && profile.loc_longitude != null) nextOrderData.loc_longitude = profile.loc_longitude;
  if (isEmptyProfileValue(nextOrderData.countryCode) && !isEmptyProfileValue(profile.countryCode)) nextOrderData.countryCode = profile.countryCode;
  if (isEmptyProfileValue(nextOrderData.phone) && !isEmptyProfileValue(profile.phone)) nextOrderData.phone = profile.phone;

  if (profile.contacts) {
    nextOrderData.contacts = {
      ...profile.contacts,
      ...nextOrderData.contacts,
      preferableMethod: nextOrderData.contacts?.preferableMethod ?? profile.contacts.preferableMethod,
      telegram: nextOrderData.contacts?.telegram ?? profile.contacts.telegram,
      phone: nextOrderData.contacts?.phone ?? profile.contacts.phone,
    };
  }

  return nextOrderData;
};

const getAuthProfileKey = (profile: AuthProfile | null): string => {
  if (!profile) {
    return '';
  }

  return [
    profile.user_id ?? '',
    profile.email ?? '',
    profile.channel_user_name ?? '',
    profile.price_level ?? '',
  ].join('|');
};
/**
 * LanguageToggle
 *
 * Custom dropdown toggle without a Bootstrap caret.
 * Keeps the compact globe + language code control used in the page header.
 */
const LanguageToggle = React.forwardRef<HTMLButtonElement, { onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void; children: React.ReactNode; ariaLabel?: string }>(
  ({ onClick, children, ariaLabel }, ref) => (
    <button
      ref={ref}
      type="button"
      className="btn btn-link d-flex align-items-center gap-1 text-decoration-none p-0 border-0 text-success fw-bold"
      onClick={(event) => {
        event.preventDefault();
        onClick?.(event);
      }}
      aria-label={ariaLabel}
    >
      {children}
    </button>
  )
);
LanguageToggle.displayName = 'LanguageToggle';

/**
 * ProfileAuthModal
 *
 * Owns the profile button destination for both anonymous and authenticated
 * users. It receives translated labels and app-level auth actions from
 * AuthContext, renders independent Bootstrap auth/profile states, and keeps all
 * login failures local so the menu, cart, and anonymous checkout stay usable.
 */
const ProfileAuthModal: React.FC<{
  show: boolean;
  onHide: () => void;
  labels: ProfileModalLabels;
}> = ({ show, onHide, labels }) => {
  const auth = useAuthData();
  const telegramWidgetRef = useRef<HTMLDivElement | null>(null);
  const [email, setEmail] = useState<string>('');
  const [emailStatus, setEmailStatus] = useState<string | null>(null);
  const [formProfile, setFormProfile] = useState<AuthProfile>({});
  const isAuthenticated = auth?.authStatus === 'authenticated' && Boolean(auth.authUser);

  useEffect(() => {
    if (!show) {
      return;
    }

    setFormProfile({
      ...(auth?.authUser ?? {}),
      channel_first_name: auth?.authUser?.channel_first_name ?? auth?.authUser?.display_name ?? '',
      contacts: {
        ...auth?.authUser?.contacts,
        preferableMethod: auth?.authUser?.contacts?.preferableMethod ?? 'phone',
      },
    });
  }, [auth?.authUser, show]);

  useEffect(() => {
    const widgetContainer = telegramWidgetRef.current;
    if (!show || isAuthenticated || !widgetContainer || !auth) {
      return;
    }

    widgetContainer.innerHTML = '';
    window.handleIfoodTelegramAuth = (telegramPayload: Record<string, unknown>) => {
      auth.loginWithTelegramWidget(telegramPayload);
    };

    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://telegram.org/js/telegram-widget.js?22';
    script.setAttribute('data-telegram-login', 'iFoodMeBot');
    script.setAttribute('data-size', 'medium');
    script.setAttribute('data-radius', '16');
    script.setAttribute('data-onauth', 'handleIfoodTelegramAuth(user)');
    widgetContainer.appendChild(script);

    return () => {
      widgetContainer.innerHTML = '';
    };
  }, [auth, isAuthenticated, show]);

  if (!auth) {
    return null;
  }

  const patchProfile = (patch: AuthProfile) => {
    setFormProfile((previous) => ({ ...previous, ...patch }));
  };

  const handleEmailSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setEmailStatus(null);

    try {
      await auth.requestEmailMagicLink(email);
      setEmailStatus(labels.magicLinkSent);
    } catch {
      setEmailStatus(null);
    }
  };

  const handleProfileSave = async () => {
    await auth.saveProfile({
      ...formProfile,
      contacts: {
        ...formProfile.contacts,
        preferableMethod: formProfile.contacts?.preferableMethod ?? 'phone',
        phone: formProfile.phone ? { phoneNumber: `${formProfile.countryCode ?? ''}${formProfile.phone}` } : formProfile.contacts?.phone,
        telegram: formProfile.channel_user_name
          ? { ...formProfile.contacts?.telegram, userName: formProfile.channel_user_name }
          : formProfile.contacts?.telegram,
      },
    });
  };

  return (
    <Modal show={show} onHide={onHide} centered fullscreen="sm-down" contentClassName="border-0 rounded-4">
      <Modal.Header closeButton className="border-0">
        <Modal.Title className="fw-bold">{isAuthenticated ? labels.profile : labels.signIn}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="pt-0" style={{ backgroundColor: '#F7F8F5' }}>
        {auth.authStatus === 'loading' && (
          <div className="d-flex align-items-center gap-2 text-secondary mb-3">
            <Spinner animation="border" size="sm" />
            <span>{labels.signIn}</span>
          </div>
        )}

        {auth.authError && (
          <Alert variant="warning" className="py-2 px-3">
            {labels.authUnavailable}: {auth.authError}
          </Alert>
        )}

        {!isAuthenticated ? (
          <div className="d-flex flex-column gap-3">
            <Form onSubmit={handleEmailSubmit}>
              <Form.Group className="mb-2" controlId="profile-auth-email">
                <Form.Label className="fw-semibold">{labels.email}</Form.Label>
                <Form.Control
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="name@example.com"
                  required
                />
              </Form.Group>
              <Button type="submit" variant="success" className="w-100 rounded-pill fw-semibold">
                {labels.sendMagicLink}
              </Button>
              {emailStatus && <div className="small text-success mt-2">{emailStatus}</div>}
            </Form>

            <Button
              type="button"
              variant="light"
              className="w-100 rounded-pill border d-flex align-items-center justify-content-center gap-2 fw-semibold"
              onClick={auth.startGoogleLogin}
            >
              <FaGoogle />
              <span>{labels.google}</span>
            </Button>

            <div className="bg-white rounded-4 border p-3 text-center">
              <div className="fw-semibold mb-2 d-flex align-items-center justify-content-center gap-2">
                <FaTelegramPlane />
                <span>{labels.telegram}</span>
              </div>
              <div ref={telegramWidgetRef} className="d-inline-block" />
            </div>
          </div>
        ) : (
          <Form className="d-flex flex-column gap-3">
            <Form.Group controlId="profile-name">
              <Form.Label className="fw-semibold">{labels.name}</Form.Label>
              <Form.Control
                value={formProfile.channel_first_name ?? ''}
                onChange={(event) => patchProfile({ channel_first_name: event.target.value, display_name: event.target.value })}
              />
            </Form.Group>

            <div className="row g-2">
              <div className="col-5">
                <Form.Group controlId="profile-country-code">
                  <Form.Label className="fw-semibold">{labels.countryCode}</Form.Label>
                  <Form.Select
                    value={formProfile.countryCode ?? ''}
                    onChange={(event) => patchProfile({ countryCode: event.target.value })}
                  >
                    <option value="">...</option>
                    {['+382', '+380', '+7', '+49', '+1', '+44', '+90'].map((code) => (
                      <option key={code} value={code}>{code}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </div>
              <div className="col-7">
                <Form.Group controlId="profile-phone">
                  <Form.Label className="fw-semibold">{labels.phone}</Form.Label>
                  <Form.Control
                    value={formProfile.phone ?? ''}
                    onChange={(event) => patchProfile({ phone: event.target.value.replace(/\D/g, '') })}
                  />
                </Form.Group>
              </div>
            </div>

            <Form.Group controlId="profile-telegram">
              <Form.Label className="fw-semibold">{labels.telegramUsername}</Form.Label>
              <Form.Control
                value={formProfile.channel_user_name ?? formProfile.contacts?.telegram?.userName ?? ''}
                onChange={(event) => patchProfile({
                  channel_user_name: event.target.value,
                  contacts: {
                    ...formProfile.contacts,
                    telegram: { ...formProfile.contacts?.telegram, userName: event.target.value },
                  },
                })}
              />
            </Form.Group>

            <div className="row g-2">
              <div className="col-6">
                <Form.Group controlId="profile-contact-method">
                  <Form.Label className="fw-semibold">{labels.preferredMethod}</Form.Label>
                  <Form.Select
                    value={formProfile.contacts?.preferableMethod ?? 'phone'}
                    onChange={(event) => patchProfile({
                      contacts: { ...formProfile.contacts, preferableMethod: event.target.value },
                    })}
                  >
                    {['phone', 'telegram', 'whatsapp', 'viber'].map((method) => (
                      <option key={method} value={method}>{method}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </div>
              <div className="col-6">
                <Form.Group controlId="profile-language">
                  <Form.Label className="fw-semibold">{labels.language}</Form.Label>
                  <Form.Select
                    value={formProfile.lang ?? ''}
                    onChange={(event) => patchProfile({ lang: event.target.value })}
                  >
                    <option value="">...</option>
                    {['en', 'ru', 'ua', 'tr', 'cn'].map((lang) => (
                      <option key={lang} value={lang}>{lang.toUpperCase()}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </div>
            </div>

            <Form.Group controlId="profile-location">
              <Form.Label className="fw-semibold">{labels.location}</Form.Label>
              <Form.Control
                value={formProfile.location ?? ''}
                onChange={(event) => patchProfile({ location: event.target.value })}
              />
            </Form.Group>

            <Form.Group controlId="profile-address">
              <Form.Label className="fw-semibold">{labels.address}</Form.Label>
              <Form.Control
                value={(formProfile as AuthProfile & { deliveryAddress?: string }).deliveryAddress ?? ''}
                onChange={(event) => patchProfile({ deliveryAddress: event.target.value } as AuthProfile)}
              />
            </Form.Group>
          </Form>
        )}
      </Modal.Body>
      <Modal.Footer className="border-0">
        {isAuthenticated ? (
          <div className="d-flex w-100 gap-2">
            <Button type="button" variant="light" className="rounded-pill border flex-fill" onClick={auth.logout}>
              {labels.logout}
            </Button>
            <Button type="button" variant="success" className="rounded-pill fw-bold flex-fill" onClick={handleProfileSave} disabled={auth.isProfileSaving}>
              {auth.isProfileSaving ? labels.saving : labels.save}
            </Button>
          </div>
        ) : (
          <Button type="button" variant="light" className="rounded-pill border w-100" onClick={onHide}>
            {labels.close}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

/**
 * PartnerPhotoGallery
 *
 * Renders the restaurant photo preview inside one shared square frame.
 * The collage intentionally uses fixed crops so the block stays visually
 * aligned: the first photo becomes a wide hero strip, while the next two
 * photos are cropped to equal squares below it.
 */
const PartnerPhotoGallery: React.FC<{
  photos: PartnerGalleryPhoto[];
  totalCount: number;
  alt: string;
}> = ({ photos, totalCount, alt }) => {
  const hiddenPhotosCount = totalCount - photos.length;
  const previewGap = '0.2rem';

  /**
   * All preview slots use cover-mode on purpose: the goal of this collage is
   * edge-to-edge visual alignment, not full uncropped visibility.
   */
  const renderPhotoCell = (photo: PartnerGalleryPhoto, index: number, cellStyle?: React.CSSProperties) => {
    const showHiddenCount = hiddenPhotosCount > 0 && index === photos.length - 1;

    return (
      <div
        key={photo.fullImageUrl}
        className="position-relative overflow-hidden"
        style={{
          minWidth: 0,
          minHeight: 0,
          ...cellStyle,
        }}
      >
        <img
          src={photo.displayImageUrl}
          alt={`${alt} ${index + 1}`}
          className="w-100 h-100"
          loading={index === 0 ? 'eager' : 'lazy'}
          decoding="async"
          style={{ objectFit: 'cover' }}
        />
        {showHiddenCount && (
          <span
            className="position-absolute top-0 end-0 rounded-pill text-white"
            style={{
              margin: '0.5rem',
              padding: '0.3rem 0.55rem',
              backgroundColor: 'rgba(30, 30, 30, 0.82)',
              fontSize: '12px',
              fontWeight: 700,
              lineHeight: 1,
            }}
          >
            +{hiddenPhotosCount}
          </span>
        )}
      </div>
    );
  };

  if (photos.length === 1) {
    return renderPhotoCell(photos[0], 0);
  }

  if (photos.length === 2) {
    return (
      <div
        className="d-grid w-100 h-100"
        style={{
          gridTemplateColumns: '1fr',
          gridTemplateRows: '1fr 1fr',
          gap: previewGap,
        }}
      >
        {photos.map((photo, index) => renderPhotoCell(photo, index))}
      </div>
    );
  }

  return (
    <div
      className="d-grid w-100 h-100"
      style={{
        gridTemplateColumns: '1fr 1fr',
        gridTemplateRows: '1fr 1fr',
        gap: previewGap,
      }}
    >
      {renderPhotoCell(photos[0], 0, { gridColumn: '1 / span 2' })}
      {photos.slice(1, 3).map((photo, index) => renderPhotoCell(photo, index + 1))}
    </div>
  );
};

/**
 * LayoutModeIcon
 *
 * Draws the requested menu layout icons directly with Bootstrap-friendly
 * primitives so the toolbar order can match the design spec exactly.
 */
const LayoutModeIcon: React.FC<{
  mode: 'compact' | 'grid' | 'list';
  active: boolean;
}> = ({ mode, active }) => {
  const tone = active ? '#1B7F3A' : '#6B7280';
  const cellStyle: React.CSSProperties = {
    borderRadius: '4px',
    backgroundColor: tone,
  };

  if (mode === 'compact') {
    return (
      <span className="d-flex flex-column justify-content-center gap-1" aria-hidden="true" style={{ width: '18px', height: '18px' }}>
        <span style={{ ...cellStyle, width: '100%', flex: 1 }} />
        <span style={{ ...cellStyle, width: '100%', flex: 1 }} />
        <span style={{ ...cellStyle, width: '100%', flex: 1 }} />
      </span>
    );
  }

  if (mode === 'grid') {
    return (
      <span className="d-grid" aria-hidden="true" style={{ width: '18px', height: '18px', gridTemplateColumns: '1fr 1fr', gap: '2px' }}>
        <span style={cellStyle} />
        <span style={cellStyle} />
        <span style={cellStyle} />
        <span style={cellStyle} />
      </span>
    );
  }

  return (
    <span className="d-flex flex-column justify-content-center gap-1" aria-hidden="true" style={{ width: '18px', height: '18px' }}>
      {[0, 1, 2].map((row) => (
        <span key={row} className="d-flex align-items-center gap-1">
          <span style={{ ...cellStyle, width: '4px', height: '4px', borderRadius: '999px', flexShrink: 0 }} />
          <span style={{ ...cellStyle, height: '3px', flex: 1, borderRadius: '999px' }} />
        </span>
      ))}
    </span>
  );
};

/**
 * AddToCartGlyph
 *
 * Reuses the same shopping cart symbol as the cart navigation control while
 * preserving the plus mark that communicates an add action on product cards.
 * The plus stays on the left, bold and slightly smaller than the cart icon,
 * so the button reads as "add" first and "cart" second.
 */
function AddToCartGlyph({ cartSize, plusSize }: { cartSize: number; plusSize: number }) {
  return (
    <span className="d-inline-flex align-items-center justify-content-center gap-2" aria-hidden="true">
      <span style={{ fontSize: `${plusSize}px`, fontWeight: 800, lineHeight: 1 }}>
        +
      </span>
      <FaShoppingCart style={{ fontSize: `${cartSize}px` }} />
    </span>
  );
}

/**
 * ProductImageWithFallback
 *
 * Ensures product photos degrade gracefully across cards and modals.
 * Receives the preferred product image URL plus the cafe logo URL used in the
 * page header, then swaps to that logo whenever the product image is missing
 * or the file fails to load.
 */
const ProductImageWithFallback: React.FC<{
  src: string | null | undefined;
  fallbackSrc: string;
  alt: string;
  className?: string;
  style?: React.CSSProperties;
  loading?: 'eager' | 'lazy';
  decoding?: 'async' | 'auto' | 'sync';
  onClick?: React.MouseEventHandler<HTMLImageElement>;
}> = ({ src, fallbackSrc, alt, className, style, loading, decoding, onClick }) => {
  const normalizedSrc = src && src.trim().length > 0 ? src : fallbackSrc;
  const [resolvedSrc, setResolvedSrc] = useState<string>(normalizedSrc);

  useEffect(() => {
    setResolvedSrc(normalizedSrc);
  }, [normalizedSrc]);

  return (
    <img
      src={resolvedSrc}
      alt={alt}
      className={className}
      style={style}
      loading={loading}
      decoding={decoding}
      onClick={onClick}
      onError={() => {
        if (resolvedSrc !== fallbackSrc) {
          setResolvedSrc(fallbackSrc);
        }
      }}
    />
  );
};

const escapeHtmlText = (value: string): string => (
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')
);

const normalizeDescriptionText = (value: string): string => value.replace(/\s+/g, ' ').trim();

const getPlainTextFromDescriptionHtml = (html: string): string => {
  if (typeof document === 'undefined') {
    return normalizeDescriptionText(html.replace(/<[^>]*>/g, ' '));
  }

  const container = document.createElement('div');
  container.innerHTML = html;
  return normalizeDescriptionText(container.textContent ?? '');
};

const getWordSafePreviewText = (value: string): string => {
  const trimmedValue = value.trimEnd();
  const lastSpaceIndex = trimmedValue.lastIndexOf(' ');

  if (lastSpaceIndex > 24) {
    return trimmedValue.slice(0, lastSpaceIndex).trimEnd();
  }

  return trimmedValue;
};

const buildCollapsedDescriptionMeasurementHtml = (previewText: string, buttonLabel: string): string => (
  `${escapeHtmlText(previewText)}${previewText ? '... ' : ''}<span style="color: #1B7F3A; font-weight: 700; font-style: italic;">${escapeHtmlText(buttonLabel)}</span>`
);

const buildSeeMoreInlineHtml = (buttonLabel: string): string => (
  `<span data-testid="menu-product-description-see-more" class="text-success fw-bold fst-italic">${escapeHtmlText(buttonLabel)}</span>`
);

const normalizeDescriptionTextSegment = (value: string, hasCopiedText: boolean): string => {
  const normalizedValue = value.replace(/\s+/g, ' ');
  return hasCopiedText ? normalizedValue : normalizedValue.trimStart();
};

const appendHtmlPreviewNode = (
  sourceNode: ChildNode,
  targetParent: Node,
  state: { copiedTextLength: number; targetTextLength: number; appendTarget: Element | null },
): boolean => {
  if (state.copiedTextLength >= state.targetTextLength) {
    return false;
  }

  if (sourceNode.nodeType === Node.TEXT_NODE) {
    const segmentText = normalizeDescriptionTextSegment(sourceNode.textContent ?? '', state.copiedTextLength > 0);
    if (!segmentText) {
      return true;
    }

    const remainingLength = state.targetTextLength - state.copiedTextLength;
    const copiedText = segmentText.length <= remainingLength
      ? segmentText
      : getWordSafePreviewText(segmentText.slice(0, remainingLength));

    if (copiedText) {
      targetParent.appendChild(document.createTextNode(copiedText));
      state.copiedTextLength += copiedText.length;
      state.appendTarget = targetParent instanceof Element ? targetParent : null;
    }

    return state.copiedTextLength < state.targetTextLength;
  }

  if (sourceNode.nodeType !== Node.ELEMENT_NODE) {
    return true;
  }

  const sourceElement = sourceNode as Element;
  const clonedElement = sourceElement.cloneNode(false) as Element;
  targetParent.appendChild(clonedElement);
  state.appendTarget = clonedElement;

  for (const childNode of Array.from(sourceElement.childNodes)) {
    if (!appendHtmlPreviewNode(childNode, clonedElement, state)) {
      break;
    }
  }

  if (
    clonedElement.childNodes.length === 0
    && !['BR', 'HR', 'IMG'].includes(clonedElement.tagName.toUpperCase())
  ) {
    clonedElement.remove();
  }

  return state.copiedTextLength < state.targetTextLength;
};

const buildCollapsedDescriptionPreviewHtml = (
  html: string,
  previewText: string,
  buttonLabel: string,
): string => {
  if (typeof document === 'undefined') {
    return buildCollapsedDescriptionMeasurementHtml(previewText, buttonLabel);
  }

  const sourceContainer = document.createElement('div');
  sourceContainer.innerHTML = html;

  const previewContainer = document.createElement('div');
  const cloneState = {
    copiedTextLength: 0,
    targetTextLength: previewText.length,
    appendTarget: null as Element | null,
  };

  for (const childNode of Array.from(sourceContainer.childNodes)) {
    if (!appendHtmlPreviewNode(childNode, previewContainer, cloneState)) {
      break;
    }
  }

  const suffixTarget = cloneState.appendTarget ?? previewContainer;
  suffixTarget.insertAdjacentHTML(
    'beforeend',
    `${cloneState.copiedTextLength > 0 ? '... ' : ''}${buildSeeMoreInlineHtml(buttonLabel)}`,
  );

  return previewContainer.innerHTML;
};

/**
 * ExpandableDescription
 *
 * Renders product descriptions with a configurable line clamp. When the text is
 * cut, the collapsed HTML block becomes the click target and ends with an
 * inline translated "See More" suffix that wraps with normal text flow.
 */
const ExpandableDescription: React.FC<{
  html: string;
  textStyle: React.CSSProperties;
  buttonLabel: string;
  clampLines?: number;
}> = ({ html, textStyle, buttonLabel, clampLines = 8 }) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [collapsedDescription, setCollapsedDescription] = useState<{ isCut: boolean; previewHtml: string }>({
    isCut: false,
    previewHtml: '',
  });
  const contentRef = useRef<HTMLDivElement | null>(null);
  const measurementRef = useRef<HTMLDivElement | null>(null);
  const plainText = useMemo(() => getPlainTextFromDescriptionHtml(html), [html]);

  useEffect(() => {
    setIsExpanded(false);
  }, [html]);

  useEffect(() => {
    const measurementElement = measurementRef.current;
    if (!measurementElement || plainText.length === 0) {
      setCollapsedDescription({ isCut: false, previewHtml: html });
      return undefined;
    }

    /**
     * The collapsed version must reserve space for the inline "See More" text.
     * A hidden element with matching typography lets us binary-search the
     * longest preview that still fits inside the requested clamp height.
     */
    const measureCollapsedText = () => {
      const computedStyle = window.getComputedStyle(measurementElement);
      const fontSize = Number.parseFloat(computedStyle.fontSize) || 13;
      const parsedLineHeight = Number.parseFloat(computedStyle.lineHeight);
      const lineHeight = Number.isFinite(parsedLineHeight) ? parsedLineHeight : fontSize * 1.4;
      const maxCollapsedHeight = lineHeight * clampLines + 1;

      measurementElement.textContent = plainText;
      const fullTextIsCut = measurementElement.scrollHeight > maxCollapsedHeight;

      if (!fullTextIsCut) {
        setCollapsedDescription((previous) => (
          previous.isCut || previous.previewHtml !== html
            ? { isCut: false, previewHtml: html }
            : previous
        ));
        return;
      }

      let low = 0;
      let high = plainText.length;
      while (low < high) {
        const middle = Math.ceil((low + high) / 2);
        const candidatePreview = plainText.slice(0, middle).trimEnd();
        measurementElement.innerHTML = buildCollapsedDescriptionMeasurementHtml(candidatePreview, buttonLabel);

        if (measurementElement.scrollHeight <= maxCollapsedHeight) {
          low = middle;
        } else {
          high = middle - 1;
        }
      }

      const previewText = getWordSafePreviewText(plainText.slice(0, low));
      const previewHtml = buildCollapsedDescriptionPreviewHtml(html, previewText, buttonLabel);
      setCollapsedDescription((previous) => (
        !previous.isCut || previous.previewHtml !== previewHtml
          ? { isCut: true, previewHtml }
          : previous
      ));
    };

    measureCollapsedText();
    window.addEventListener('resize', measureCollapsedText);
    return () => window.removeEventListener('resize', measureCollapsedText);
  }, [buttonLabel, clampLines, html, plainText]);

  const expandDescription = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    setIsExpanded(true);
  }, []);

  const handleDescriptionKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    setIsExpanded(true);
  }, []);

  const collapsedStyle: React.CSSProperties = {
    ...textStyle,
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: clampLines,
    overflow: 'hidden',
  };

  return (
    <div className="position-relative" data-testid="menu-product-description-area">
      {collapsedDescription.isCut && !isExpanded ? (
        <div
          ref={contentRef}
          data-testid="menu-product-description"
          role="button"
          tabIndex={0}
          aria-expanded={false}
          style={{ ...collapsedStyle, cursor: 'pointer' }}
          onClick={expandDescription}
          onKeyDown={handleDescriptionKeyDown}
          dangerouslySetInnerHTML={{ __html: collapsedDescription.previewHtml }}
        />
      ) : (
        <div
          ref={contentRef}
          style={isExpanded ? textStyle : collapsedStyle}
          data-testid="menu-product-description"
          dangerouslySetInnerHTML={{ __html: html }}
        />
      )}
      <div
        ref={measurementRef}
        aria-hidden="true"
        style={{
          ...textStyle,
          position: 'absolute',
          visibility: 'hidden',
          pointerEvents: 'none',
          left: 0,
          top: 0,
          width: '100%',
          height: 'auto',
          overflow: 'visible',
        }}
      />
    </div>
  );
};


/**
 * ContactForm
 *
 * Collects the checkout callback details. Standard web checkout exposes the
 * full identity and channel chooser, while Telegram sessions without a
 * username reuse the same component in phone-only mode.
 */
const ContactForm: React.FC<{
  orderData: MenuOrderData;
  onUpdateOrderData: PartialOrderDataUpdater;
  orderErrors: OrderFormErrors;
  phoneOnly?: boolean;
  nameOnly?: boolean;
  texts: {
    name: string;
    preferredMethod: string;
    countryCode: string;
    phone: string;
    phoneMethod: string;
    telegram: string;
    countryCodeOptions: CountryCodeOption[];
  };
}> = ({ orderData, onUpdateOrderData, orderErrors, texts, phoneOnly = false, nameOnly = false }) => {
  const isTelegramPreferred = !phoneOnly && orderData.contacts?.preferableMethod === 'telegram';

  const contactButtonClass = (method: string) => (
    orderData.contacts?.preferableMethod === method
      ? 'btn btn-success border-0 rounded-pill w-100 d-flex align-items-center justify-content-center px-3 py-2'
      : 'btn btn-light border rounded-pill w-100 d-flex align-items-center justify-content-center px-3 py-2'
  );

  return (
    <div className="card border-0 rounded-4 shadow-sm mb-3">
      <div className="card-body p-3">
        {!phoneOnly && (
          <div className="mb-3">
            <input
              id="checkout-name-field"
              data-testid="checkout-name"
              className={`form-control bg-light ${orderErrors.name ? 'is-invalid' : ''}`}
              value={orderData.channel_first_name ?? ''}
              onChange={(event) => onUpdateOrderData({ channel_first_name: event.target.value })}
              placeholder={texts.name}
            />
          </div>
        )}

        {!phoneOnly && !nameOnly && (
          <div className="mb-3" data-testid="checkout-contact-methods">
            <div className="fw-semibold mb-2">{texts.preferredMethod}</div>
            <div className="row g-2">
              {['phone', 'viber', 'telegram', 'whatsapp'].map((method) => (
                <div className="col-6" key={method}>
                  <input
                    type="radio"
                    className="btn-check"
                    id={`contact-${method}`}
                    name="contact-method"
                    checked={orderData.contacts?.preferableMethod === method}
                    onChange={() => onUpdateOrderData({
                      contacts: { ...orderData.contacts, preferableMethod: method },
                    })}
                  />
                  <label className={contactButtonClass(method)} htmlFor={`contact-${method}`}>
                    <span className="small text-capitalize">{method === 'phone' ? texts.phoneMethod : method}</span>
                  </label>
                </div>
              ))}
            </div>
          </div>
        )}

        {!nameOnly && (!isTelegramPreferred ? (
          <div className="row g-2">
            <div className="col-5">
              <select
                id="checkout-country-code-field"
                data-testid="checkout-country-code"
                className={`form-select bg-light ${orderErrors.countryCode ? 'is-invalid' : ''}`}
                value={orderData.countryCode ?? ''}
                onChange={(event) => onUpdateOrderData({ countryCode: event.target.value })}
              >
                <option value="">{texts.countryCode}</option>
                {texts.countryCodeOptions.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
            <div className="col-7">
              <input
                id="checkout-phone-field"
                data-testid="checkout-phone"
                className={`form-control bg-light ${orderErrors.phone ? 'is-invalid' : ''}`}
                value={orderData.phone ?? ''}
                onChange={(event) => onUpdateOrderData({ phone: event.target.value.replace(/\D/g, '') })}
                placeholder={texts.phone}
              />
            </div>
          </div>
        ) : (
          <input
            id="checkout-telegram-field"
            data-testid="checkout-telegram"
            className={`form-control bg-light ${orderErrors.telegramName ? 'is-invalid' : ''}`}
            value={orderData.contacts?.telegram?.userName ?? ''}
            onChange={(event) => onUpdateOrderData({
              contacts: {
                ...orderData.contacts,
                telegram: { ...orderData.contacts?.telegram, userName: event.target.value },
              },
            })}
            placeholder={texts.telegram}
          />
        ))}
      </div>
    </div>
  );
};

/**
 * SegmentedToggle
 *
 * Renders a two-option segmented control with equal-width actions so paired
 * checkout choices stay visually balanced and aligned across the form.
 */
const SegmentedToggle: React.FC<{
  name: string;
  options: Array<{
    id: string;
    label: string;
    checked: boolean;
    onChange: () => void;
    testId?: string;
    disabled?: boolean;
  }>;
}> = ({ name, options }) => (
  <div className="row g-2">
    {options.map((option) => (
      <div className="col-6" key={option.id}>
        <input
          type="radio"
          className="btn-check"
          id={option.id}
          name={name}
          checked={option.checked}
          disabled={option.disabled}
          onChange={() => {
            if (!option.disabled) {
              option.onChange();
            }
          }}
        />
        <label
          data-testid={option.testId}
          className={`btn w-100 rounded-pill py-2 fw-semibold d-flex align-items-center justify-content-center ${option.disabled ? 'btn-light border text-secondary opacity-75' : (option.checked ? 'btn-success border-0 text-white' : 'btn-light border')}`}
          htmlFor={option.id}
          aria-disabled={option.disabled}
        >
          {option.label}
        </label>
      </div>
    ))}
  </div>
);

/**
 * DeliveryBlock
 *
 * Captures delivery, address, and payment details for the current order using
 * the same data shape expected by the active backend checkout flow.
 */
const DeliveryBlock: React.FC<{
  partnerName: string;
  isCashlessTerminalAvailable: boolean;
  isDeliveryUnavailableNow: boolean;
  deliveryScheduleSelection: DeliveryScheduleSelection;
  mapInitialCenter: DeliveryCoordinates;
  selectedDeliveryPosition: DeliveryCoordinates | null;
  deliverySelectionLocation?: string | null;
  orderData: MenuOrderData;
  onUpdateOrderData: PartialOrderDataUpdater;
  orderErrors: OrderFormErrors;
  labels: {
    howToGet: string;
    selfPickup: string;
    delivery: string;
    deliveryPoint: string;
    tapToChoose: string;
    selectDeliveryPoint: string;
    expandMap: string;
    collapseMap: string;
    address: string;
    entrance: string;
    code: string;
    floor: string;
    apartment: string;
    paymentMethod: string;
    cash: string;
    bankCard: string;
    needChange: string;
    yes: string;
    no: string;
    changeAmount: string;
    deliveryDay: string;
    deliveryInterval: string;
    pickupDay: string;
    pickupInterval: string;
    deliveryTime: string;
    whenReady: string;
    atTheTime: string;
    selectTime: string;
    noDeliveryTimesAvailable: string;
    noPickupIntervalsAvailable: string;
    prepareChangeAmount: string;
    mapLoading: string;
    mapLoadFailed: string;
    mapUnavailableDescription: string;
    retry: string;
    reset: string;
    deliveryInstructionsPlaceholder: string;
  };
}> = ({
  partnerName,
  isCashlessTerminalAvailable,
  isDeliveryUnavailableNow,
  deliveryScheduleSelection,
  mapInitialCenter,
  selectedDeliveryPosition,
  deliverySelectionLocation,
  orderData,
  onUpdateOrderData,
  orderErrors,
  labels,
}) => {
  const isDelivery = orderData.deliveryNeeded === 'Yes';
  const showChangeSection = isDelivery && orderData.paymentMethod !== 'bank_card';
  const shouldShowDeliverySchedule = !deliveryScheduleSelection.isAsapOnly;
  const selectedDate = orderData.date ?? deliveryScheduleSelection.dayOptions[0]?.value ?? '';
  const selectedIntervalCandidate = deliveryScheduleSelection.selectedIntervalCandidate;
  const shouldShowExactTimeToggle = shouldShowDeliverySchedule
    && deliveryScheduleSelection.isExactTimeAllowed
    && selectedIntervalCandidate !== null;
  const shouldShowScheduleSelectors = shouldShowDeliverySchedule
    && (!shouldShowExactTimeToggle || orderData.deliveryInTime === 'Yes');
  const exactTimeOptions = useMemo(
    () => selectedIntervalCandidate
      ? getCandidateExactTimeOptions(selectedIntervalCandidate, deliveryScheduleSelection.readinessTime)
      : [],
    [deliveryScheduleSelection.readinessTime, selectedIntervalCandidate],
  );

  useEffect(() => {
    if (!isCashlessTerminalAvailable && orderData.paymentMethod === 'bank_card') {
      onUpdateOrderData({ paymentMethod: 'cash' });
    }
  }, [isCashlessTerminalAvailable, onUpdateOrderData, orderData.paymentMethod]);

  useEffect(() => {
    if (isDeliveryUnavailableNow && orderData.deliveryNeeded === 'Yes') {
      onUpdateOrderData({ deliveryNeeded: 'No' });
    }
  }, [isDeliveryUnavailableNow, onUpdateOrderData, orderData.deliveryNeeded]);

  return (
    <div className="card border-0 rounded-4 shadow-sm mb-3">
      <div className="card-body p-3">
        <div className="mb-3">
          <div className="fw-semibold mb-2">{labels.howToGet}</div>
          <SegmentedToggle
            name="delivery-needed"
            options={[
              {
                id: 'delivery-yes',
                label: labels.delivery,
                checked: orderData.deliveryNeeded === 'Yes',
                onChange: () => onUpdateOrderData({ deliveryNeeded: 'Yes' }),
                testId: 'delivery-option',
                disabled: isDeliveryUnavailableNow,
              },
              {
                id: 'delivery-pickup',
                label: labels.selfPickup,
                checked: orderData.deliveryNeeded === 'No',
                onChange: () => onUpdateOrderData({ deliveryNeeded: 'No' }),
                testId: 'delivery-pickup-option',
              },
            ]}
          />
        </div>

        {shouldShowDeliverySchedule && (
          <div className="mb-3" data-testid="delivery-schedule-section">
            {shouldShowExactTimeToggle && (
              <div className="rounded-4 mb-3" data-testid="delivery-exact-time-section">
                <div className="fw-semibold mb-2">{labels.deliveryTime}</div>
                <SegmentedToggle
                  name="delivery-in-time"
                  options={[
                    {
                      id: 'delivery-when-ready',
                      label: labels.whenReady,
                      checked: orderData.deliveryInTime !== 'Yes',
                      onChange: () => onUpdateOrderData({ deliveryInTime: 'No', time: null }),
                      testId: 'delivery-when-ready-option',
                    },
                    {
                      id: 'delivery-at-time',
                      label: labels.atTheTime,
                      checked: orderData.deliveryInTime === 'Yes',
                      onChange: () => onUpdateOrderData({ deliveryInTime: 'Yes' }),
                      testId: 'delivery-at-time-option',
                    },
                  ]}
                />
              </div>
            )}

            {shouldShowScheduleSelectors && (
              <>
                <div
                  id="checkout-delivery-date-field"
                  tabIndex={-1}
                  className={`mb-3 rounded-4 ${orderErrors.deliveryDate ? 'border border-danger p-2' : ''}`}
                >
                  <div className="fw-semibold mb-2">{isDelivery ? labels.deliveryDay : labels.pickupDay}</div>
                  {deliveryScheduleSelection.dayOptions.length > 0 ? (
                    <div className="row g-2">
                      {deliveryScheduleSelection.dayOptions.map((dayOption) => (
                        <div className="col-6" key={dayOption.value}>
                          <input
                            type="radio"
                            className="btn-check"
                            id={`delivery-day-${dayOption.value}`}
                            name="delivery-day"
                            checked={selectedDate === dayOption.value}
                            onChange={() => onUpdateOrderData({
                              date: dayOption.value,
                              deliveryInterval: null,
                              time: null,
                            })}
                          />
                          <label
                            data-testid="delivery-day-option"
                            className={`btn w-100 rounded-pill py-2 fw-semibold d-flex align-items-center justify-content-center ${selectedDate === dayOption.value ? 'btn-success border-0 text-white' : 'btn-light border'}`}
                            htmlFor={`delivery-day-${dayOption.value}`}
                          >
                            {dayOption.label}
                          </label>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <Alert variant="warning" className="py-2 px-3 mb-0 small" data-testid="delivery-times-unavailable">
                      {isDelivery ? labels.noDeliveryTimesAvailable : labels.noPickupIntervalsAvailable}
                    </Alert>
                  )}
                </div>

                {deliveryScheduleSelection.dayOptions.length > 0 && (
                  <div
                    id="checkout-delivery-interval-field"
                    tabIndex={-1}
                    className={`mb-3 rounded-4 ${orderErrors.deliveryInterval ? 'border border-danger p-2' : ''}`}
                  >
                    <div className="fw-semibold mb-2">{isDelivery ? labels.deliveryInterval : labels.pickupInterval}</div>
                    {deliveryScheduleSelection.selectedDayIntervals.length > 0 ? (
                      <div className="row g-2">
                        {deliveryScheduleSelection.selectedDayIntervals.map((interval, index) => (
                          <div className="col-6" key={interval}>
                            <input
                              type="radio"
                              className="btn-check"
                              id={`delivery-interval-${index}`}
                              name="delivery-interval"
                              checked={orderData.deliveryInterval === interval}
                              onChange={() => onUpdateOrderData({
                                deliveryInterval: interval,
                                time: null,
                              })}
                            />
                            <label
                              data-testid="delivery-interval-option"
                              className={`btn w-100 rounded-pill py-2 fw-semibold d-flex align-items-center justify-content-center ${orderData.deliveryInterval === interval ? 'btn-success border-0 text-white' : 'btn-light border'}`}
                              htmlFor={`delivery-interval-${index}`}
                            >
                              {interval}
                            </label>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <Alert variant="warning" className="py-2 px-3 mb-0 small" data-testid="delivery-times-unavailable">
                        {isDelivery ? labels.noDeliveryTimesAvailable : labels.noPickupIntervalsAvailable}
                      </Alert>
                    )}
                  </div>
                )}

                {shouldShowExactTimeToggle && selectedIntervalCandidate && (
                  <div className="d-flex align-items-center gap-2 mb-3 flex-nowrap">
                    <label className="fw-semibold flex-grow-1 mb-0 ps-3" style={{ minWidth: 0 }} htmlFor="checkout-delivery-time-field">
                      {labels.selectTime}
                    </label>
                    <select
                      id="checkout-delivery-time-field"
                      data-testid="checkout-delivery-time"
                      className={`form-select bg-light flex-shrink-0 ${orderErrors.deliveryTime ? 'is-invalid' : ''}`}
                      style={{ width: '132px' }}
                      value={orderData.time ?? ''}
                      onChange={(event) => onUpdateOrderData({ time: event.target.value || null })}
                    >
                      <option value="">--:--</option>
                      {exactTimeOptions.map((timeOption) => (
                        <option key={timeOption} value={timeOption}>{timeOption}</option>
                      ))}
                    </select>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {isDelivery && (
          <>
            <DeliveryMapPicker
              initialCenter={mapInitialCenter}
              selectedPosition={selectedDeliveryPosition}
              selectionLocation={deliverySelectionLocation}
              deliveryAddress={orderData.deliveryAddress}
              onUpdateOrderData={onUpdateOrderData}
              title={labels.deliveryPoint}
              tapToChoose={labels.tapToChoose}
              fullscreenTitle={labels.selectDeliveryPoint}
              expand={labels.expandMap}
              collapse={labels.collapseMap}
              mapLoading={labels.mapLoading}
              mapLoadFailed={labels.mapLoadFailed}
              mapUnavailableDescription={labels.mapUnavailableDescription}
              retry={labels.retry}
              hasError={orderErrors.deliveryPoint}
            />
            <div className="mb-3">
              <input
                id="checkout-delivery-address-field"
                data-testid="checkout-delivery-address"
                className={`form-control bg-light ${orderErrors.deliveryAddress ? 'is-invalid' : ''}`}
                value={orderData.deliveryAddress ?? ''}
                onChange={(event) => onUpdateOrderData({ deliveryAddress: event.target.value })}
                placeholder={`${labels.address} ${partnerName}`}
              />
            </div>
            <div className="row g-2 mb-3">
              <div className="col-6">
                <input className="form-control bg-light" value={orderData.entrance ?? ''} onChange={(event) => onUpdateOrderData({ entrance: event.target.value })} placeholder={labels.entrance} />
              </div>
              <div className="col-6">
                <input className="form-control bg-light" value={orderData.entrance_code ?? ''} onChange={(event) => onUpdateOrderData({ entrance_code: event.target.value })} placeholder={labels.code} />
              </div>
              <div className="col-6">
                <input className="form-control bg-light" value={orderData.floor ?? ''} onChange={(event) => onUpdateOrderData({ floor: event.target.value })} placeholder={labels.floor} />
              </div>
              <div className="col-6">
                <input className="form-control bg-light" value={orderData.apartment ?? ''} onChange={(event) => onUpdateOrderData({ apartment: event.target.value })} placeholder={labels.apartment} />
              </div>
              <div className="col-12">
                <textarea
                  className="form-control bg-light"
                  rows={2}
                  value={orderData.delivery_instructions ?? ''}
                  onChange={(event) => onUpdateOrderData({ delivery_instructions: event.target.value })}
                  placeholder={labels.deliveryInstructionsPlaceholder}
                />
              </div>
            </div>
          </>
        )}

        {isCashlessTerminalAvailable && (
          <div className="mb-3">
            <div className="fw-semibold mb-2">{labels.paymentMethod}</div>
            <SegmentedToggle
              name="payment-method"
              options={[
                {
                  id: 'payment-cash',
                  label: labels.cash,
                  checked: orderData.paymentMethod !== 'bank_card',
                  onChange: () => onUpdateOrderData({ paymentMethod: 'cash' }),
                  testId: 'payment-cash-option',
                },
                {
                  id: 'payment-card',
                  label: labels.bankCard,
                  checked: orderData.paymentMethod === 'bank_card',
                  onChange: () => onUpdateOrderData({ paymentMethod: 'bank_card' }),
                  testId: 'payment-card-option',
                },
              ]}
            />
          </div>
        )}

        {showChangeSection && (
          <div>
            <div className="fw-semibold mb-2">{labels.needChange}</div>
            <SegmentedToggle
              name="change-needed"
              options={[
                {
                  id: 'change-no',
                  label: labels.no,
                  checked: orderData.changeNeeded !== 'Yes',
                  onChange: () => onUpdateOrderData({ changeNeeded: 'No', changeAmount: null }),
                },
                {
                  id: 'change-yes',
                  label: labels.yes,
                  checked: orderData.changeNeeded === 'Yes',
                  onChange: () => onUpdateOrderData({ changeNeeded: 'Yes' }),
                  testId: 'change-yes-option',
                },
              ]}
            />
            {orderData.changeNeeded === 'Yes' && (
              <div className="mt-3">
                {/**
                 * The change block keeps quick-add presets above the manual field
                 * so clients can grow the requested amount incrementally and still
                 * override or clear it explicitly.
                 */}
                <div className="d-flex align-items-center gap-2 mb-3 flex-nowrap">
                  <div className="fw-semibold flex-grow-1 ps-3" style={{ minWidth: 0 }}>
                    {labels.prepareChangeAmount}
                  </div>
                  <input
                    id="checkout-change-amount-field"
                    data-testid="checkout-change-amount"
                    type="number"
                    className={`form-control bg-light flex-shrink-0 ${orderErrors.changeAmount ? 'is-invalid' : ''}`}
                    style={{ width: '60px' }}
                    value={orderData.changeAmount ?? ''}
                    onChange={(event) => onUpdateOrderData({ changeAmount: event.target.value === '' ? null : Number(event.target.value) })}
                  />
                  <button
                    type="button"
                    className="btn btn-light border rounded-pill px-3 flex-shrink-0"
                    onClick={() => onUpdateOrderData({ changeAmount: null })}
                  >
                    ↺
                  </button>
                </div>
                <div className="row g-2 flex-nowrap">
                  {[5, 10, 20, 50].map((amount) => (
                    <div className="col" key={amount}>
                      <button
                        type="button"
                        className="btn btn-outline-success rounded-pill w-100 fw-semibold py-1"
                        style={{ fontSize: '14px' }}
                        onClick={() => onUpdateOrderData({ changeAmount: (orderData.changeAmount ?? 0) + amount })}
                      >
                        +{amount} €
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * CommentsField
 *
 * Captures free-form client instructions and keeps them attached to the order
 * draft through checkout confirmation.
 */
const CommentsField: React.FC<{
  comment: string;
  onChange: (comment: string) => void;
  title: string;
  placeholder: string;
}> = ({ comment, onChange, title, placeholder }) => (
  <div className="card border-0 rounded-4 shadow-sm mb-3">
    <div className="card-body p-3">
      <div className="fw-semibold mb-2">{title}</div>
      <textarea data-testid="checkout-comment" className="form-control bg-light" rows={2} value={comment} onChange={(event) => onChange(event.target.value)} placeholder={placeholder} />
    </div>
  </div>
);

/**
 * LoyaltyCardNumberField
 *
 * Captures an optional partner loyalty-card identifier immediately before the
 * checkout comment. It receives the current order-draft value and localized
 * labels, filters unsupported characters while typing, and applies canonical
 * separator formatting when the field loses focus.
 */
const LoyaltyCardNumberField: React.FC<{
  value: string;
  onChange: (value: string) => void;
  label: string;
  placeholder: string;
}> = ({ value, onChange, label, placeholder }) => (
  <div className="card border-0 rounded-4 shadow-sm mb-3">
    <div className="card-body p-3">
      <Form.Label htmlFor="checkout-loyalty-card-number" className="fw-semibold mb-2">
        {label}
      </Form.Label>
      <Form.Control
        id="checkout-loyalty-card-number"
        data-testid="checkout-loyalty-card-number"
        type="text"
        inputMode="text"
        autoComplete="off"
        className="bg-light"
        value={value}
        placeholder={placeholder}
        onChange={(event) => onChange(sanitizeLoyaltyCardNumberInput(event.target.value))}
        onBlur={() => onChange(normalizeLoyaltyCardNumber(value) ?? '')}
      />
    </div>
  </div>
);

/**
 * ProductDetailModal
 *
 * Configures a complex product before adding it to the cart and keeps the cart
 * item payload aligned with the existing backend structure.
 */
const ProductDetailModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  item: MenuItemWithKey | null;
  menu: FoodMakerMenu;
  language: string;
  priceLevel: number | string | null | undefined;
  logoUrl: string | null;
  translations: Record<string, Record<string, string>>;
  labels: {
    chooseVersion: string;
    addExtras: string;
    buildCombo: string;
    quantity: string;
    addToCart: string;
    cancel: string;
    selectRequired: string;
  };
  onConfirm: (cartItem: CartProductItem) => void;
}> = ({ isOpen, onClose, item, menu, language, priceLevel, logoUrl, translations, labels, onConfirm }) => {
  const [selectedVersion, setSelectedVersion] = useState<string | null>(null);
  const [selectedAdds, setSelectedAdds] = useState<string[]>([]);
  const [selectedCombo, setSelectedCombo] = useState<Record<string, string[]>>({});
  const [quantity, setQuantity] = useState<number>(1);

  useEffect(() => {
    if (!item) {
      return;
    }

    const currentMenuItem = menu[item.product_key] as MenuItemWithPrice | undefined;
    const versionPrices = currentMenuItem ? getEffectiveVersionPriceMap(currentMenuItem, priceLevel) : getEffectiveVersionPriceMap(item, priceLevel);
    const versionOptions = getVersionOptions(item, versionPrices, priceLevel);
    setSelectedVersion(versionOptions.length > 0 ? versionOptions[0].key : null);
    setSelectedAdds([]);
    setQuantity(item.min_quantity ?? 1);

    const initialCombo: Record<string, string[]> = {};
    (item.combo ?? []).forEach((section) => {
      if (!section.is_active || section.section_elements.length === 0) {
        return;
      }
      initialCombo[section.section_name_code] = [section.section_elements[0]];
    });
    setSelectedCombo(initialCombo);
  }, [item, menu, priceLevel]);

  if (!item) {
    return null;
  }

  const currentMenuItem = menu[item.product_key] as MenuItemWithPrice | undefined;
  const effectiveItem = currentMenuItem ?? item;
  const versionPrices = getEffectiveVersionPriceMap(effectiveItem, priceLevel);
  const versionOptions = getVersionOptions(item, versionPrices, priceLevel)
    .map((option) => ({
      ...option,
      label: resolveMenuText(translations, option.key, language, option.label),
    }));
  const basePrice = selectedVersion
    ? getEffectiveVersionPrice(effectiveItem, priceLevel, selectedVersion)
    : getEffectiveItemPrice(effectiveItem, priceLevel);
  const addsTotal = selectedAdds.reduce((sum, addKey) => {
    const addItem = menu[addKey] as MenuItemWithPrice | undefined;
    return sum + (addItem ? getEffectiveItemPrice(addItem, priceLevel) : 0);
  }, 0);
  const comboSelections = Object.values(selectedCombo).flat();
  const comboSections = item.combo?.filter((section) => section.is_active) ?? [];
  const isComboValid = comboSections.every((section) => (selectedCombo[section.section_name_code] ?? []).length > 0);
  const imageUrl = getItemImageUrl(item, 'detail') ?? logoUrl ?? '';
  const itemName = resolveMenuText(translations, item.name_code, language, item.name_code);
  const descriptionText = toPlainText(
    item.description_code
      ? resolveMenuText(translations, item.description_code, language, item.description_code)
      : ''
  );
  const totalUnitPrice = basePrice + addsTotal;
  const totalPrice = totalUnitPrice * quantity;

  const handleToggleAdd = (addKey: string) => {
    setSelectedAdds((previous) => (
      previous.includes(addKey) ? previous.filter((key) => key !== addKey) : [...previous, addKey]
    ));
  };

  const handleComboChange = (sectionKey: string, elementKey: string, isMultiple: boolean) => {
    setSelectedCombo((previous) => {
      const current = previous[sectionKey] ?? [];
      if (isMultiple) {
        return {
          ...previous,
          [sectionKey]: current.includes(elementKey)
            ? current.filter((key) => key !== elementKey)
            : [...current, elementKey],
        };
      }
      return {
        ...previous,
        [sectionKey]: [elementKey],
      };
    });
  };

  /**
   * Renders the rounded selection rows used for versions, add-ons, and combo
   * choices so all configurable product options share the same visual system.
   */
  const renderSelectableRow = ({
    id,
    checked,
    controlType,
    onChange,
    label,
    priceLabel,
  }: {
    id: string;
    checked: boolean;
    controlType: 'radio' | 'checkbox';
    onChange: () => void;
    label: string;
    priceLabel?: string;
  }) => (
    <label
      key={id}
      htmlFor={id}
      className="bg-white border d-flex align-items-center gap-3 rounded-pill px-4 py-3"
      style={{ borderColor: '#E9EEF3', minHeight: '86px', cursor: 'pointer', boxShadow: '0 0.2rem 0.8rem rgba(30, 30, 30, 0.03)' }}
    >
      <input
        id={id}
        type={controlType}
        className="d-none"
        checked={checked}
        onChange={onChange}
      />
      <span
        className={`rounded-circle d-inline-flex align-items-center justify-content-center flex-shrink-0 ${checked ? 'bg-success text-white border-0' : 'bg-white border'}`}
        style={{ width: '30px', height: '30px', borderColor: '#CBD5E1' }}
      >
        {checked ? <FaCheck size={15} /> : null}
      </span>
      <span className="flex-grow-1" style={{ fontSize: '17px', fontWeight: 500, color: '#0E1730', minWidth: 0 }}>
        {label}
      </span>
      {priceLabel && (
        <span className="flex-shrink-0 text-success" style={{ fontSize: '17px', fontWeight: 700 }}>
          {priceLabel}
        </span>
      )}
    </label>
  );

  /**
   * Keeps section titles visually aligned with the target design by using
   * a muted uppercase caption style above each logical configuration block.
   */
  const renderSectionTitle = (title: string) => (
    <div
      className="text-uppercase mb-3"
      style={{ fontSize: '15px', fontWeight: 800, letterSpacing: '0.03em', color: '#94A3B8' }}
    >
      {title}
    </div>
  );

  return (
    <Modal
      show={isOpen}
      onHide={onClose}
      fullscreen="sm-down"
      scrollable
      contentClassName="border-0 bg-transparent"
      dialogClassName="m-0 m-sm-auto"
    >
      <Modal.Body className="p-0">
        <div className="bg-light" style={{ minHeight: '100%' }}>
          <div className="position-relative" style={{ minHeight: '380px', height: '48vh', maxHeight: '460px', backgroundColor: '#0E1730' }}>
            <ProductImageWithFallback
              src={imageUrl}
              fallbackSrc={logoUrl ?? '/Assets/images/logo_ifood.png'}
              alt={itemName}
              className="w-100 h-100 object-fit-cover"
              decoding="async"
            />
            <button
              type="button"
              className="btn btn-light rounded-circle position-absolute top-0 start-0 m-4 d-flex align-items-center justify-content-center"
              style={{ width: '56px', height: '56px' }}
              onClick={onClose}
              aria-label={labels.cancel}
            >
              <FaArrowLeft size={24} />
            </button>
            <button
              type="button"
              className="btn btn-light rounded-circle position-absolute top-0 end-0 m-4 d-flex align-items-center justify-content-center"
              style={{ width: '56px', height: '56px' }}
              onClick={onClose}
              aria-label={labels.cancel}
            >
              <span style={{ fontSize: '32px', lineHeight: 1, color: '#475467', marginTop: '-2px' }}>×</span>
            </button>
          </div>

          <div
            className="bg-light position-relative"
            style={{ marginTop: '-28px', borderTopLeftRadius: '28px', borderTopRightRadius: '28px' }}
          >
            <div className="px-4 pt-4 pb-4">
              <div className="mb-4">
                <h2
                  className="mb-2"
                  style={{
                    fontSize: '34px',
                    fontWeight: 800,
                    lineHeight: 1.05,
                    color: '#0E1730',
                    minWidth: 0,
                    overflowWrap: 'anywhere',
                    wordBreak: 'break-word',
                  }}
                >
                  {itemName}
                </h2>
                <div
                  className="d-flex justify-content-end"
                >
                  <div
                    className="text-success text-end text-nowrap"
                    style={{ fontSize: '28px', fontWeight: 800, lineHeight: 1.1 }}
                  >
                    {formatEuro(totalUnitPrice)}
                  </div>
                </div>
              </div>

              {descriptionText && (
                <div className="mb-4">
                  <p className="mb-0" style={{ fontSize: '16px', lineHeight: 1.7, color: '#475467' }}>
                    {descriptionText}
                  </p>
                </div>
              )}

              {versionOptions.length > 0 && (
                <div className="mb-4">
                  {renderSectionTitle(labels.chooseVersion)}
                  <div className="d-flex flex-column gap-3">
                    {versionOptions.map((option) => renderSelectableRow({
                      id: `product-version-${option.key}`,
                      checked: selectedVersion === option.key,
                      controlType: 'radio',
                      onChange: () => setSelectedVersion(option.key),
                      label: option.label,
                      priceLabel: formatEuro(option.price),
                    }))}
                  </div>
                </div>
              )}

              {item.possible_adds && item.possible_adds.length > 0 && (
                <div className="mb-4">
                  {renderSectionTitle(labels.addExtras)}
                  <div className="d-flex flex-column gap-3">
                    {item.possible_adds.map((addKey) => {
                      const addItem = menu[addKey] as MenuItemWithPrice | undefined;
                      const addPrice = addItem ? getEffectiveItemPrice(addItem, priceLevel) : 0;
                      if (!addItem || addPrice <= 0) {
                        return null;
                      }
                      return renderSelectableRow({
                        id: `product-add-${addKey}`,
                        checked: selectedAdds.includes(addKey),
                        controlType: 'checkbox',
                        onChange: () => handleToggleAdd(addKey),
                        label: resolveMenuText(translations, addItem?.name_code ?? addKey, language, addItem?.name_code ?? addKey),
                        priceLabel: `+${formatEuro(addPrice)}`,
                      });
                    })}
                  </div>
                </div>
              )}

              {comboSections.length > 0 && (
                <div className="mb-4">
                  {renderSectionTitle(labels.buildCombo)}
                  <div className="d-flex flex-column gap-4">
                    {comboSections.map((section: ComboSection) => (
                      <div key={section.section_name_code}>
                        <div className="mb-3" style={{ fontSize: '18px', fontWeight: 700, color: '#0E1730' }}>
                          {resolveMenuText(translations, section.section_name_code, language, section.section_name_code)}
                        </div>
                        <div className="d-flex flex-column gap-3">
                          {section.section_elements.map((elementKey) => renderSelectableRow({
                            id: `combo-${section.section_name_code}-${elementKey}`,
                            checked: (selectedCombo[section.section_name_code] ?? []).includes(elementKey),
                            controlType: section.is_multiple_choices ? 'checkbox' : 'radio',
                            onChange: () => handleComboChange(section.section_name_code, elementKey, section.is_multiple_choices),
                            label: resolveMenuText(translations, menu[elementKey]?.name_code ?? elementKey, language, menu[elementKey]?.name_code ?? elementKey),
                          }))}
                        </div>
                      </div>
                    ))}
                  </div>
                  {!isComboValid && <div className="text-danger small mt-3">{labels.selectRequired}</div>}
                </div>
              )}

              <div className="pt-2 text-center">
                {renderSectionTitle(labels.quantity)}
                <div className="d-inline-flex align-items-center gap-4 mt-2">
                  <button
                    type="button"
                    className="btn btn-light rounded-circle d-flex align-items-center justify-content-center border-0"
                    style={{ width: '58px', height: '58px', boxShadow: '0 0.35rem 1rem rgba(30, 30, 30, 0.06)' }}
                    onClick={() => setQuantity((previous) => Math.max(item.min_quantity ?? 1, previous - (item.unit_step ?? 1)))}
                  >
                    <FaMinus size={24} />
                  </button>
                  <span style={{ minWidth: '28px', fontSize: '28px', fontWeight: 600, color: '#0E1730' }}>
                    {quantity}
                  </span>
                  <button
                    type="button"
                    className="btn btn-success rounded-circle d-flex align-items-center justify-content-center border-0"
                    style={{ width: '58px', height: '58px', boxShadow: '0 0.45rem 1.2rem rgba(27, 127, 58, 0.22)' }}
                    onClick={() => setQuantity((previous) => previous + (item.unit_step ?? 1))}
                  >
                    <FaPlus size={24} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer className="border-0 bg-light px-4 pb-4 pt-3">
        <Button
          variant="success"
          data-testid="product-options-add-to-cart"
          className="w-100 rounded-pill py-3 fw-bold border-0"
          style={{ fontSize: '20px', boxShadow: '0 0.5rem 1.2rem rgba(27, 127, 58, 0.22)' }}
          disabled={(Boolean(comboSections.length) && !isComboValid) || totalUnitPrice <= 0}
          onClick={() => {
            if (totalUnitPrice <= 0) {
              return;
            }
            onConfirm({
              product_key: item.product_key,
              product_version: selectedVersion,
              combo_product_list: comboSelections.length > 0 ? comboSelections : null,
              adds: selectedAdds.length > 0 ? selectedAdds : null,
              total_cost: totalUnitPrice,
              quantity,
            });
            onClose();
          }}
        >
          {labels.addToCart} • {formatEuro(totalPrice)}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

/**
 * CheckoutModal
 *
 * Holds the cart, checkout, confirmation, and receipt steps inside one modal so
 * the new menu flow can reuse the existing backend payload and delivery logic.
 */
const CheckoutModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  step: CheckoutStep;
  onStepChange: (step: CheckoutStep) => void;
  shoppingCart: ShoppingCart;
  onUpdateCart: (cart: ShoppingCart) => void;
  menu: FoodMakerMenu;
  partnerData: PartnerDataType | null;
  isCashlessTerminalAvailable: boolean;
  isDeliveryUnavailableNow: boolean;
  deliveryScheduleSelection: DeliveryScheduleSelection;
  deliveryMapContext: DeliveryMapContext;
  language: string;
  priceLevel: number | string | null | undefined;
  effectiveOrderChannel: string;
  shouldForcePhoneContact: boolean;
  shouldUsePhoneOnlyTelegramContact: boolean;
  simpleOrderPlacement: boolean;
  orderData: MenuOrderData;
  onUpdateOrderData: React.Dispatch<React.SetStateAction<MenuOrderData>>;
  orderErrors: OrderFormErrors;
  promoCode: string;
  onPromoCodeChange: (value: string) => void;
  promoData: PromoData | null;
  onPromoDataChange: (value: PromoData | null) => void;
  promoMessage: string | null;
  onPromoMessageChange: (value: string | null) => void;
  isApplyingPromo: boolean;
  onApplyingPromoChange: (value: boolean) => void;
  onConfirmOrder: () => void;
  isSubmittingOrder: boolean;
  receiptHtml: string;
  receiptNotice: ReceiptNotice | null;
  receiptSummary: ReceiptSummary;
  deliveryCostState: DeliveryCostState;
  orderFlowWarning: string | null;
  shouldScrollToOrderFlowWarning: boolean;
  unavailableCartItemNames: string[];
  isOrderingBlocked: boolean;
  isReviewBlocked: boolean;
  isConfirmBlocked: boolean;
  orderingBlockedNoticeHtml: string | null;
  fulfilmentNotice: string | null;
  labels: CheckoutLabels;
}> = ({
  isOpen,
  onClose,
  step,
  onStepChange,
  shoppingCart,
  onUpdateCart,
  menu,
  partnerData,
  isCashlessTerminalAvailable,
  isDeliveryUnavailableNow,
  deliveryScheduleSelection,
  deliveryMapContext,
  language,
  priceLevel,
  effectiveOrderChannel,
  shouldForcePhoneContact,
  shouldUsePhoneOnlyTelegramContact,
  simpleOrderPlacement,
  orderData,
  onUpdateOrderData,
  orderErrors,
  promoCode,
  onPromoCodeChange,
  promoData,
  onPromoDataChange,
  promoMessage,
  onPromoMessageChange,
  isApplyingPromo,
  onApplyingPromoChange,
  onConfirmOrder,
  isSubmittingOrder,
  receiptHtml,
  receiptNotice,
  receiptSummary,
  deliveryCostState,
  orderFlowWarning,
  shouldScrollToOrderFlowWarning,
  unavailableCartItemNames,
  isOrderingBlocked,
  isReviewBlocked,
  isConfirmBlocked,
  orderingBlockedNoticeHtml,
  fulfilmentNotice,
  labels,
}) => {
  const pageTextsData = usePageTextsData();
  const menuTexts = EMPTY_MENU_TRANSLATIONS;
  const previousCheckoutStepRef = useRef<CheckoutStep | null>(null);
  const orderFlowWarningRef = useRef<HTMLDivElement | null>(null);
  const shouldShowLoyaltyCardNumber = !simpleOrderPlacement && isMenuLoyaltyCardNumberEnabled(partnerData);
  const shouldShowContactForm = simpleOrderPlacement
    || effectiveOrderChannel !== 'telegram'
    || shouldUsePhoneOnlyTelegramContact;
  const contactFormOrderData = (shouldForcePhoneContact || shouldUsePhoneOnlyTelegramContact) && orderData.contacts?.preferableMethod === 'telegram'
    ? {
      ...orderData,
      contacts: {
        ...orderData.contacts,
        preferableMethod: 'phone',
      },
    }
    : orderData;
  const deliveryZone = findDeliveryZone(partnerData, orderData, language, deliveryMapContext.effectiveCoordinates);
  const promoFeedbackText = promoMessage
    ? ((pageTextsData?.[promoMessage] && pageTextsData[promoMessage].trim().length > 0)
      ? pageTextsData[promoMessage]
      : promoMessage)
    : null;

  const resolveUnitPrice = (item: CartProductItem, menuItem?: MenuItemWithPrice): number => {
    if (menuItem) {
      return getCartItemEffectiveUnitPrice(item, menu, priceLevel);
    }
    return parseMenuPriceValue(item.total_cost);
  };

  /**
   * Separates the base product price from configured add-on rows so the cart
   * card can show a vertical, accounting-style price column.
   */
  const resolveBaseProductPrice = (item: CartProductItem, menuItem?: MenuItemWithPrice): number => {
    if (item.product_version && menuItem) {
      return getEffectiveVersionPrice(menuItem, priceLevel, item.product_version);
    }

    return menuItem ? getEffectiveItemPrice(menuItem, priceLevel) : parseMenuPriceValue(item.total_cost);
  };

  const getCartComponentRows = (product: CartProductItem) => {
    const componentKeys = [...(product.adds ?? []), ...(product.combo_product_list ?? [])];

    return componentKeys.map((componentKey) => {
      const componentItem = menu[componentKey];
      const label = resolveMenuText(
        menuTexts,
        componentItem?.name_code ?? componentKey,
        language,
        componentItem?.name_code ?? componentKey
      );
      const price = componentItem ? getEffectiveItemPrice(componentItem as MenuItemWithPrice, priceLevel) : 0;

      return {
        key: componentKey,
        label,
        price,
      };
    });
  };

  const patchOrderData = useCallback((patch: Partial<MenuOrderData>) => {
    onUpdateOrderData((previous) => ({ ...previous, ...patch }));
  }, [onUpdateOrderData]);

  /**
   * Partner data can change while a checkout draft remains in local storage.
   * Clear a previously entered number as soon as the strict feature flag is no
   * longer enabled, preventing hidden draft data from surviving in the UI.
   */
  useEffect(() => {
    if (!shouldShowLoyaltyCardNumber && orderData.loyalty_card_number != null) {
      patchOrderData({ loyalty_card_number: null });
    }
  }, [orderData.loyalty_card_number, patchOrderData, shouldShowLoyaltyCardNumber]);

  /**
   * The checkout step should always start with "Need change" set to "No",
   * regardless of a previous order draft or an earlier visit to this modal.
   * Resetting only on entry avoids fighting the user's current edits while they
   * remain on the same step.
   */
  useEffect(() => {
    if (step === 'checkout' && previousCheckoutStepRef.current !== 'checkout') {
      patchOrderData({ changeNeeded: 'No', changeAmount: null });
    }

    previousCheckoutStepRef.current = step;
  }, [patchOrderData, step]);

  useEffect(() => {
    if (!shouldScrollToOrderFlowWarning || !orderFlowWarning || step === 'receipt') {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      orderFlowWarningRef.current?.scrollIntoView({
        block: 'start',
        behavior: 'smooth',
      });
    }, 0);

    return () => window.clearTimeout(timeoutId);
  }, [
    orderFlowWarning,
    shouldScrollToOrderFlowWarning,
    step,
  ]);

  const renderProductExtras = (product: CartProductItem): string[] => {
    const lines: string[] = [];
    if (product.product_version) {
      const productVersionLabel = getProductVersionLabelFallback(menu[product.product_key], product.product_version);
      lines.push(resolveMenuText(menuTexts, product.product_version, language, productVersionLabel ?? product.product_version));
    }
    if (product.adds?.length) {
      lines.push(`${labels.adds}: ${product.adds.map((addKey) => resolveMenuText(menuTexts, menu[addKey]?.name_code ?? addKey, language, menu[addKey]?.name_code ?? addKey)).join(', ')}`);
    }
    if (product.combo_product_list?.length) {
      lines.push(`${labels.combo}: ${product.combo_product_list.map((comboKey) => resolveMenuText(menuTexts, menu[comboKey]?.name_code ?? comboKey, language, menu[comboKey]?.name_code ?? comboKey)).join(', ')}`);
    }
    return lines;
  };

  /**
   * The confirm step needs a human-readable snapshot of the collected checkout
   * fields so the customer can review logistics and payment details before the
   * final submit, not only prices.
   */
  const orderDetailRows = useMemo<Array<{ key: string; label: string; value: string }>>(() => {
    const rows: Array<{ key: string; label: string; value: string }> = [
      {
        key: 'deliveryNeeded',
        label: labels.howToGet,
        value: orderData.deliveryNeeded === 'Yes' ? labels.delivery : labels.selfPickup,
      },
    ];

    /**
     * Delivery and pickup use the same readiness-filtered schedule. ASAP keeps
     * the resolved fields internal and exposes only the customer-facing
     * "When ready" value; scheduled orders show their selected date and slot.
     */
    const appendFulfilmentScheduleRows = () => {
      if (
        simpleOrderPlacement
        || deliveryScheduleSelection.isAsapOnly
        || (
          deliveryScheduleSelection.isExactTimeAllowed
          && orderData.deliveryInTime !== 'Yes'
        )
      ) {
        rows.push({
          key: orderData.deliveryNeeded === 'Yes' ? 'asapDeliveryTime' : 'asapPickupTime',
          label: labels.deliveryTime,
          value: labels.whenReady,
        });
        return;
      }

      if (orderData.date) {
        rows.push({
          key: 'deliveryDate',
          label: labels.deliveryDay,
          value: orderData.date,
        });
      }
      if (orderData.deliveryInTime === 'Yes' && orderData.time) {
        rows.push({
          key: 'deliveryTime',
          label: labels.selectTime,
          value: orderData.time,
        });
      } else if (orderData.deliveryInterval) {
        rows.push({
          key: 'deliveryInterval',
          label: labels.deliveryTime,
          value: orderData.deliveryInterval,
        });
      } else if (orderData.date && orderData.deliveryInTime === 'No') {
        rows.push({
          key: 'deliveryTime',
          label: labels.deliveryTime,
          value: labels.whenReady,
        });
      }
    };

    if (orderData.deliveryNeeded === 'Yes') {
      rows.push({
        key: 'paymentMethod',
        label: labels.paymentMethod,
        value: orderData.paymentMethod === 'bank_card' ? labels.bankCard : labels.cash,
      });
      rows.push({
        key: 'changeNeeded',
        label: labels.needChange,
        value: orderData.changeNeeded === 'Yes' ? labels.yes : labels.no,
      });
      appendFulfilmentScheduleRows();

      const deliveryRows: Array<[string, string, string | null | undefined]> = [
        ['deliveryAddress', labels.address, orderData.deliveryAddress],
        ['entrance', labels.entrance, orderData.entrance],
        ['entranceCode', labels.code, orderData.entrance_code],
        ['floor', labels.floor, orderData.floor],
        ['apartment', labels.apartment, orderData.apartment],
        ['courierInstructions', labels.courierInstructions, orderData.delivery_instructions],
      ];

      deliveryRows.forEach(([key, label, value]) => {
        const normalizedValue = typeof value === 'string' ? value.trim() : '';
        if (normalizedValue.length > 0) {
          rows.push({ key, label, value: normalizedValue });
        }
      });
    } else {
      appendFulfilmentScheduleRows();
      const pickupAddress = toPlainText(partnerData?.pickup_address) || FALLBACK_PARTNER.address;
      if (pickupAddress) {
        rows.push({
          key: 'pickupAddress',
          label: labels.address,
          value: pickupAddress,
        });
      }
    }

    if (
      orderData.deliveryNeeded === 'Yes'
      && orderData.changeNeeded === 'Yes'
      && typeof orderData.changeAmount === 'number'
      && Number.isFinite(orderData.changeAmount)
    ) {
      rows.push({
        key: 'changeAmount',
        label: labels.changeAmount,
        value: formatEuro(orderData.changeAmount),
      });
    }

    const normalizedLoyaltyCardNumber = normalizeLoyaltyCardNumber(orderData.loyalty_card_number);
    if (shouldShowLoyaltyCardNumber && normalizedLoyaltyCardNumber) {
      rows.push({
        key: 'loyaltyCardNumber',
        label: labels.loyaltyCardNumber,
        value: normalizedLoyaltyCardNumber,
      });
    }

    if (orderData.comment?.trim()) {
      rows.push({
        key: 'comment',
        label: labels.additionalInstructions,
        value: orderData.comment.trim(),
      });
    }

    return rows;
  }, [
    labels.additionalInstructions,
    labels.address,
    labels.apartment,
    labels.bankCard,
    labels.cash,
    labels.changeAmount,
    labels.code,
    labels.courierInstructions,
    labels.delivery,
    labels.deliveryDay,
    labels.deliveryTime,
    labels.entrance,
    labels.floor,
    labels.howToGet,
    labels.loyaltyCardNumber,
    labels.needChange,
    labels.no,
    labels.paymentMethod,
    labels.selectTime,
    labels.selfPickup,
    labels.whenReady,
    labels.yes,
    orderData.apartment,
    orderData.changeAmount,
    orderData.changeNeeded,
    orderData.comment,
    orderData.date,
    orderData.deliveryAddress,
    orderData.deliveryInTime,
    orderData.deliveryInterval,
    orderData.deliveryNeeded,
    orderData.delivery_instructions,
    orderData.entrance,
    orderData.entrance_code,
    orderData.floor,
    orderData.loyalty_card_number,
    orderData.paymentMethod,
    orderData.time,
    partnerData?.pickup_address,
    deliveryScheduleSelection.isExactTimeAllowed,
    deliveryScheduleSelection.isAsapOnly,
    simpleOrderPlacement,
    shouldShowLoyaltyCardNumber,
  ]);

  /**
   * The review screen groups logistics, payment/change, and comments into
   * separate visual sections so the customer can scan the order snapshot in a
   * predictable order.
   */
  const orderDetailSections = useMemo(
    () => ([
      {
        key: 'delivery',
        rows: orderDetailRows.filter((row) => [
          'deliveryNeeded',
          'deliveryDate',
          'deliveryTime',
          'deliveryInterval',
          'asapDeliveryTime',
          'asapPickupTime',
          'pickupAddress',
          'deliveryAddress',
          'entrance',
          'entranceCode',
          'floor',
          'apartment',
          'courierInstructions',
        ].includes(row.key)),
      },
      {
        key: 'payment',
        rows: orderDetailRows.filter((row) => [
          'paymentMethod',
          'changeNeeded',
          'changeAmount',
        ].includes(row.key)),
      },
      {
        key: 'loyaltyCardNumber',
        rows: orderDetailRows.filter((row) => row.key === 'loyaltyCardNumber'),
      },
      {
        key: 'comment',
        rows: orderDetailRows.filter((row) => row.key === 'comment'),
      },
    ].filter((section) => section.rows.length > 0)),
    [orderDetailRows]
  );

  const renderDeliveryCostValue = () => {
    if (orderData.deliveryNeeded !== 'Yes') {
      return labels.selfPickup;
    }

    if (deliveryCostState.status === 'loading') {
      return <span className="d-inline-flex align-items-center gap-2 text-secondary"><Spinner animation="border" size="sm" /><span>{labels.calculatingDeliveryCost}</span></span>;
    }

    if (receiptSummary.deliveryCost < 0) {
      return labels.managerDefining;
    }

    return formatEuro(receiptSummary.deliveryCost);
  };

  const applyPromoCode = async () => {
    const foodMakerKey = partnerData?.food_maker_key ?? '';
    const normalizedCode = promoCode.trim();
    if (!normalizedCode || !foodMakerKey) {
      onPromoMessageChange(labels.enterPromo);
      return;
    }

    onApplyingPromoChange(true);
    onPromoMessageChange(null);

    try {
      const response = await fetch(
        `${backendUrl}/get_promo?code=${encodeURIComponent(normalizedCode)}&food_maker=${encodeURIComponent(foodMakerKey)}&lang=${encodeURIComponent(language)}`,
        {
          method: 'GET',
          headers: {
            'ngrok-skip-browser-warning': 'true',
            'Content-Type': 'application/json',
          },
        }
      );
      const payload = await response.json();

      if (payload?.is_valid) {
        onPromoDataChange(payload.promo as PromoData);
        onPromoMessageChange(payload.promo?.promo_name_code ?? labels.promoApplied);
        patchOrderData({ promo_code: normalizedCode });
      } else {
        onPromoDataChange(null);
        onPromoMessageChange(payload?.reason ?? labels.promoInvalid);
      }
    } catch {
      onPromoDataChange(null);
      onPromoMessageChange(labels.promoFailed);
    } finally {
      onApplyingPromoChange(false);
    }
  };

  return (
    <Modal show={isOpen} onHide={onClose} fullscreen="sm-down" centered scrollable contentClassName="border-0 rounded-4" data-testid="checkout-modal">
      <Modal.Header closeButton className="border-0">
        <Modal.Title>
          {step === 'cart' && labels.yourCart}
          {step === 'checkout' && labels.checkout}
          {step === 'confirm' && labels.reviewOrder}
          {step === 'receipt' && labels.receipt}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body data-testid="checkout-modal-body" className="pt-0" style={{ backgroundColor: '#F7F8F5' }}>
        {shoppingCart.length === 0 && step !== 'receipt' && (
          <Alert variant="light" className="mb-0">{labels.cartEmpty}</Alert>
        )}
        {fulfilmentNotice && shoppingCart.length > 0 && step !== 'receipt' && (
          <Alert variant="warning" className="mb-3" data-testid="cart-fulfilment-notice">
            {fulfilmentNotice}
          </Alert>
        )}
        {orderingBlockedNoticeHtml && step !== 'receipt' && (
          <Alert
            variant="warning"
            className={shoppingCart.length === 0 ? 'mt-3 mb-0' : 'mb-3'}
          >
            <div dangerouslySetInnerHTML={{ __html: orderingBlockedNoticeHtml }} />
          </Alert>
        )}
        {!isOrderingBlocked && orderFlowWarning && step !== 'receipt' && (
          <div ref={orderFlowWarningRef} data-testid="order-flow-warning">
            <Alert variant="warning" className={shoppingCart.length === 0 ? 'mt-3 mb-0' : 'mb-3'}>
              <div dangerouslySetInnerHTML={{ __html: orderFlowWarning }} />
            </Alert>
          </div>
        )}
        {unavailableCartItemNames.length > 0 && step !== 'receipt' && (
          <Alert variant="warning" className={shoppingCart.length === 0 ? 'mt-3 mb-0' : 'mb-3'} data-testid="cart-unavailable-warning">
            <div className="fw-bold mb-1">{labels.cartItemsUnavailableTitle}</div>
            <div className="small mb-2">{labels.cartItemsUnavailableBody}</div>
            <ul className="mb-0 ps-3">
              {unavailableCartItemNames.map((itemName) => (
                <li key={itemName}>{itemName}</li>
              ))}
            </ul>
          </Alert>
        )}

        {step === 'cart' && shoppingCart.length > 0 && (
          <div className="d-flex flex-column gap-3">
            {shoppingCart.map((product, index) => {
              const menuItem = menu[product.product_key];
              const imageFallbackUrl = partnerData?.logotype ? `${logoImagesPath}${partnerData.logotype}` : '/Assets/images/logo_ifood.png';
              const imageUrl = getItemImageUrl(menuItem, 'thumb') ?? imageFallbackUrl;
              const configuredUnitPrice = resolveUnitPrice(product, menuItem);
              const baseProductPrice = resolveBaseProductPrice(product, menuItem);
              const productName = resolveMenuText(menuTexts, menuItem?.name_code ?? product.product_key, language, menuItem?.name_code ?? product.product_key);
              const productVersionLabel = product.product_version
                ? resolveMenuText(
                  menuTexts,
                  product.product_version,
                  language,
                  getProductVersionLabelFallback(menuItem, product.product_version) ?? product.product_version
                )
                : null;
              const componentRows = getCartComponentRows(product);
              const priceColumnWidth = 'clamp(72px, 22vw, 96px)';
              const productImageSize = 'clamp(72px, 24vw, 92px)';

              return (
                <div key={`${product.product_key}-${index}`} data-testid="cart-item" className="bg-white rounded-4 p-3 shadow-sm">
                  <div className="d-flex gap-2 gap-sm-3">
                    <ProductImageWithFallback
                      src={imageUrl}
                      fallbackSrc={imageFallbackUrl}
                      alt={menuItem?.name_code ?? product.product_key}
                      className="rounded-4 flex-shrink-0"
                      style={{ width: productImageSize, height: productImageSize, objectFit: 'contain', backgroundColor: '#fff' }}
                      loading="lazy"
                      decoding="async"
                    />
                    <div className="flex-grow-1" style={{ minWidth: 0 }}>
                      <div>
                        <div className="d-flex align-items-start justify-content-between gap-3">
                          <div className="flex-grow-1" style={{ minWidth: 0 }}>
                            <div className="fw-bold">{productName}</div>
                            {productVersionLabel && (
                              <div className="text-secondary small mt-1">{productVersionLabel}</div>
                            )}
                          </div>
                          <div
                            className="text-end flex-shrink-0"
                            style={{ width: priceColumnWidth, color: '#0E1730', fontWeight: 700, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
                          >
                            {formatEuro(baseProductPrice)}
                          </div>
                        </div>
                        {componentRows.map((component) => (
                          <div key={component.key} className="d-flex align-items-start justify-content-between gap-3 mt-1">
                            <div className="flex-grow-1 text-secondary small" style={{ minWidth: 0 }}>{component.label}</div>
                            <div
                              className="text-end flex-shrink-0 text-secondary small"
                              style={{ width: priceColumnWidth, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
                            >
                              {component.price > 0 ? formatEuro(component.price) : ''}
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="d-flex align-items-end justify-content-between mt-3 gap-2 gap-sm-3">
                        <div className="d-flex align-items-center gap-2">
                          <div
                            className="d-inline-flex align-items-center rounded-pill px-2 py-1"
                            role="group"
                            aria-label={labels.quantity}
                            style={{ backgroundColor: '#F7F8F5', gap: '8px', paddingInline: '6px', paddingBlock: '4px' }}
                          >
                            <button
                              type="button"
                              className="btn btn-light rounded-circle d-flex align-items-center justify-content-center border-0"
                              style={{ width: '26px', height: '26px', color: '#1B7F3A', boxShadow: '0 0.12rem 0.35rem rgba(30, 30, 30, 0.06)', padding: 0 }}
                              onClick={() => {
                                const updatedCart = shoppingCart.reduce<ShoppingCart>((acc, item, itemIndex) => {
                                  if (itemIndex !== index) {
                                    acc.push(item);
                                    return acc;
                                  }
                                  const unitStep = menu[item.product_key]?.unit_step ?? 1;
                                  const minQuantity = menu[item.product_key]?.min_quantity ?? 1;
                                  const nextQuantity = item.quantity - unitStep;
                                  if (nextQuantity < minQuantity) {
                                    return acc;
                                  }
                                  acc.push({ ...item, quantity: nextQuantity });
                                  return acc;
                                }, []);
                                onUpdateCart(updatedCart);
                              }}
                            >
                              <FaMinus />
                            </button>
                            <span style={{ minWidth: '14px', fontSize: '13px', fontWeight: 700, color: '#0E1730', textAlign: 'center' }}>
                              {product.quantity}
                            </span>
                            <button
                              type="button"
                              className="btn btn-success rounded-circle d-flex align-items-center justify-content-center border-0"
                              style={{ width: '26px', height: '26px', boxShadow: '0 0.2rem 0.55rem rgba(27, 127, 58, 0.18)', padding: 0 }}
                              disabled={isOrderingBlocked}
                              onClick={() => {
                                const updatedCart = shoppingCart.map((item, itemIndex) => (
                                  itemIndex === index
                                    ? { ...item, quantity: item.quantity + (menu[item.product_key]?.unit_step ?? 1) }
                                    : item
                                ));
                                onUpdateCart(updatedCart);
                              }}
                            >
                              <FaPlus />
                            </button>
                          </div>
                          <button
                            type="button"
                            className="btn btn-link text-secondary d-flex align-items-center justify-content-center p-0 flex-shrink-0"
                            style={{ width: '22px', height: '22px', color: '#94A3B8' }}
                            onClick={() => onUpdateCart(shoppingCart.filter((_, itemIndex) => itemIndex !== index))}
                            aria-label={labels.remove}
                          >
                            <FaTrash size={18} />
                          </button>
                        </div>
                        <div
                          className="text-end flex-shrink-0"
                          style={{ width: priceColumnWidth, fontVariantNumeric: 'tabular-nums', lineHeight: 1.2, whiteSpace: 'nowrap' }}
                        >
                          <div className="text-secondary small mb-1">
                            × {formatEuro(configuredUnitPrice)}
                          </div>
                          <div className="text-success fw-bold">
                            {formatEuro(configuredUnitPrice * product.quantity)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            <div className="d-flex flex-column gap-3 mt-1">
              <div className="d-flex align-items-stretch gap-2 gap-sm-3">
                <div
                  className="bg-white rounded-pill d-flex align-items-center gap-3 px-3 px-sm-4 flex-grow-1"
                  style={{
                    minHeight: '62px',
                    borderRadius: '22px',
                    border: '1px solid #EEF2F6',
                    boxShadow: '0 0.2rem 0.7rem rgba(30, 30, 30, 0.04)',
                  }}
                >
                  <FaTag style={{ color: '#94A3B8', fontSize: '18px', flexShrink: 0 }} />
                  <input
                    type="text"
                    className="form-control border-0 bg-transparent shadow-none px-0"
                    value={promoCode}
                    placeholder={labels.promoPlaceholder}
                    onChange={(event) => onPromoCodeChange(event.target.value)}
                    style={{ fontSize: '16px', color: '#64748B' }}
                  />
                </div>
                <Button
                  type="button"
                  className="border-0 rounded-pill px-3 px-sm-4 flex-shrink-0"
                  style={{
                    minWidth: '112px',
                    minHeight: '62px',
                    borderRadius: '22px',
                    backgroundColor: '#DDEBDF',
                    color: '#1B7F3A',
                    fontSize: '16px',
                    fontWeight: 700,
                    boxShadow: '0 0.2rem 0.7rem rgba(30, 30, 30, 0.04)',
                  }}
                  onClick={applyPromoCode}
                  disabled={isApplyingPromo}
                >
                  {isApplyingPromo ? labels.applying : labels.apply}
                </Button>
              </div>
              <div
                className="bg-white"
                style={{
                  borderRadius: '24px',
                  border: '1px solid #EEF2F6',
                  boxShadow: '0 0.25rem 0.85rem rgba(30, 30, 30, 0.05)',
                  padding: promoFeedbackText ? '18px 20px' : '22px 20px',
                }}
              >
                {promoFeedbackText && (
                  <div
                    className="d-flex align-items-start justify-content-between gap-3 mb-3"
                    style={{ fontSize: '16px', color: '#64748B' }}
                  >
                    <span className="flex-grow-1" style={{ lineHeight: 1.35, minWidth: 0 }}>
                      {promoFeedbackText}
                    </span>
                    {promoData && (
                      <span style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                        {promoData.promo_type === 'x_%_discount'
                          ? `-${promoData.promo_amount}%`
                          : `-${formatEuro(promoData.promo_amount)}`}
                      </span>
                    )}
                  </div>
                )}
                <div
                  data-testid="cart-total"
                  className="d-flex align-items-center justify-content-between"
                  style={promoFeedbackText ? { borderTop: '1px dashed #E2E8F0', paddingTop: '14px' } : undefined}
                >
                  <span style={{ fontSize: '20px', fontWeight: 800, color: '#0E1730' }}>{labels.total}</span>
                  <span className="text-success" style={{ fontSize: '21px', fontWeight: 800, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                    {formatEuro(receiptSummary.total)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {step === 'checkout' && shoppingCart.length > 0 && (
          <>
            {shouldShowContactForm && (
              <ContactForm
                orderData={contactFormOrderData}
                onUpdateOrderData={patchOrderData}
                orderErrors={orderErrors}
                phoneOnly={simpleOrderPlacement ? false : shouldUsePhoneOnlyTelegramContact}
                nameOnly={simpleOrderPlacement}
                texts={{
                  name: labels.yourName,
                  preferredMethod: labels.preferredMethod,
                  countryCode: labels.selectCountryCode,
                  phone: labels.phoneNumber,
                  phoneMethod: labels.phoneMethod,
                  telegram: labels.telegramUsername,
                  countryCodeOptions: labels.countryCodeOptions,
                }}
              />
            )}
            {!simpleOrderPlacement && (
              <DeliveryBlock
                partnerName={partnerData?.name_code ?? ''}
                isCashlessTerminalAvailable={isCashlessTerminalAvailable}
                isDeliveryUnavailableNow={isDeliveryUnavailableNow}
                deliveryScheduleSelection={deliveryScheduleSelection}
                mapInitialCenter={deliveryMapContext.initialCenter}
                selectedDeliveryPosition={deliveryMapContext.selectedPosition}
                deliverySelectionLocation={deliveryMapContext.partnerLocation}
                orderData={orderData}
                onUpdateOrderData={patchOrderData}
                orderErrors={orderErrors}
                labels={{
                howToGet: labels.howToGet,
                selfPickup: labels.selfPickup,
                delivery: labels.delivery,
                deliveryPoint: labels.deliveryPoint,
                tapToChoose: labels.tapToChoose,
                selectDeliveryPoint: labels.selectDeliveryPoint,
                expandMap: labels.expandMap,
                collapseMap: labels.collapseMap,
                address: labels.address,
                entrance: labels.entrance,
                code: labels.code,
                floor: labels.floor,
                apartment: labels.apartment,
                paymentMethod: labels.paymentMethod,
                cash: labels.cash,
                bankCard: labels.bankCard,
                needChange: labels.needChange,
                yes: labels.yes,
                no: labels.no,
                changeAmount: labels.changeAmount,
                deliveryDay: labels.deliveryDay,
                deliveryInterval: labels.deliveryInterval,
                pickupDay: labels.pickupDay,
                pickupInterval: labels.pickupInterval,
                deliveryTime: labels.deliveryTime,
                whenReady: labels.whenReady,
                atTheTime: labels.atTheTime,
                selectTime: labels.selectTime,
                noDeliveryTimesAvailable: labels.noDeliveryTimesAvailable,
                noPickupIntervalsAvailable: labels.noPickupIntervalsAvailable,
                prepareChangeAmount: labels.prepareChangeAmount,
                mapLoading: labels.mapLoading,
                mapLoadFailed: labels.mapLoadFailed,
                mapUnavailableDescription: labels.mapUnavailableDescription,
                retry: labels.retry,
                reset: labels.reset,
                deliveryInstructionsPlaceholder: labels.deliveryInstructionsPlaceholder,
                }}
              />
            )}
            {shouldShowLoyaltyCardNumber && (
              <LoyaltyCardNumberField
                value={orderData.loyalty_card_number ?? ''}
                onChange={(loyaltyCardNumber) => patchOrderData({
                  loyalty_card_number: loyaltyCardNumber || null,
                })}
                label={labels.loyaltyCardNumber}
                placeholder={labels.enterLoyaltyCardNumber}
              />
            )}
            <CommentsField
              comment={orderData.comment ?? ''}
              onChange={(comment) => patchOrderData({ comment })}
              title={labels.additionalInstructions}
              placeholder={labels.anythingElse}
            />
          </>
        )}

        {step === 'confirm' && shoppingCart.length > 0 && (
          <div data-testid="confirm-order-summary" className="d-flex flex-column gap-3">
            <div className="bg-white rounded-4 p-3 shadow-sm">
              {shoppingCart.map((product, index) => (
                <div key={`${product.product_key}-${index}`} className={index === shoppingCart.length - 1 ? '' : 'border-bottom pb-3 mb-3'}>
                  <div className="d-flex justify-content-between gap-3">
                    <div className="flex-grow-1" style={{ minWidth: 0 }}>
                      <div className="fw-bold">{resolveMenuText(menuTexts, menu[product.product_key]?.name_code ?? product.product_key, language, menu[product.product_key]?.name_code ?? product.product_key)}</div>
                      <div className="small text-secondary">{labels.quantity}: {product.quantity}</div>
                      {renderProductExtras(product).map((line) => <div key={line} className="small text-secondary">{line}</div>)}
                    </div>
                    <div
                      data-testid="confirm-product-price"
                      className="fw-semibold text-success text-end flex-shrink-0"
                      style={{ minWidth: '5rem', whiteSpace: 'nowrap' }}
                    >
                      {formatEuro(product.total_cost * product.quantity)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="bg-white rounded-4 p-3 shadow-sm">
              <div className="d-flex justify-content-between mb-2"><span>{labels.subtotal}</span><span>{formatEuro(receiptSummary.subtotal)}</span></div>
              <div className="d-flex justify-content-between mb-2">
                <span>{labels.deliveryCost}</span>
                <span>{renderDeliveryCostValue()}</span>
              </div>
              {orderData.deliveryNeeded === 'Yes' && receiptSummary.deliveryCost < 0 && (
                <Alert variant="warning" className="py-2 px-3 mb-2 small" style={{ whiteSpace: 'pre-line' }}>
                  {labels.deliveryCostMissingData}
                </Alert>
              )}
              {deliveryCostState.status === 'error' && orderData.deliveryNeeded === 'Yes' && deliveryZone && (
                <Alert variant="warning" className="py-2 px-3 mb-2 small">
                  {deliveryCostState.message ?? labels.deliveryCostFailed}
                </Alert>
              )}
              {deliveryZone && deliveryCostState.status !== 'success' && <div className="small text-secondary mb-2">{labels.deliveryZone}: {deliveryZone.name}</div>}
              <div className="d-flex justify-content-between fw-bold fs-5 text-success border-top pt-2"><span>{labels.total}</span><span>{formatEuro(receiptSummary.total)}</span></div>
            </div>
            <div className="bg-white rounded-4 p-3 shadow-sm">
              <div className="fw-bold mb-3">{labels.orderDetails}</div>
              {orderDetailSections.map((section, index) => (
                <div key={section.key}>
                  {index > 0 && <div className="border-top mt-3 pt-3" style={{ borderColor: '#E5E7EB' }} />}
                  <div className="d-flex flex-column gap-2">
                    {section.rows.map((row) => (
                      <div key={row.key} className="d-flex justify-content-between align-items-start gap-3">
                        <span className="text-secondary small">{row.label}</span>
                        <span className="text-end small" style={{ color: '#0E1730', whiteSpace: 'pre-line' }}>{row.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {step === 'receipt' && (
          <>
            {receiptNotice && (
              <Alert
                data-testid="receipt-status-message"
                variant={receiptNotice.type === 'success' ? 'success' : 'danger'}
                className="d-flex align-items-center flex-nowrap gap-2 mb-3"
              >
                <span
                  data-testid="receipt-status-icon"
                  aria-hidden="true"
                  className={receiptNotice.type === 'success' ? 'text-success flex-shrink-0' : 'text-danger flex-shrink-0'}
                >
                  {receiptNotice.type === 'success' ? <FaCheck /> : <FaExclamationTriangle />}
                </span>
                <span>{receiptNotice.message}</span>
              </Alert>
            )}
            {receiptNotice?.type === 'error' && receiptNotice.supportHref && (
              <Button
                as="a"
                data-testid="receipt-support-button"
                href={receiptNotice.supportHref}
                target="_blank"
                rel="noopener noreferrer"
                variant="success"
                className="w-100 rounded-pill py-2 fw-bold d-inline-flex align-items-center justify-content-center gap-2 mb-3"
              >
                <FaTelegramPlane aria-hidden="true" />
                <span>{labels.connectWithSupport}</span>
              </Button>
            )}
            {receiptHtml && (
              <div className="bg-white rounded-4 p-3 shadow-sm">
                <div data-testid="receipt-content" dangerouslySetInnerHTML={{ __html: receiptHtml }} />
              </div>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer className="border-0 pt-0">
        {step === 'cart' && (
          <div className="d-flex w-100 gap-2">
            <Button variant="light" className={`${shoppingCart.length > 0 ? 'w-50' : 'w-100'} rounded-pill py-2 fw-semibold`} onClick={onClose}>
              <span className="d-inline-flex align-items-center gap-2"><FaArrowLeft /><span>{labels.back}</span></span>
            </Button>
            {shoppingCart.length > 0 && (
              <Button
                data-testid="checkout-proceed-button"
                variant="success"
                className="w-50 rounded-pill py-2 fw-bold"
                disabled={isOrderingBlocked}
                onClick={() => {
                  if (!isOrderingBlocked) {
                    onStepChange('checkout');
                  }
                }}
              >
                {labels.proceedToCheckout}
              </Button>
            )}
          </div>
        )}
        {step === 'checkout' && shoppingCart.length > 0 && (
          <div className="d-flex w-100 gap-2 align-items-stretch">
            <Button variant="light" className="w-50 rounded-pill py-2 fw-semibold" onClick={() => onStepChange('cart')}>
              <span className="d-inline-flex align-items-center gap-2"><FaArrowLeft /><span>{labels.back}</span></span>
            </Button>
            <Button
              data-testid="checkout-review-button"
              variant="success"
              className="w-50 rounded-pill py-2 fw-bold"
              disabled={isReviewBlocked}
              onClick={() => {
                if (!isReviewBlocked) {
                  onStepChange('confirm');
                }
              }}
            >
              {labels.reviewOrder}
            </Button>
          </div>
        )}
        {step === 'confirm' && shoppingCart.length > 0 && (
          <div className="d-flex w-100 gap-2">
            <Button variant="light" className="w-50 rounded-pill py-2 fw-semibold" onClick={() => onStepChange('checkout')}>
              <span className="d-inline-flex align-items-center gap-2"><FaArrowLeft /><span>{labels.back}</span></span>
            </Button>
            <Button data-testid="confirm-order-button" variant="success" className="w-50 rounded-pill py-2 fw-bold" onClick={onConfirmOrder} disabled={isReviewBlocked || isConfirmBlocked || isSubmittingOrder || deliveryCostState.status === 'loading'}>
              {isSubmittingOrder ? <span className="d-inline-flex align-items-center gap-2"><Spinner animation="border" size="sm" /><span>{labels.confirm}</span></span> : labels.confirm}
            </Button>
          </div>
        )}
        {step === 'receipt' && (
          <div className="w-100">
            <Button data-testid="receipt-ok-button" variant="success" className="w-100 rounded-pill py-2 fw-bold" onClick={onClose}>OK</Button>
          </div>
        )}
      </Modal.Footer>
    </Modal>
  );
};

/**
 * MenuContent
 *
 * Renders the full menu page using app-level partner/menu/session data plus
 * translated page texts from PageTextsDataContext.
 * Handles view state, category/filter/search interactions, language switching,
 * and derived partner/product presentation logic.
 */
const MenuContent: React.FC = () => {
  const sessionData = useSessionData();
  const userData = useUserData();
  const auth = useAuthData();
  const partnerData = usePartnerData();
  const refreshPartnerData = usePartnerDataRefresh();
  const pageTextsData = usePageTextsData();

  const language = sessionData?.lang ?? 'en';
  const [searchValue, setSearchValue] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<string>('All items');
  const [viewMode, setViewMode] = useState<ProductCardViewMode>(() => getResponsiveDefaultProductView());
  const [isCategoryPanelOpen, setIsCategoryPanelOpen] = useState<boolean>(false);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState<boolean>(false);
  const [selectedFilters, setSelectedFilters] = useState<Partial<Record<FilterAttributeKey, string>>>({});
  const [shoppingCart, setShoppingCart] = useState<ShoppingCart>([]);
  const [isCartOpen, setIsCartOpen] = useState<boolean>(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState<boolean>(false);
  const [checkoutStep, setCheckoutStep] = useState<CheckoutStep>('cart');
  const [optionsItem, setOptionsItem] = useState<MenuItemWithKey | null>(null);
  const [isOptionsOpen, setIsOptionsOpen] = useState<boolean>(false);
  const menuStickyToolbarRef = useRef<HTMLDivElement | null>(null);
  const hasAppliedInitialCategoryHashRef = useRef<string | null>(null);
  const [orderData, setOrderData] = useState<MenuOrderData>({ deliveryNeeded: 'Yes' });
  const [orderErrors, setOrderErrors] = useState<OrderFormErrors>({
    name: false,
    countryCode: false,
    phone: false,
    telegramName: false,
    deliveryPoint: false,
    deliveryAddress: false,
    deliveryDate: false,
    deliveryTime: false,
    deliveryInterval: false,
    changeAmount: false,
  });
  const [promoCode, setPromoCode] = useState<string>('');
  const [promoData, setPromoData] = useState<PromoData | null>(null);
  const [promoMessage, setPromoMessage] = useState<string | null>(null);
  const [isApplyingPromo, setIsApplyingPromo] = useState<boolean>(false);
  const [isSubmittingOrder, setIsSubmittingOrder] = useState<boolean>(false);
  const [receiptHtml, setReceiptHtml] = useState<string>('');
  const [receiptNotice, setReceiptNotice] = useState<ReceiptNotice | null>(null);
  const [orderStatus, setOrderStatus] = useState<string | null>(null);
  const [fulfilmentAdjustmentNotice, setFulfilmentAdjustmentNotice] = useState<string | null>(null);
  const [scheduleNow, setScheduleNow] = useState<Date>(() => new Date());
  const [unavailableCartItemNames, setUnavailableCartItemNames] = useState<string[]>([]);
  const [isClosedHoursModalOpen, setIsClosedHoursModalOpen] = useState<boolean>(false);
  const [isOrdersInfoModalOpen, setIsOrdersInfoModalOpen] = useState<boolean>(false);
  const [isGalleryOpen, setIsGalleryOpen] = useState<boolean>(false);
  const [productImagePreview, setProductImagePreview] = useState<ImagePreviewState | null>(null);
  const [deliveryCostState, setDeliveryCostState] = useState<DeliveryCostState>({
    status: 'idle',
    cost: null,
    minimumOrderAmount: 0,
    message: null,
  });
  const appliedAuthProfileKeyRef = useRef<string>('');
  const appliedDefaultProductViewKeyRef = useRef<string>('');
  const shownClosedHoursWarningPartnerKeyRef = useRef<string>('');
  const isSubmittingOrderRef = useRef<boolean>(false);

  const t = useCallback((key: string, fallback: string): string => {
    const value = pageTextsData?.[key];
    return value && value.trim().length > 0 ? value : fallback;
  }, [pageTextsData]);

  /**
   * Product delivery previews and checkout eligibility are time-sensitive. A
   * minute clock keeps both views current without creating per-card timers.
   */
  useEffect(() => {
    const intervalId = window.setInterval(() => setScheduleNow(new Date()), 60_000);
    return () => window.clearInterval(intervalId);
  }, []);
  const resolveTranslatedOrPlainHtml = useCallback((value: string | null | undefined): string => {
    if (!value || value.trim().length === 0) {
      return '';
    }

    const trimmedValue = value.trim();
    const translatedValue = pageTextsData?.[trimmedValue];
    if (translatedValue && translatedValue.trim().length > 0) {
      return translatedValue.trim();
    }

    const looksLikeCode = /^[A-Za-z0-9_]+$/.test(trimmedValue);
    return looksLikeCode ? '' : trimmedValue;
  }, [pageTextsData]);
  const authenticatedProfile = auth?.authStatus === 'authenticated' ? (auth.authUser ?? userData) : null;
  const partnerLogoFallbackUrl = partnerData?.logotype ? `${logoImagesPath}${partnerData.logotype}` : '/Assets/images/logo_ifood.png';

  /**
   * Normalizes the supplier type coming from partner data so the page can
   * switch shared labels between food makers and shops without duplicating UI.
   */
  const partnerSupplierType = useMemo<'FoodMaker' | 'Shop' | 'Delivery' | null>(() => {
    const rawSupplierType = (partnerData as (PartnerDataType & { supplier_type?: string | null }) | null)?.h_supplier_type
      ?? (partnerData as (PartnerDataType & { supplier_type?: string | null }) | null)?.supplier_type
      ?? null;

    return rawSupplierType === 'FoodMaker' || rawSupplierType === 'Shop' || rawSupplierType === 'Delivery'
      ? rawSupplierType
      : null;
  }, [partnerData]);

  const menuSectionTitle = partnerSupplierType === 'Shop'
    ? t('catalog_title', 'Catalog')
    : t('menu_title', 'Menu');

  const searchPlaceholder = partnerSupplierType === 'Shop'
    ? t('search_placeholder_for_shop', 'Search in catalog...')
    : t('search_placeholder_for_FoodMaker', 'Search for dishes...');

  const promoSlides = useMemo<PromoSlideData[]>(() => ([
    {
      title: t('promo_1_title', 'Veggie Power'),
      subtitle: t('promo_1_subtitle', 'Get 20% off on all salads today'),
      buttonText: t('promo_1_button', 'Claim deal'),
      imageUrl:
        'https://lh3.googleusercontent.com/aida-public/AB6AXuACSfE8tAvDX7WWDVEnvzeovQiWyFW66fE6rzbS2bI1-2eBnWmQQRRuurmw8I-B14XzWuX8lIj7gZEHf0PUvTtpB1RRCilh4GkEA-O9YLdKObvOY_Zy7o4ze2BD-Q0V0MXZcGFMDX-Upzj5oY4wgglsJQcz0Bs8Es7T0A_EiEH93H1M-KTRCIeyRTBNNoUTgL3o-HNdV3PltnwNxugnwe4qvv766fwAyRh0GJO3IERze9w7dyGAMgk5wEsIhPMYUnLVV0opGdPuAMyF',
    },
    {
      title: t('promo_2_title', 'Morning Brew'),
      subtitle: t('promo_2_subtitle', 'Buy 1 Coffee get 1 half price'),
      buttonText: t('promo_2_button', 'Learn more'),
      imageUrl:
        'https://lh3.googleusercontent.com/aida-public/AB6AXuD-3I2qbfgSXgJALlDcgGpLE5Op2t_lvtyCcBGQL6dsZ1TT_yRY2XECgtfTYoHK3lF5kwmMZ_DBJ86CJGsG6RRdqN52G1Ov0bs2b9DEn6w49sY8hwBnvWZdDUyq6wMuqLLEQAjtVfHsY_gOwU8nG54Pi7h6EMJwxUYuW-Ju8M1pdxbtZZ5f4-syuL9dIgNZLg1MA--ic3mptIBAkUChCvbYXWMHTIHLl48xn0l_-IghDw3Xs74YGSMwrAk9XfxEK8RTZKeZdGeZNJ5Q',
    },
  ]), [t]);

  const menuTranslations = EMPTY_MENU_TRANSLATIONS;
  const menu = useMemo<FoodMakerMenu>(() => partnerData?.menu ?? {}, [partnerData?.menu]);
  const backendDefaultProductView = resolveMenuDefaultProductView(partnerData);
  const shouldShowProductParameters = resolveDoProductParametersShow(partnerData);
  const activePriceLevel = getFirstDefinedPriceLevel(
    authenticatedProfile?.price_level,
    orderData.price_level,
    sessionData?.price_level,
    userData?.price_level,
  );
  const isSpecialPriceLevelUser = hasSpecialPriceLevel(activePriceLevel);
  const shouldShowFullStock = shouldShowFullStockOnMenu(window.location.hostname, isSpecialPriceLevelUser);
  // Temporary workaround for whs.bottle-shop.me. Remove this flag and all
  // dependent simplified-checkout logic when user authentication is implemented.
  const simpleOrderPlacement = isSimpleOrderPlacementHost(window.location.hostname);
  const simpleOrderPlacementPartnerDefaults = useMemo(() => (
    partnerData
      ? {
          pickupAddress: toPlainText(partnerData.pickup_address),
          pickupPoint: getPartnerCoordinates(partnerData),
          location: partnerData.location ?? '',
        }
      : null
  ), [partnerData]);
  const effectiveOrderChannel = resolveEffectiveOrderChannel(orderData, sessionData, userData);
  const shouldForcePhoneContact = shouldUseWebPhoneContactForMissingTelegramId(orderData, sessionData, userData);
  const shouldUsePhoneOnlyTelegramContact = shouldUsePhoneOnlyContactForTelegramWithoutUsername(
    orderData,
    sessionData,
    userData,
  );
  const activeDeliveryScheduleResolution = useMemo(
    () => resolveLocationSchedule(
      partnerData?.delivery_schedule,
      [orderData.location, sessionData?.location, userData?.location],
      partnerData?.location,
    ),
    [
      orderData.location,
      partnerData?.delivery_schedule,
      partnerData?.location,
      sessionData?.location,
      userData?.location,
    ],
  );
  const activeDeliveryScheduleLocation = activeDeliveryScheduleResolution?.locationCode ?? '';
  const activeDeliveryScheduleEntry = activeDeliveryScheduleResolution?.entry;

  /**
   * Builds customer-facing product cards from the partner menu endpoint only.
   * The `/menu` payload already carries the strings that the public menu should
   * render, while `/get_full_menu` remains reserved for editing workflows.
   */
  const menuCards = useMemo<MenuCardData[]>(() => {
    const translations = menuTranslations;

    const dynamicCards = Object.entries(menu)
      .filter(([, item]) => item.is_active && !item.is_add && getEffectiveItemPrice(item, activePriceLevel) > 0)
      .sort(([, left], [, right]) => left.display_order - right.display_order)
      .map(([productKey, item]) => {
        const sourceItem = item as MenuItem & Partial<Record<string, string | null>>;
        const name = resolveMenuText(translations, item.name_code, language, item.name_code);
        const description = item.description_code ?? '';
        const categoryLabel = item.category_code;
        const unitLabel = item.unit_code ?? '';
        const lowStockQuantity = getVisibleStockQuantity(item.remaining_stock, shouldShowFullStock);
        const effectivePrice = getEffectiveItemPrice(item, activePriceLevel);
        const productConditions = getProductDeliveryConditions(item.delivery_conditions);
        const nextDeliveryResolution = productConditions
          ? resolveFulfilmentSchedule(
              activeDeliveryScheduleEntry?.schedule,
              getProductFulfilmentConstraints(productConditions),
              'delivery',
              activeDeliveryScheduleEntry?.average_delivery_time,
              scheduleNow,
              activeDeliveryScheduleEntry?.is_deliver_on_time === true,
            )
          : null;
        const nextDeliveryInterval = nextDeliveryResolution?.firstAllowedInterval ?? null;
        const nextDeliveryText = productConditions
          ? nextDeliveryInterval
            ? `${t('next_delivery_interval', 'Next delivery')}: ${formatDeliveryDayOptionLabel(nextDeliveryInterval.start, language, t, scheduleNow)}, ${nextDeliveryInterval.label}`
            : t('product_delivery_interval_unavailable', 'No delivery interval is currently available.')
          : null;
        const additionalProperties = MENU_CARD_PROPERTIES.flatMap((propertyKey) => {
          const rawValue = sourceItem[propertyKey];
          if (typeof rawValue !== 'string' || rawValue.trim().length === 0) {
            return [];
          }

          return rawValue.trim().length > 0 ? [rawValue] : [];
        });

        return {
          productKey,
          name,
          description,
          descriptionPlain: toSearchableText(description),
          categoryLabel,
          price: toDisplayPrice(item, activePriceLevel),
          priceValue: effectivePrice,
          hasMultipleVersions: hasProductVersions(item),
          unitLabel,
          displayImageUrl: getItemImageUrl(item, 'card') ?? partnerLogoFallbackUrl,
          fullImageUrl: getItemImageUrl(item, 'full') ?? partnerLogoFallbackUrl,
          additionalProperties,
          lowStockQuantity,
          isPurchasable: isMenuItemPurchasable(item, activePriceLevel),
          nextDeliveryText,
          isNextDeliveryUnavailable: Boolean(productConditions && !nextDeliveryInterval),
        };
      });

    if (dynamicCards.length === 0) {
      const hasRealMenuItems = Object.values(menu).some((item) => item.is_active && !item.is_add);
      return hasRealMenuItems ? [] : FALLBACK_MENU_CARDS;
    }
    return dynamicCards;
  }, [
    activeDeliveryScheduleEntry?.average_delivery_time,
    activeDeliveryScheduleEntry?.is_deliver_on_time,
    activeDeliveryScheduleEntry?.schedule,
    activePriceLevel,
    language,
    menu,
    menuTranslations,
    partnerLogoFallbackUrl,
    scheduleNow,
    shouldShowFullStock,
    t,
  ]);

  const activeMenuItems = useMemo<MenuItem[]>(() => {
    return Object.values(menu).filter((item) => (
      item.is_active && !item.is_add && getEffectiveItemPrice(item, activePriceLevel) > 0
    ));
  }, [activePriceLevel, menu]);

  /**
   * Creates a cascading filter schema.
   * Once one filter is chosen, option pools for the other filters are recalculated.
   */
  const availableFilterAttributes = useMemo(() => {
    const translations = menuTranslations;

    return FILTER_ATTRIBUTE_KEYS
      .map((attributeKey) => {
        const poolForAttribute = activeMenuItems.filter((item) =>
          FILTER_ATTRIBUTE_KEYS.every((anotherKey) => {
            if (anotherKey === attributeKey) {
              return true;
            }
            const selectedValue = selectedFilters[anotherKey];
            if (!selectedValue) {
              return true;
            }
            const itemValue = (item as unknown as Record<string, unknown>)[anotherKey];
            return typeof itemValue === 'string' && itemValue === selectedValue;
          })
        );

        const uniqueValues = Array.from(
          new Set(
            poolForAttribute
              .map((item) => (item as unknown as Record<string, unknown>)[attributeKey])
              .filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
          )
        );

        if (uniqueValues.length === 0) {
          return null;
        }

        return {
          key: attributeKey,
          label: t(`filter_label_${attributeKey}`, FILTER_ATTRIBUTE_LABELS[attributeKey]),
          options: uniqueValues
            .map((codeValue) => ({
              value: codeValue,
              label: resolveMenuText(translations, codeValue, language, codeValue),
            }))
            .sort((left, right) => left.label.localeCompare(right.label)),
        };
      })
      .filter((entry): entry is { key: FilterAttributeKey; label: string; options: Array<{ value: string; label: string }> } => entry !== null);
  }, [activeMenuItems, language, menuTranslations, selectedFilters, t]);

  useEffect(() => {
    setSelectedFilters((previous) => {
      let wasChanged = false;
      const next: Partial<Record<FilterAttributeKey, string>> = { ...previous };

      Object.keys(previous).forEach((rawKey) => {
        const key = rawKey as FilterAttributeKey;
        const selectedValue = previous[key];
        if (!selectedValue) {
          return;
        }

        const filterEntry = availableFilterAttributes.find((attribute) => attribute.key === key);
        const hasSelectedValue = Boolean(filterEntry?.options.some((option) => option.value === selectedValue));

        if (!hasSelectedValue) {
          delete next[key];
          wasChanged = true;
        }
      });

      return wasChanged ? next : previous;
    });
  }, [availableFilterAttributes]);
  const categoryTabs = useMemo<string[]>(() => {
    const tabs = Array.from(new Set(menuCards.map((card) => card.categoryLabel).filter(Boolean)));
    if (tabs.length === 0) {
      return [
        t('fallback_breakfast', 'Breakfast'),
        t('fallback_main_dishes', 'Main dishes'),
        t('fallback_desserts', 'Desserts'),
        t('fallback_drinks', 'Drinks'),
      ];
    }
    return tabs;
  }, [menuCards, t]);

  const categoryAnchorIds = useMemo(
    () => buildCategoryAnchorIds(categoryTabs),
    [categoryTabs]
  );

  const categoryByAnchorId = useMemo(
    () => new Map(Object.entries(categoryAnchorIds).map(([categoryLabel, anchorId]) => [anchorId, categoryLabel])),
    [categoryAnchorIds]
  );

  const categoryPanelItems = useMemo<CategoryPanelItem[]>(() => {
    const translations = menuTranslations;
    const dynamicItems = Object.entries(menu)
      .map(([productKey, item]) => ({ productKey, item }))
      .filter(({ item }) => (
        item.is_active
        && !item.is_add
        && getEffectiveItemPrice(item, activePriceLevel) > 0
      ));

    if (dynamicItems.length > 0) {
      const grouped = new Map<string, Array<{ productKey: string; item: MenuItem }>>();
      dynamicItems.forEach((entry) => {
        const bucket = grouped.get(entry.item.category_code) ?? [];
        bucket.push(entry);
        grouped.set(entry.item.category_code, bucket);
      });

      const groupedPanelItems = Array.from(grouped.entries()).map(([categoryCode, items]) => {
        const sortedItems = [...items].sort((left, right) => left.item.display_order - right.item.display_order);
        const firstImageItem = sortedItems.find(({ item }) => Boolean(getItemImageUrl(item, 'thumb')));
        const imageUrl = firstImageItem ? (getItemImageUrl(firstImageItem.item, 'thumb') as string) : partnerLogoFallbackUrl;
        return {
          key: categoryCode,
          label: resolveMenuText(translations, categoryCode, language, categoryCode),
          count: sortedItems.length,
          displayImageUrl: imageUrl,
        };
      });
      const groupedPanelItemMap = new Map(groupedPanelItems.map((item) => [item.label, item]));
      const orderedPanelItems = categoryTabs
        .map((categoryLabel) => groupedPanelItemMap.get(categoryLabel))
        .filter((item): item is CategoryPanelItem => Boolean(item));
      const remainingPanelItems = groupedPanelItems.filter((item) => !categoryTabs.includes(item.label));

      return [...orderedPanelItems, ...remainingPanelItems];
    }

    const fallbackGroup = new Map<string, CategoryPanelItem>();
    FALLBACK_MENU_CARDS.forEach((card) => {
      const existing = fallbackGroup.get(card.categoryLabel);
      if (existing) {
        existing.count += 1;
        return;
      }
      fallbackGroup.set(card.categoryLabel, {
        key: card.categoryLabel,
        label: card.categoryLabel,
        count: 1,
        displayImageUrl: card.displayImageUrl || DEFAULT_CATEGORY_IMAGE,
      });
    });

    const fallbackItems = Array.from(fallbackGroup.values());
    const fallbackItemMap = new Map(fallbackItems.map((item) => [item.label, item]));
    const orderedFallbackItems = categoryTabs
      .map((categoryLabel) => fallbackItemMap.get(categoryLabel))
      .filter((item): item is CategoryPanelItem => Boolean(item));
    const remainingFallbackItems = fallbackItems.filter((item) => !categoryTabs.includes(item.label));

    return [...orderedFallbackItems, ...remainingFallbackItems];
  }, [activePriceLevel, categoryTabs, language, menu, menuTranslations, partnerLogoFallbackUrl]);

  const filteredCards = useMemo<MenuCardData[]>(() => {
    const normalizedSearch = searchValue.trim().toLowerCase();
    return menuCards.filter((card) => {
      const searchMatch =
        normalizedSearch.length === 0
        || card.name.toLowerCase().includes(normalizedSearch)
        || card.descriptionPlain.toLowerCase().includes(normalizedSearch);

      const filterMatch = Object.entries(selectedFilters).every(([attributeKey, selectedValue]) => {
        if (!selectedValue) {
          return true;
        }
        const sourceItem = menu[card.productKey];
        if (!sourceItem) {
          return true;
        }
        const itemValue = (sourceItem as unknown as Record<string, unknown>)[attributeKey];
        return typeof itemValue === 'string' && itemValue === selectedValue;
      });

      return searchMatch && filterMatch;
    });
  }, [menu, menuCards, searchValue, selectedFilters]);

  /**
   * Keeps the reset control tied to real user-applied narrowing.
   * The default menu view should not show a clear action until search text or filters are active.
   */
  const hasActiveSearchOrFilters = useMemo(
    () => searchValue.trim().length > 0 || Object.values(selectedFilters).some((value) => Boolean(value && value.trim().length > 0)),
    [searchValue, selectedFilters]
  );

  const groupedCards = useMemo<GroupedMenuCards[]>(
    () => categoryTabs
      .map((categoryLabel) => {
        const anchorId = categoryAnchorIds[categoryLabel] ?? `${CATEGORY_ANCHOR_PREFIX}${buildCategoryAnchorSlug(categoryLabel)}`;
        return {
          categoryLabel,
          anchorId,
          cards: filteredCards.filter((card) => card.categoryLabel === categoryLabel),
        };
      })
      .filter((group) => group.cards.length > 0),
    [categoryAnchorIds, categoryTabs, filteredCards]
  );

  const partnerName = partnerData?.name_code
    ? partnerData.name_code.replaceAll('_', ' ')
    : FALLBACK_PARTNER.name;
  const foodMakerKey = partnerData?.food_maker_key ?? '';
  const partnerLogo = partnerData?.logotype ? `${logoImagesPath}${partnerData.logotype}` : null;
  const photoBase = productImagesPath.replace('products/', 'foodpartners/');
  /**
   * The hero preview uses only the first three photos, but the modal slider
   * must expose the complete gallery when the partner has more images.
   */
  const allPartnerPhotos = useMemo(
    () => (partnerData?.photos && partnerData.photos.length > 0
      ? partnerData.photos.map((photo) => toPartnerPhotoUrl(photo, photoBase, 'full'))
      : [FALLBACK_HERO_IMAGE]),
    [partnerData?.photos, photoBase]
  );
  const previewPartnerPhotos = useMemo<PartnerGalleryPhoto[]>(
    () => allPartnerPhotos.slice(0, 3).map((photo) => ({
      displayImageUrl: getImageVariantUrl(photo, 'galleryPreview'),
      fullImageUrl: photo,
    })),
    [allPartnerPhotos]
  );
  const descriptionRaw = partnerData?.short_description_code ?? null;
  const partnerDescriptionHtml = getHtmlOrFallback(descriptionRaw, FALLBACK_PARTNER.description);
  const partnerScheduleHtml = getHtmlOrFallback(partnerData?.schedule_description, FALLBACK_PARTNER.schedule);
  const partnerPhone = toPlainText(partnerData?.contact_phone) || FALLBACK_PARTNER.phone;
  const partnerAddress = toPlainText(partnerData?.pickup_address) || FALLBACK_PARTNER.address;
  const partnerIsTemporarilyStopped = Boolean(partnerData?.is_stop_working_temporary);
  const fulfilmentConstraints = useMemo(
    () => getCartFulfilmentConstraints(shoppingCart, menu),
    [menu, shoppingCart],
  );
  const fulfilmentMode = orderData.deliveryNeeded === 'Yes' ? 'delivery' : 'pickup';
  const fulfilmentSchedule = useMemo(
    () => resolveFulfilmentSchedule(
      activeDeliveryScheduleEntry?.schedule,
      fulfilmentConstraints,
      fulfilmentMode,
      activeDeliveryScheduleEntry?.average_delivery_time,
      scheduleNow,
      activeDeliveryScheduleEntry?.is_deliver_on_time === true,
    ),
    [
      activeDeliveryScheduleEntry?.average_delivery_time,
      activeDeliveryScheduleEntry?.is_deliver_on_time,
      activeDeliveryScheduleEntry?.schedule,
      fulfilmentConstraints,
      fulfilmentMode,
      scheduleNow,
    ],
  );
  const readinessTime = fulfilmentSchedule.readinessTime;
  const requiredFulfilmentInterval = fulfilmentSchedule.firstAllowedInterval;
  const availableDeliveryDayOptions = useMemo<DeliveryDayOption[]>(() => {
    const optionsByDate = new Map<string, DeliveryDayOption>();
    fulfilmentSchedule.allowedIntervals.forEach((candidate) => {
      if (!optionsByDate.has(candidate.dateValue)) {
        optionsByDate.set(candidate.dateValue, {
          value: candidate.dateValue,
          date: new Date(candidate.start),
          label: formatDeliveryDayOptionLabel(candidate.start, language, t, scheduleNow),
        });
      }
    });
    return Array.from(optionsByDate.values()).slice(0, 4);
  }, [fulfilmentSchedule.allowedIntervals, language, scheduleNow, t]);
  const selectedDeliveryDateValue = availableDeliveryDayOptions.some((option) => option.value === orderData.date)
    ? orderData.date ?? ''
    : availableDeliveryDayOptions[0]?.value ?? '';
  const selectedDeliveryDayCandidates = useMemo(
    () => fulfilmentSchedule.allowedIntervals.filter((candidate) => candidate.dateValue === selectedDeliveryDateValue),
    [fulfilmentSchedule.allowedIntervals, selectedDeliveryDateValue],
  );
  const selectedDeliveryDayIntervals = useMemo(
    () => selectedDeliveryDayCandidates.map((candidate) => candidate.label),
    [selectedDeliveryDayCandidates],
  );
  const selectedIntervalCandidate = selectedDeliveryDayCandidates.find((candidate) => candidate.label === orderData.deliveryInterval)
    ?? selectedDeliveryDayCandidates[0]
    ?? null;
  const deliveryScheduleSelection = useMemo<DeliveryScheduleSelection>(() => ({
    isAsapOnly: activeDeliveryScheduleEntry?.is_delivery_ASAP_only === true,
    isExactTimeAllowed: activeDeliveryScheduleEntry?.is_deliver_on_time === true,
    dayOptions: availableDeliveryDayOptions,
    selectedDayIntervals: selectedDeliveryDayIntervals,
    selectedDayCandidates: selectedDeliveryDayCandidates,
    selectedIntervalCandidate,
    readinessTime,
  }), [
    activeDeliveryScheduleEntry?.is_deliver_on_time,
    activeDeliveryScheduleEntry?.is_delivery_ASAP_only,
    availableDeliveryDayOptions,
    readinessTime,
    selectedDeliveryDayCandidates,
    selectedDeliveryDayIntervals,
    selectedIntervalCandidate,
  ]);
  const isDeliveryBlockedByReadiness = orderData.deliveryNeeded === 'Yes'
    && activeDeliveryScheduleEntry?.is_delivery_ASAP_only !== true
    && requiredFulfilmentInterval == null;
  const fulfilmentNotice = useMemo(() => {
    if (
      shoppingCart.length === 0
      || (fulfilmentConstraints.preparationMinutes <= 0 && fulfilmentConstraints.nearestInterval == null)
    ) {
      return null;
    }

    if (orderData.deliveryNeeded !== 'Yes') {
      if (requiredFulfilmentInterval) {
        return t(
          'pickup_earliest_interval_notice',
          `Your order will be ready for pickup no earlier than ${requiredFulfilmentInterval.dateValue}, ${requiredFulfilmentInterval.label}.`,
        );
      }
      return t(
        'pickup_readiness_notice',
        `Your order will be ready for pickup no earlier than ${formatFulfilmentDateTime(readinessTime, language)}.`,
      );
    }

    if (isDeliveryBlockedByReadiness) {
      return t('delivery_readiness_unavailable', 'No delivery interval satisfies the preparation-time requirement. Please revise your order.');
    }
    if (requiredFulfilmentInterval) {
      return t(
        'delivery_interval_adjusted_notice',
        'Delivery is available no earlier than {date}, {interval}.',
      )
        .replace('{date}', requiredFulfilmentInterval.dateValue)
        .replace('{interval}', requiredFulfilmentInterval.label);
    }
    if (fulfilmentConstraints.preparationMinutes > 0) {
      return t(
        'delivery_readiness_notice',
        `Delivery will not occur before ${formatFulfilmentDateTime(readinessTime, language)}.`,
      );
    }
    return null;
  }, [
    fulfilmentConstraints.nearestInterval,
    fulfilmentConstraints.preparationMinutes,
    isDeliveryBlockedByReadiness,
    language,
    orderData.deliveryNeeded,
    readinessTime,
    requiredFulfilmentInterval,
    shoppingCart.length,
    t,
  ]);
  const deliveryMapContext = useMemo(
    () => resolveDeliveryMapContext(
      orderData,
      sessionData,
      userData,
      partnerData ?? null,
      simpleOrderPlacement,
    ),
    [orderData, partnerData, sessionData, simpleOrderPlacement, userData]
  );
  const effectiveDeliveryOrderData = useMemo(
    () => applyEffectiveDeliveryCoordinates(orderData, deliveryMapContext.effectiveCoordinates),
    [deliveryMapContext.effectiveCoordinates, orderData]
  );
  const partnerWorkingSchedule = useMemo<PartnerDailySchedule | undefined>(() => (
    (partnerData?.daly_schedule ?? partnerData?.daily_schedule) as PartnerDailySchedule | undefined
  ), [partnerData?.daily_schedule, partnerData?.daly_schedule]);
  const partnerIsOpenNow = isPartnerOpenNow(partnerWorkingSchedule);
  const manualDeliveryUnavailable = activeDeliveryScheduleEntry?.is_delivery_available === false;
  const isDeliveryScheduleOpenNowValue = useMemo(
    () => isDeliveryScheduleOpenNow(activeDeliveryScheduleEntry?.schedule),
    [activeDeliveryScheduleEntry?.schedule]
  );
  /**
   * A closed current interval does not make delivery unavailable when the
   * location schedule contains a future day. ASAP hides that future selection
   * from customers but still retains it for backend fulfilment.
   */
  const operationalDeliveryDayOptions = useMemo(
    () => getAvailableDeliveryDayOptions(activeDeliveryScheduleEntry?.schedule, language, t, scheduleNow),
    [activeDeliveryScheduleEntry?.schedule, language, scheduleNow, t],
  );
  const canScheduleFutureDelivery = operationalDeliveryDayOptions.length > 0;
  const isDeliveryClosedByScheduleNow = partnerIsOpenNow
    && isDeliveryScheduleOpenNowValue === false
    && !canScheduleFutureDelivery;
  const isDeliveryUnavailableNow = manualDeliveryUnavailable || isDeliveryClosedByScheduleNow;
  const partnerNextOpening = useMemo(
    () => getPartnerNextOpening(partnerWorkingSchedule),
    [partnerWorkingSchedule]
  );
  const partnerNextOpeningLabel = useMemo(
    () => formatNextOpeningLabel(partnerNextOpening, language),
    [language, partnerNextOpening]
  );
  const partnerScheduleWarningHtml = useMemo(() => {
    const nestedScheduleText = resolveTranslatedOrPlainHtml(getScheduleDescriptionTextCode(partnerWorkingSchedule));
    if (nestedScheduleText) {
      return nestedScheduleText;
    }

    return getHtmlOrFallback(partnerData?.schedule_description, '');
  }, [partnerData?.schedule_description, partnerWorkingSchedule, resolveTranslatedOrPlainHtml]);
  const temporaryStopPopupHtml = useMemo(() => {
    if (!partnerIsTemporarilyStopped) {
      return null;
    }

    const noticeText = partnerData?.stop_working_notice_code;
    return toModalHtml(noticeText) || toModalHtml('The restaurant is temporarily closed. Orders are not available right now.');
  }, [partnerData?.stop_working_notice_code, partnerIsTemporarilyStopped]);
  const deliveryUnavailableNoticeHtml = useMemo(() => {
    if (!isDeliveryUnavailableNow) {
      return null;
    }

    if (manualDeliveryUnavailable) {
      return toModalHtml(activeDeliveryScheduleEntry?.delivery_unavailable_notification_text_code);
    }

    if (isDeliveryClosedByScheduleNow) {
      return toModalHtml(t('there_is_no_delivery', 'Delivery is not available right now.'));
    }

    return null;
  }, [
    activeDeliveryScheduleEntry?.delivery_unavailable_notification_text_code,
    isDeliveryClosedByScheduleNow,
    isDeliveryUnavailableNow,
    manualDeliveryUnavailable,
    t,
  ]);
  const closedHoursPopupHtml = useMemo(() => {
    if (partnerIsTemporarilyStopped || partnerIsOpenNow) {
      return null;
    }

    const warningText = t(
      'non_time_work_warning',
      'The cafe is currently closed. Orders may not be available right now or may be processed later.'
    );

    return [warningText, partnerScheduleWarningHtml]
      .map((value) => toModalHtml(value))
      .filter((value) => value.length > 0)
      .join('');
  }, [partnerIsOpenNow, partnerIsTemporarilyStopped, partnerScheduleWarningHtml, t]);
  /**
   * Temporary stop blocks all ordering and owns the modal by itself. Delivery
   * unavailability is softer: it only disables delivery and can be shown
   * together with the existing closed-hours warning.
   */
  const operationalNoticePopupHtml = joinModalHtmlSections(closedHoursPopupHtml, deliveryUnavailableNoticeHtml) || null;
  const closedHoursOrderFlowWarningHtml = useMemo(() => {
    if (partnerIsTemporarilyStopped || partnerIsOpenNow) {
      return null;
    }

    const warningText = t(
      'non_time_order_warning',
      'The cafe is currently outside working hours. Orders may not be available right now or may be processed later.'
    );

    const warningMessage = partnerNextOpeningLabel
      ? `${warningText} ${partnerNextOpeningLabel}`
      : warningText;
    return toModalHtml(warningMessage);
  }, [partnerIsOpenNow, partnerIsTemporarilyStopped, partnerNextOpeningLabel, t]);
  const orderFlowWarning = partnerIsTemporarilyStopped
    ? null
    : (joinModalHtmlSections(closedHoursOrderFlowWarningHtml, deliveryUnavailableNoticeHtml) || null);
  const operationalNoticePopupType = [
    closedHoursPopupHtml ? 'closed-hours' : null,
    deliveryUnavailableNoticeHtml ? 'delivery-unavailable' : null,
  ].filter(Boolean).join('+') || 'none';
  const activeNoticePopupHtml = temporaryStopPopupHtml ?? operationalNoticePopupHtml;
  const activeNoticePopupType = temporaryStopPopupHtml ? 'temporary-stop' : operationalNoticePopupType;
  const activeNoticePopupTitle = temporaryStopPopupHtml
    ? t('temporarily_closed', 'Temporarily closed')
    : (closedHoursPopupHtml ? t('closed_now', 'Closed now') : t('delivery_unavailable', 'Delivery unavailable'));
  /**
   * The about card now reserves a dedicated status line above the contact rows.
   * Today it carries only the open/closed state, but the list form keeps the
   * layout ready for additional partner status chips later.
   */
  const partnerStatusItems = [
    {
      key: partnerIsOpenNow ? 'open_now' : 'closed_now',
      label: partnerIsOpenNow ? t('open_now', 'Open now') : t('closed_now', 'Closed now'),
      toneClassName: partnerIsOpenNow ? 'bg-success-subtle text-success' : 'bg-secondary-subtle text-secondary',
    },
  ];
  const cartItemsCount = shoppingCart.reduce((total, item) => total + item.quantity, 0);
  const totalProductsCost = useMemo(
    () => calculateTotalProductsCost(shoppingCart),
    [shoppingCart],
  );
  const receiptSummary = useMemo(
    () => buildReceiptSummary(
      shoppingCart,
      partnerData ?? null,
      effectiveDeliveryOrderData,
      language,
      promoData,
      deliveryCostState.status === 'success' && deliveryMapContext.effectiveCoordinates
        ? deliveryCostState.cost
        : null,
      deliveryMapContext.effectiveCoordinates,
    ),
    [
      deliveryCostState.cost,
      deliveryCostState.status,
      deliveryMapContext.effectiveCoordinates,
      effectiveDeliveryOrderData,
      language,
      partnerData,
      promoData,
      shoppingCart,
    ]
  );
  const buildCurrentDataLayerPayload = useCallback((cart: ShoppingCart, value: number) => (
    buildMenuDataLayerEcommercePayload(cart, menu, menuTranslations, language, activePriceLevel, value)
  ), [
    activePriceLevel,
    language,
    menu,
    menuTranslations,
  ]);
  const deliveryMinimumOrderAmount = deliveryCostState.status === 'success'
    ? deliveryCostState.minimumOrderAmount
    : 0;
  const isDeliveryMinimumOrderActive = orderData.deliveryNeeded === 'Yes'
    && deliveryMinimumOrderAmount > 0;
  const isDeliveryMinimumOrderMet = !isDeliveryMinimumOrderActive
    || receiptSummary.subtotal >= deliveryMinimumOrderAmount;
  const deliveryMinimumOrderWarningHtml = useMemo(() => {
    if (isDeliveryMinimumOrderMet || !isDeliveryMinimumOrderActive) {
      return null;
    }

    return toModalHtml(t(
      'delivery_minimum_order_amount_warning',
      'Delivery to this point is available only for orders of at least {amount}. Please add more items or choose pickup at the cafe.'
    ).replace('{amount}', formatEuroOneDecimal(deliveryMinimumOrderAmount)));
  }, [
    deliveryMinimumOrderAmount,
    isDeliveryMinimumOrderActive,
    isDeliveryMinimumOrderMet,
    t,
  ]);
  const effectiveOrderFlowWarning = useMemo(
    () => joinModalHtmlSections(orderFlowWarning, deliveryMinimumOrderWarningHtml) || null,
    [deliveryMinimumOrderWarningHtml, orderFlowWarning]
  );

  const productLookup = useMemo<Record<string, MenuItemWithKey>>(() => (
    Object.entries(menu).reduce<Record<string, MenuItemWithKey>>((acc, [productKey, item]) => {
      acc[productKey] = { ...item, product_key: productKey };
      return acc;
    }, {})
  ), [menu]);

  /**
   * Social icons are rendered only for links that exist in partnerData.socialmedia_links.
   * This keeps the block aligned with backend configuration without placeholder actions.
   */
  const socialLinks = useMemo((): SocialLinkItem[] => {
    const links = partnerData?.socialmedia_links;
    if (!links) {
      return [];
    }

    const items: SocialLinkItem[] = [];

    if (links.telegram) {
      items.push({
        key: 'telegram',
        href: links.telegram,
        label: t('telegram', 'Telegram'),
        icon: <FaTelegramPlane className="text-secondary" />,
      });
    }

    if (links.instagram) {
      items.push({
        key: 'instagram',
        href: links.instagram,
        label: t('instagram', 'Instagram'),
        icon: <FaInstagram className="text-secondary" />,
      });
    }

    if (links.whatsapp) {
      items.push({
        key: 'whatsapp',
        href: links.whatsapp,
        label: t('whatsapp', 'WhatsApp'),
        icon: <FaWhatsapp className="text-secondary" />,
      });
    }

    return items;
  }, [partnerData?.socialmedia_links, t]);

  /**
   * The closed-hours popup is a one-time informational notice for the current
   * partner. It should not reopen on every rerender once the customer dismisses
   * it during the same page visit.
   */
  useEffect(() => {
    const partnerKey = partnerData?.food_maker_key;
    if (!partnerKey || !activeNoticePopupHtml) {
      return;
    }

    const noticeKey = `${partnerKey}:${activeNoticePopupType}`;
    if (shownClosedHoursWarningPartnerKeyRef.current === noticeKey) {
      return;
    }

    shownClosedHoursWarningPartnerKeyRef.current = noticeKey;
    setIsClosedHoursModalOpen(true);
  }, [activeNoticePopupHtml, activeNoticePopupType, partnerData?.food_maker_key]);

  useEffect(() => {
    if (simpleOrderPlacement || !isDeliveryUnavailableNow || orderData.deliveryNeeded !== 'Yes') {
      return;
    }

    setOrderData((previous) => (
      previous.deliveryNeeded === 'Yes'
        ? { ...previous, deliveryNeeded: 'No' }
        : previous
    ));
  }, [isDeliveryUnavailableNow, orderData.deliveryNeeded, simpleOrderPlacement]);

  /**
   * The same readiness-filtered selection is used for pickup and delivery. It
   * preserves valid later choices and changes only missing or invalid fields.
   * ASAP hides these values from the customer but retains them for fulfilment.
   */
  useEffect(() => {
    const patch: Partial<MenuOrderData> = {};
    const firstDay = deliveryScheduleSelection.dayOptions[0] ?? null;
    const hasValidDay = Boolean(
      orderData.date
      && deliveryScheduleSelection.dayOptions.some((dayOption) => dayOption.value === orderData.date)
    );
    const effectiveDate = hasValidDay ? orderData.date ?? '' : firstDay?.value ?? '';
    if (!hasValidDay && firstDay) {
      patch.date = firstDay.value;
    }

    const dayCandidates = fulfilmentSchedule.allowedIntervals.filter((candidate) => candidate.dateValue === effectiveDate);
    const selectedCandidate = dayCandidates.find((candidate) => candidate.label === orderData.deliveryInterval)
      ?? dayCandidates[0]
      ?? null;
    const hasValidInterval = Boolean(
      selectedCandidate
      && isCandidateSelection(selectedCandidate, effectiveDate, orderData.deliveryInterval)
    );
    if (!hasValidInterval && selectedCandidate) {
      patch.deliveryInterval = selectedCandidate.label;
    }

    if (deliveryScheduleSelection.isAsapOnly) {
      if (orderData.time != null) patch.time = null;
      if (orderData.deliveryInTime !== 'No') patch.deliveryInTime = 'No';
    } else {
      if (!deliveryScheduleSelection.isExactTimeAllowed) {
        if (orderData.deliveryInTime !== 'No') patch.deliveryInTime = 'No';
        if (orderData.time != null) patch.time = null;
      } else if (orderData.deliveryInTime !== 'Yes') {
        if (orderData.deliveryInTime !== 'No') patch.deliveryInTime = 'No';
        if (orderData.time != null) patch.time = null;
      } else if (
        selectedCandidate
        && orderData.time
        && !isExactTimeOptionAvailable(selectedCandidate, orderData.time, readinessTime)
      ) {
        patch.time = getCandidateExactTimeOptions(selectedCandidate, readinessTime)[0] ?? null;
      }

    }

    if (!firstDay) {
      if (orderData.date != null) patch.date = null;
      if (orderData.deliveryInterval != null) patch.deliveryInterval = null;
      if (orderData.time != null) patch.time = null;
      if (orderData.deliveryInTime !== 'No') patch.deliveryInTime = 'No';
    }

    if (Object.keys(patch).length === 0) {
      return;
    }

    setOrderData((previous) => ({ ...previous, ...patch }));
  }, [
    deliveryScheduleSelection.dayOptions,
    deliveryScheduleSelection.isAsapOnly,
    deliveryScheduleSelection.isExactTimeAllowed,
    fulfilmentSchedule.allowedIntervals,
    orderData.date,
    orderData.deliveryInTime,
    orderData.deliveryInterval,
    orderData.time,
    readinessTime,
  ]);

  useEffect(() => {
    if (!partnerIsTemporarilyStopped || !isOptionsOpen) {
      return;
    }

    setIsOptionsOpen(false);
    setOptionsItem(null);
  }, [isOptionsOpen, partnerIsTemporarilyStopped]);

  useEffect(() => {
    if (!foodMakerKey) {
      return;
    }
    const storage = getCartStorage();
    const cart = (storage[foodMakerKey] as ShoppingCart | undefined) ?? [];
    const normalizedCart = cart.map((item) => {
      if (menu[item.product_key]) {
        return item;
      }
      const matchedEntry = Object.entries(menu).find(([, menuItem]) => menuItem.name_code === item.product_key);
      return matchedEntry ? { ...item, product_key: matchedEntry[0] } : item;
    }).reduce<ShoppingCart>((acc, item) => {
      const menuItem = menu[item.product_key] as MenuItemWithPrice | undefined;
      if (!menuItem) {
        acc.push(item);
        return acc;
      }

      const totalCost = getCartItemEffectiveUnitPrice(item, menu, activePriceLevel);
      if (totalCost > 0) {
        acc.push({ ...item, total_cost: totalCost });
      }
      return acc;
    }, []);
    setShoppingCart(normalizedCart);
  }, [activePriceLevel, foodMakerKey, menu]);

  useEffect(() => {
    if (!foodMakerKey) {
      return;
    }
    const storage = getCartStorage();
    storage[foodMakerKey] = shoppingCart;
    saveCartStorage(storage);
  }, [foodMakerKey, shoppingCart]);

  const handleUpdateCart = useCallback((nextCart: ShoppingCart) => {
    setShoppingCart(nextCart);
    setUnavailableCartItemNames([]);
  }, []);

  useEffect(() => {
    if (!sessionData) {
      return;
    }
    setOrderData((previous) => {
      const nextOrderDraft: MenuOrderData = {
        ...previous,
        ...sessionData,
        deliveryNeeded: normalizeDeliveryNeeded(sessionData.deliveryNeeded),
        changeNeeded: normalizeYesNo(sessionData.changeNeeded),
      };
      const nextEffectiveChannel = resolveEffectiveOrderChannel(nextOrderDraft, sessionData, userData);
      const nextShouldForcePhoneContact = shouldUseWebPhoneContactForMissingTelegramId(nextOrderDraft, sessionData, userData);
      const nextShouldUsePhoneOnlyTelegramContact = shouldUsePhoneOnlyContactForTelegramWithoutUsername(
        nextOrderDraft,
        sessionData,
        userData,
      );
      const previousPreferredMethod = previous.contacts?.preferableMethod;
      const sessionPreferredMethod = sessionData.contacts?.preferableMethod;
      const nextPreferredMethod = nextShouldForcePhoneContact || nextShouldUsePhoneOnlyTelegramContact
        ? 'phone'
        : (previousPreferredMethod ?? sessionPreferredMethod ?? (nextEffectiveChannel === 'telegram' ? 'telegram' : 'phone'));

      return {
        ...nextOrderDraft,
        paymentMethod: previous.paymentMethod ?? 'cash',
        contacts: {
          ...(previous.contacts ?? {}),
          ...sessionData.contacts,
          preferableMethod: nextPreferredMethod,
        },
      };
    });
  }, [sessionData, userData]);

  useEffect(() => {
    if (!simpleOrderPlacement || !simpleOrderPlacementPartnerDefaults) {
      return;
    }

    /**
     * The wholesale workaround keeps hidden required fields deterministic
     * after session hydration without replacing the user's name, comment, or
     * channel identity.
     */
    setOrderData((previous) => applySimpleOrderPlacementDefaults(
      previous,
      simpleOrderPlacementPartnerDefaults,
    ));
  }, [
    sessionData,
    simpleOrderPlacement,
    simpleOrderPlacementPartnerDefaults,
    userData,
  ]);

  /**
   * Backend page parameters can define the initial product-card view for each
   * partner. The value is applied once per partner/default combination, so the
   * visible switcher remains user-controlled after the page has loaded.
   */
  useEffect(() => {
    const foodMakerKey = partnerData?.food_maker_key ?? '';
    const defaultViewKey = `${foodMakerKey}:${backendDefaultProductView ?? 'responsive'}`;

    if (!foodMakerKey || appliedDefaultProductViewKeyRef.current === defaultViewKey) {
      return;
    }

    appliedDefaultProductViewKeyRef.current = defaultViewKey;
    setViewMode(backendDefaultProductView ?? getResponsiveDefaultProductView());
  }, [backendDefaultProductView, partnerData?.food_maker_key]);

  /**
   * Without a backend page default, the legacy responsive rule keeps compact
   * cards mobile-only and promotes wider screens to the two-column grid.
   */
  useEffect(() => {
    if (backendDefaultProductView) {
      return undefined;
    }

    const syncViewModeWithViewport = () => {
      if (window.innerWidth >= BOOTSTRAP_SM_BREAKPOINT) {
        setViewMode((previous) => (previous === 'grid' ? previous : 'grid'));
      }
    };

    syncViewModeWithViewport();
    window.addEventListener('resize', syncViewModeWithViewport);
    return () => window.removeEventListener('resize', syncViewModeWithViewport);
  }, [backendDefaultProductView]);

  useEffect(() => {
    const profileKey = getAuthProfileKey(authenticatedProfile);
    if (!authenticatedProfile || !profileKey || appliedAuthProfileKeyRef.current === profileKey) {
      return;
    }

    /**
     * Authenticated profile data is a progressive enhancement for checkout.
     * It only fills missing order fields once per signed-in identity, so a
     * later auth refresh cannot replace values the user typed for this order.
     */
    setOrderData((previous) => mergeOrderDataWithAuthProfile(previous, authenticatedProfile));
    appliedAuthProfileKeyRef.current = profileKey;
  }, [authenticatedProfile]);

  useEffect(() => {
    if (Object.keys(orderData).length === 0) {
      return;
    }
    localStorage.setItem('orderData', JSON.stringify({
      ...orderData,
      update_time: Date.now(),
    }));
  }, [orderData]);

  const loadDeliveryCost = useCallback(async () => {
    const partnerNameCode = resolveDeliveryCostPartnerCode(partnerData ?? null, orderData);
    const effectiveDeliveryCoordinates = deliveryMapContext.effectiveCoordinates;
    const latitude = effectiveDeliveryCoordinates?.[0] ?? null;
    const longitude = effectiveDeliveryCoordinates?.[1] ?? null;

    if (orderData.deliveryNeeded !== 'Yes') {
      setDeliveryCostState({ status: 'idle', cost: null, minimumOrderAmount: 0, message: null });
      return;
    }

    if (!partnerNameCode || latitude == null || longitude == null) {
      setDeliveryCostState({
        status: 'error',
        cost: null,
        minimumOrderAmount: 0,
        message: t('delivery_cost_missing_data', 'Delivery cost cannot be calculated without partner and delivery coordinates.'),
      });
      return;
    }

    setDeliveryCostState({ status: 'loading', cost: null, minimumOrderAmount: 0, message: null });

    try {
      const deliveryCost = await fetchDeliveryCost(
        {
          partner_name_code: partnerNameCode,
          latitude,
          longitude,
          total_products_cost: totalProductsCost,
        },
        {
          failed: t('delivery_cost_failed', 'Failed to calculate delivery cost.'),
          invalid: t('delivery_cost_invalid_response', 'Delivery cost response did not contain a valid amount.'),
        }
      );
      setDeliveryCostState({
        status: 'success',
        cost: deliveryCost.cost,
        minimumOrderAmount: deliveryCost.minimumOrderAmount,
        message: null,
      });
    } catch (error) {
      setDeliveryCostState({
        status: 'error',
        cost: null,
        minimumOrderAmount: 0,
        message: error instanceof Error ? error.message : t('delivery_cost_failed', 'Failed to calculate delivery cost.'),
      });
    }
  }, [
    deliveryMapContext.effectiveCoordinates,
    orderData,
    partnerData,
    t,
    totalProductsCost,
  ]);

  useEffect(() => {
    if (checkoutStep !== 'confirm') {
      return;
    }

    /**
     * The Review Order screen must show the backend delivery amount for the
     * currently selected delivery point; recalculating here keeps cart and
     * address edits from reusing a stale amount or delivery cost.
     */
    loadDeliveryCost();
  }, [checkoutStep, loadDeliveryCost]);

  /**
   * Checkout validation should bring the user to the first required field that
   * still blocks progression instead of leaving the modal scrolled elsewhere.
   */
  const scrollToFirstCheckoutErrorField = useCallback((errors: OrderFormErrors) => {
    const fieldId = [
      errors.name ? 'checkout-name-field' : null,
      errors.countryCode ? 'checkout-country-code-field' : null,
      errors.phone ? 'checkout-phone-field' : null,
      errors.telegramName ? 'checkout-telegram-field' : null,
      errors.deliveryDate ? 'checkout-delivery-date-field' : null,
      errors.deliveryInterval ? 'checkout-delivery-interval-field' : null,
      errors.deliveryTime ? 'checkout-delivery-time-field' : null,
      errors.deliveryPoint ? 'checkout-delivery-point-field' : null,
      errors.deliveryAddress ? 'checkout-delivery-address-field' : null,
      errors.changeAmount ? 'checkout-change-amount-field' : null,
    ].find((value): value is string => Boolean(value));

    if (!fieldId) {
      return;
    }

    window.requestAnimationFrame(() => {
      const targetField = document.getElementById(fieldId);
      if (!targetField) {
        return;
      }

      targetField.scrollIntoView({ behavior: 'smooth', block: 'center' });
      targetField.focus({ preventScroll: true });
    });
  }, []);

  const getQuantityInCart = (productKey: string): number => (
    shoppingCart.reduce((total, cartItem) => (
      cartItem.product_key === productKey ? total + cartItem.quantity : total
    ), 0)
  );

  const addCartItem = (item: MenuItemWithKey, cartItem: CartProductItem) => {
    if (partnerIsTemporarilyStopped) {
      setIsClosedHoursModalOpen(true);
      return;
    }

    const existingIndex = shoppingCart.findIndex((existingItem) =>
      existingItem.product_key === cartItem.product_key
      && existingItem.product_version === cartItem.product_version
      && areArraysEqual(existingItem.combo_product_list, cartItem.combo_product_list)
      && areArraysEqual(existingItem.adds, cartItem.adds)
    );

    const minQuantity = item.min_quantity ?? 1;
    const addedCartItem = {
      ...cartItem,
      quantity: Math.max(cartItem.quantity, minQuantity),
    };
    const updatedCart = [...shoppingCart];
    if (existingIndex >= 0) {
      const existingItem = updatedCart[existingIndex];
      updatedCart[existingIndex] = {
        ...existingItem,
        quantity: Math.max(existingItem.quantity + cartItem.quantity, minQuantity),
      };
    } else {
      updatedCart.push(addedCartItem);
    }

    setShoppingCart(updatedCart);
    setOrderStatus(null);
    setUnavailableCartItemNames([]);
    pushDataLayerEvent(
      DATA_LAYER_EVENT_NAMES.addToCart,
      buildCurrentDataLayerPayload(
        [addedCartItem],
        getCartItemEffectiveUnitPrice(addedCartItem, menu, activePriceLevel) * addedCartItem.quantity
      )
    );
  };

  const adjustSimpleItemQuantity = (item: MenuItemWithKey, delta: number) => {
    const unitStep = item.unit_step ?? 1;
    const minQuantity = item.min_quantity ?? 1;
    const targetIndex = shoppingCart.findIndex((existingItem) =>
      existingItem.product_key === item.product_key
      && existingItem.product_version === null
      && existingItem.combo_product_list === null
      && existingItem.adds === null
    );

    if (delta > 0) {
      const unitPrice = getEffectiveItemPrice(item, activePriceLevel);
      if (unitPrice <= 0) {
        return;
      }
      addCartItem(item, {
        product_key: item.product_key,
        product_version: null,
        combo_product_list: null,
        adds: null,
        total_cost: unitPrice,
        quantity: unitStep,
      });
      return;
    }

    if (targetIndex < 0) {
      return;
    }

    const updatedCart = [...shoppingCart];
    const existingItem = updatedCart[targetIndex];
    const nextQuantity = existingItem.quantity - unitStep;
    if (nextQuantity < minQuantity) {
      updatedCart.splice(targetIndex, 1);
    } else {
      updatedCart[targetIndex] = { ...existingItem, quantity: nextQuantity };
    }
    setShoppingCart(updatedCart);
  };

  const handleCardAdd = (productKey: string) => {
    const item = productLookup[productKey];
    if (partnerIsTemporarilyStopped) {
      setIsClosedHoursModalOpen(true);
      return;
    }

    if (!item || !partnerData?.is_order || !item.is_available || getEffectiveItemPrice(item, activePriceLevel) <= 0) {
      return;
    }

    const shouldOpenOptions = Boolean(
      hasProductVersions(item)
      || (item.possible_adds && item.possible_adds.length > 0)
      || (item.combo && item.combo.length > 0)
    );
    if (shouldOpenOptions) {
      setOptionsItem(item);
      setIsOptionsOpen(true);
      return;
    }

    addCartItem(item, {
      product_key: item.product_key,
      product_version: null,
      combo_product_list: null,
      adds: null,
      total_cost: getEffectiveItemPrice(item, activePriceLevel),
      quantity: item.unit_step ?? 1,
    });
  };

  const handleCardContentClick = (productKey: string) => {
    if (partnerIsTemporarilyStopped) {
      return;
    }

    const item = productLookup[productKey];
    if (!item) {
      return;
    }
    const shouldOpenOptions = Boolean(
      hasProductVersions(item)
      || (item.possible_adds && item.possible_adds.length > 0)
      || (item.combo && item.combo.length > 0)
    );
    if (shouldOpenOptions) {
      handleCardAdd(productKey);
    }
  };

  /**
   * Keeps image preview separate from card-body actions so users can enlarge a
   * product photo without triggering the product configuration modal.
   */
  const handleOpenProductImage = (imageUrl: string, imageAlt: string) => {
    setProductImagePreview({ src: imageUrl, alt: imageAlt });
  };

  const validateOrderData = (): { isValid: boolean; errors: OrderFormErrors } => {
    const isTelegramOrder = effectiveOrderChannel === 'telegram';
    const shouldRequirePhoneOnlyTelegramContact = shouldUsePhoneOnlyTelegramContact;
    const shouldRequireName = simpleOrderPlacement
      || (!isTelegramOrder && !shouldRequirePhoneOnlyTelegramContact);
    const isTelegramPreferred = !shouldForcePhoneContact
      && !shouldRequirePhoneOnlyTelegramContact
      && orderData.contacts?.preferableMethod === 'telegram';
    const shouldRequirePhoneDetails = shouldRequirePhoneOnlyTelegramContact
      || (!isTelegramOrder && !isTelegramPreferred);
    const hasDeliveryCoordinates = Boolean(deliveryMapContext.effectiveCoordinates);
    const shouldValidateDeliverySchedule = !deliveryScheduleSelection.isAsapOnly
      && (orderData.deliveryNeeded === 'Yes' || deliveryScheduleSelection.dayOptions.length > 0);
    const selectedDeliveryDateIsValid = Boolean(
      orderData.date
      && deliveryScheduleSelection.dayOptions.some((dayOption) => dayOption.value === orderData.date)
    );
    const selectedDeliveryIntervalIsValid = Boolean(
      orderData.deliveryInterval
      && deliveryScheduleSelection.selectedDayIntervals.includes(orderData.deliveryInterval)
    );
    const exactTimeCandidate = deliveryScheduleSelection.selectedDayCandidates.find((candidate) => (
      candidate.dateValue === orderData.date && candidate.label === orderData.deliveryInterval
    )) ?? null;
    const shouldRequireExactDeliveryTime = shouldValidateDeliverySchedule
      && deliveryScheduleSelection.isExactTimeAllowed
      && orderData.deliveryInTime === 'Yes';
    const shouldRequireDeliveryInterval = shouldValidateDeliverySchedule
      && selectedDeliveryDateIsValid;
    const selectedExactTimeIsValid = Boolean(
      exactTimeCandidate
      && isExactTimeOptionAvailable(exactTimeCandidate, orderData.time, deliveryScheduleSelection.readinessTime)
    );
    const hasValidChangeAmount = typeof orderData.changeAmount === 'number'
      && Number.isFinite(orderData.changeAmount)
      && orderData.changeAmount > 0;
    const nextErrors: OrderFormErrors = {
      name: shouldRequireName && !orderData.channel_first_name,
      countryCode: shouldRequirePhoneDetails && !orderData.countryCode,
      phone: shouldRequirePhoneDetails && !orderData.phone,
      telegramName: !isTelegramOrder && isTelegramPreferred && !orderData.contacts?.telegram?.userName,
      deliveryPoint: orderData.deliveryNeeded === 'Yes' && !hasDeliveryCoordinates,
      deliveryAddress: orderData.deliveryNeeded === 'Yes' && !orderData.deliveryAddress,
      deliveryDate: shouldValidateDeliverySchedule && !selectedDeliveryDateIsValid,
      deliveryTime: shouldRequireExactDeliveryTime && !selectedExactTimeIsValid,
      deliveryInterval: shouldRequireDeliveryInterval && !selectedDeliveryIntervalIsValid,
      changeAmount: orderData.deliveryNeeded === 'Yes'
        && orderData.paymentMethod !== 'bank_card'
        && orderData.changeNeeded === 'Yes'
        && !hasValidChangeAmount,
    };
    setOrderErrors(nextErrors);
    return {
      isValid: !Object.values(nextErrors).some(Boolean),
      errors: nextErrors,
    };
  };

  const handleProceedToReview = () => {
    if (isDeliveryBlockedByReadiness) {
      setOrderStatus(t('delivery_readiness_unavailable', 'No delivery interval satisfies the preparation-time requirement. Please revise your order.'));
      setCheckoutStep('cart');
      return;
    }
    if (partnerIsTemporarilyStopped) {
      setOrderStatus(toPlainText(temporaryStopPopupHtml) || 'The restaurant is temporarily closed. Orders are not available right now.');
      setCheckoutStep('cart');
      return;
    }

    const validation = validateOrderData();

    if (!validation.isValid) {
      setOrderStatus(t('complete_required_fields', 'Please complete the highlighted fields before continuing.'));
      scrollToFirstCheckoutErrorField(validation.errors);
      return;
    }
    setOrderStatus(null);
    pushDataLayerEvent(
      DATA_LAYER_EVENT_NAMES.checkoutReview,
      buildCurrentDataLayerPayload(shoppingCart, receiptSummary.total)
    );
    setCheckoutStep('confirm');
  };

  const handleConfirmOrder = async () => {
    if (isSubmittingOrderRef.current) {
      return;
    }

    if (isDeliveryBlockedByReadiness) {
      setOrderStatus(t('delivery_readiness_unavailable', 'No delivery interval satisfies the preparation-time requirement. Please revise your order.'));
      setCheckoutStep('cart');
      return;
    }

    if (partnerIsTemporarilyStopped) {
      setOrderStatus(toPlainText(temporaryStopPopupHtml) || 'The restaurant is temporarily closed. Orders are not available right now.');
      setCheckoutStep('cart');
      return;
    }

    const validation = validateOrderData();

    if (!validation.isValid) {
      setOrderStatus(t('complete_required_fields', 'Please complete the highlighted fields before continuing.'));
      setCheckoutStep('checkout');
      scrollToFirstCheckoutErrorField(validation.errors);
      return;
    }

    isSubmittingOrderRef.current = true;
    setIsSubmittingOrder(true);
    setOrderStatus(null);
    setReceiptHtml('');
    setReceiptNotice(null);
    setUnavailableCartItemNames([]);

    try {
      if (!refreshPartnerData) {
        throw new Error('Menu refresh is not available.');
      }

      const refreshedPartnerData = await refreshPartnerData();
      if (!refreshedPartnerData?.menu) {
        throw new Error('Menu refresh failed.');
      }

      const cartValidation = validateCartAgainstMenu(
        shoppingCart,
        refreshedPartnerData.menu,
        menu,
        activePriceLevel,
        menuTranslations,
        language,
      );

      if (cartValidation.unavailableItemNames.length > 0) {
        setShoppingCart(cartValidation.validCart);
        setUnavailableCartItemNames(cartValidation.unavailableItemNames);
        setPromoCode('');
        setPromoData(null);
        setPromoMessage(null);
        setCheckoutStep('cart');
        const storage = getCartStorage();
        storage[foodMakerKey] = cartValidation.validCart;
        saveCartStorage(storage);
        return;
      }

      setShoppingCart(cartValidation.validCart);
      const refreshedSimpleDefaults = {
        pickupAddress: toPlainText(refreshedPartnerData.pickup_address),
        pickupPoint: getPartnerCoordinates(refreshedPartnerData),
        location: refreshedPartnerData.location ?? '',
      };
      const refreshedOrderData = simpleOrderPlacement
        ? applySimpleOrderPlacementDefaults(orderData, refreshedSimpleDefaults)
        : orderData;
      const refreshedConstraints = getCartFulfilmentConstraints(cartValidation.validCart, refreshedPartnerData.menu);
      const refreshedScheduleResolution = resolveLocationSchedule(
        refreshedPartnerData.delivery_schedule,
        [refreshedOrderData.location, sessionData?.location, userData?.location],
        refreshedPartnerData.location,
      );
      const refreshedSchedule = refreshedScheduleResolution?.entry;
      const refreshedMode = refreshedOrderData.deliveryNeeded === 'Yes' ? 'delivery' : 'pickup';
      const refreshedResolution = resolveFulfilmentSchedule(
        refreshedSchedule?.schedule,
        refreshedConstraints,
        refreshedMode,
        refreshedSchedule?.average_delivery_time,
        new Date(),
        refreshedSchedule?.is_deliver_on_time === true,
      );
      const refreshedSelectedInterval = refreshedResolution.allowedIntervals.find((candidate) => (
        isCandidateSelection(candidate, refreshedOrderData.date, refreshedOrderData.deliveryInterval)
      )) ?? null;
      const refreshedRequiredInterval = refreshedSelectedInterval ?? refreshedResolution.firstAllowedInterval;
      const refreshedIsAsapOnly = refreshedSchedule?.is_delivery_ASAP_only === true;
      const refreshedRequiresScheduledDelivery = refreshedOrderData.deliveryNeeded === 'Yes'
        && !refreshedIsAsapOnly;
      if (refreshedRequiresScheduledDelivery && !refreshedRequiredInterval) {
        setOrderStatus(t('delivery_readiness_unavailable', 'No delivery interval satisfies the preparation-time requirement. Please revise your order.'));
        setCheckoutStep('cart');
        return;
      }
      const refreshedScheduleIsCustomerVisible = !simpleOrderPlacement
        && !refreshedIsAsapOnly
        && !(
          refreshedSchedule?.is_deliver_on_time === true
          && refreshedOrderData.deliveryInTime !== 'Yes'
        );
      if (refreshedScheduleIsCustomerVisible && !refreshedSelectedInterval && refreshedRequiredInterval) {
        setOrderData((previous) => ({
          ...previous,
          date: refreshedRequiredInterval.dateValue,
          deliveryInterval: refreshedRequiredInterval.label,
          time: null,
        }));
        setFulfilmentAdjustmentNotice(t(
          'fulfilment_selection_adjusted_after_refresh',
          'The fulfilment schedule changed. The first valid interval was selected; please review it before submitting.',
        ));
        setCheckoutStep('checkout');
        return;
      }
      let submissionOrderData = refreshedOrderData;
      if (refreshedIsAsapOnly) {
        submissionOrderData = {
          ...refreshedOrderData,
          date: refreshedRequiredInterval?.dateValue ?? null,
          deliveryInTime: 'No',
          time: null,
          deliveryInterval: refreshedRequiredInterval?.label ?? null,
        };
        setOrderData(submissionOrderData);
      } else if (refreshedRequiredInterval) {
        const shouldSubmitExactTime = !simpleOrderPlacement
          && refreshedSchedule?.is_deliver_on_time === true
          && refreshedOrderData.deliveryInTime === 'Yes';
        const refreshedExactTimeOptions = shouldSubmitExactTime
          ? getCandidateExactTimeOptions(refreshedRequiredInterval, refreshedResolution.readinessTime)
          : [];
        const selectedExactDateTime = shouldSubmitExactTime
          && isExactTimeOptionAvailable(refreshedRequiredInterval, refreshedOrderData.time, refreshedResolution.readinessTime)
          ? getExactDateTimeForCandidate(refreshedRequiredInterval, refreshedOrderData.time ?? '')
          : null;
        if (shouldSubmitExactTime && !selectedExactDateTime) {
          const firstValidExactTime = refreshedExactTimeOptions[0] ?? null;
          if (!firstValidExactTime) {
            setOrderStatus(t(
              'delivery_exact_time_invalid',
              'Choose a delivery time inside the selected interval and after the order readiness time.',
            ));
            setOrderErrors((previous) => ({ ...previous, deliveryTime: true }));
            setCheckoutStep('checkout');
            return;
          }
          setOrderData((previous) => ({
            ...previous,
            date: refreshedRequiredInterval.dateValue,
            deliveryInterval: refreshedRequiredInterval.label,
            deliveryInTime: 'Yes',
            time: firstValidExactTime,
          }));
          setFulfilmentAdjustmentNotice(t(
            'fulfilment_selection_adjusted_after_refresh',
            'The fulfilment schedule changed. The first valid interval was selected; please review it before submitting.',
          ));
          setCheckoutStep('checkout');
          return;
        }
        submissionOrderData = shouldSubmitExactTime && selectedExactDateTime
          ? {
              ...refreshedOrderData,
              date: formatDeliveryDateValue(selectedExactDateTime),
              deliveryInTime: 'Yes',
              time: formatTimeInputValue(selectedExactDateTime),
              deliveryInterval: null,
            }
          : {
              ...refreshedOrderData,
              date: refreshedRequiredInterval.dateValue,
              deliveryInTime: 'No',
              time: null,
              deliveryInterval: refreshedRequiredInterval.label,
            };
        setOrderData(submissionOrderData);
      } else if (refreshedOrderData.deliveryNeeded !== 'Yes') {
        submissionOrderData = {
          ...refreshedOrderData,
          date: null,
          deliveryInTime: 'No',
          time: null,
          deliveryInterval: null,
        };
      }
      const submissionDeliveryCoordinates = simpleOrderPlacement
        ? refreshedSimpleDefaults.pickupPoint
        : deliveryMapContext.effectiveCoordinates;
      const normalizedOrderDataDraft = buildOrderDataForSubmission(
        submissionOrderData,
        sessionData,
        userData,
        refreshedPartnerData,
        submissionDeliveryCoordinates,
      );
      const normalizedOrderData = simpleOrderPlacement
        ? applySimpleOrderPlacementDefaults(normalizedOrderDataDraft, refreshedSimpleDefaults)
        : normalizedOrderDataDraft;
      if (simpleOrderPlacement) {
        // The simplified form never exposes loyalty data, so a stale stored
        // value must not reappear in the payload even when the partner enables it.
        delete normalizedOrderData.loyalty_card_number;
      }
      const finalReceiptSummary = buildReceiptSummary(
        cartValidation.validCart,
        refreshedPartnerData,
        normalizedOrderData,
        language,
        promoData,
        deliveryCostState.status === 'success' && submissionDeliveryCoordinates
          ? deliveryCostState.cost
          : null,
        submissionDeliveryCoordinates,
      );
      const finalEcommercePayload = buildMenuDataLayerEcommercePayload(
        cartValidation.validCart,
        refreshedPartnerData.menu,
        menuTranslations,
        language,
        activePriceLevel,
        finalReceiptSummary.total,
      );
      pushDataLayerEvent(DATA_LAYER_EVENT_NAMES.orderConfirmClick, finalEcommercePayload);
      const payload = await submitOrderRequest({
        cart_items: cartValidation.validCart,
        order_data: normalizedOrderData,
      });
      const receiptText = String(payload?.receipt ?? '').trim();

      if (!receiptText) {
        throw new Error('Order response did not include a receipt.');
      }

      setReceiptNotice({
        type: 'success',
        message: t(
          'order_receipt_success_message',
          'Your order has been delivered to {food_maker_name}.'
        ).replaceAll('{food_maker_name}', partnerName),
      });
      setReceiptHtml(`<p>${receiptText.replace(/(\r\n|\r|\n)/g, '</p><p>')}</p>`);
      pushDataLayerEvent(DATA_LAYER_EVENT_NAMES.purchaseSuccess, finalEcommercePayload);
      setCheckoutStep('receipt');
      setShoppingCart([]);
      const storage = getCartStorage();
      storage[foodMakerKey] = [];
      saveCartStorage(storage);
    } catch (error) {
      console.error('Failed to submit order', error);
      setReceiptHtml('');
      /**
       * The support action exposes only the caught failure and operational
       * metadata. It resolves a validated Telegram destination separately so
       * an invalid partner value cannot become a navigable receipt link.
       */
      const fallbackErrorMessage = "We're sorry, something went wrong. Please contact the technical support team.";
      const translatedErrorMessage = t('order_receipt_error_message', fallbackErrorMessage);
      const supportUrl = resolveTelegramSupportUrl(partnerData?.support_contact_link, translatedErrorMessage);
      const backendOrigin = (() => {
        try {
          return new URL(backendUrl, window.location.origin).origin;
        } catch {
          return backendUrl;
        }
      })();
      const supportReport = buildTelegramSupportReport({
        error,
        timestamp: new Date(),
        foodMakerCode: foodMakerKey,
        location: activeDeliveryScheduleLocation
          || orderData.location
          || sessionData?.location
          || userData?.location
          || partnerData?.location
          || '',
        currentUrl: window.location.href,
        frontendOrigin: window.location.origin,
        backendOrigin,
        applicationVersion: formatVersion(APP_VERSION),
        pageVersion: formatVersion(PAGE_VERSIONS.menu),
        buildTime: BUILD_TIME,
        labels: {
          intro: t(
            'support_error_report_intro',
            'Hello, I have encountered a problem with the application. The error description is below:',
          ),
          consoleError: t('support_console_error', 'Console error'),
          error: t('support_error', 'Error'),
          httpStatus: t('support_http_status', 'HTTP status'),
          backendDetails: t('support_backend_details', 'Backend details'),
          stackTrace: t('support_stack_trace', 'Stack trace'),
          diagnosticContext: t('support_diagnostic_context', 'Diagnostic context'),
          timestampUtc: t('support_timestamp_utc', 'Timestamp (UTC)'),
          foodMaker: t('support_food_maker', 'Food maker'),
          location: t('support_location', 'Location'),
          route: t('support_route', 'Route'),
          frontendOrigin: t('support_frontend_origin', 'Frontend origin'),
          backendOrigin: t('support_backend_origin', 'Backend origin'),
          applicationVersion: t('support_application_version', 'Application version'),
          pageVersion: t('support_page_version', 'Menu page version'),
          buildTime: t('support_build_time', 'Build time'),
        },
      });
      const supportHref = supportUrl ? buildTelegramSupportHref(supportUrl, supportReport) : null;
      const visibleErrorMessage = supportHref
        ? stripTelegramSupportUrls(translatedErrorMessage) || fallbackErrorMessage
        : translatedErrorMessage;
      setReceiptNotice({
        type: 'error',
        message: visibleErrorMessage,
        supportHref,
      });
      setCheckoutStep('receipt');
    } finally {
      isSubmittingOrderRef.current = false;
      setIsSubmittingOrder(false);
    }
  };

  const renderPrice = (card: MenuCardData, size: string) => (
    <span className="text-success d-inline-flex align-items-center flex-wrap gap-1" style={{ fontSize: size, fontWeight: 700 }}>
      {card.hasMultipleVersions && (
        <span style={{ fontWeight: 600 }}>
          {t('more_with_arrow', '> ')}
        </span>
      )}
      <span>
        {card.price}
        {card.unitLabel ? ` / ${card.unitLabel}` : ''}
      </span>
    </span>
  );

  const renderUnavailableStatus = (compact = false) => (
    <span
      className={`badge rounded-pill ${compact ? '' : 'px-3 py-2'}`}
      style={{ backgroundColor: '#EEF6EF', color: '#1B7F3A', fontSize: compact ? '12px' : '13px', fontWeight: 700 }}
    >
      {t('sold_out', 'Sold out')}
    </span>
  );

  /**
   * Product-card basket counts are attached to the add button so the image
   * remains reserved for product inspection and the count sits beside the
   * action that changes it.
   */
  const renderAddButtonQuantityBadge = (quantityInCart: number) => {
    if (quantityInCart <= 0) {
      return null;
    }

    return (
      <span
        data-testid="add-to-cart-quantity-badge"
        className="position-absolute top-0 start-100 translate-middle d-inline-flex align-items-center justify-content-center rounded-circle text-white fw-bold border border-white"
        aria-hidden="true"
        style={{
          minWidth: '20px',
          height: '20px',
          padding: '0 5px',
          backgroundColor: '#DC3545',
          fontSize: '11px',
          lineHeight: 1,
          pointerEvents: 'none',
        }}
      >
        {quantityInCart}
      </span>
    );
  };

  const renderCardMeta = (card: MenuCardData, className = 'mt-2') => {
    const visibleAdditionalProperties = shouldShowProductParameters ? card.additionalProperties : [];
    const hasAdditionalProperties = visibleAdditionalProperties.length > 0;
    if (!hasAdditionalProperties && card.lowStockQuantity === null) {
      return null;
    }

    return (
      <div data-testid="menu-card-meta" className={`text-secondary ${className}`} style={{ fontSize: '12px', fontWeight: 500, lineHeight: 1.4 }}>
        {hasAdditionalProperties && visibleAdditionalProperties.join(' | ')}
        {hasAdditionalProperties && card.lowStockQuantity !== null && ' | '}
        {card.lowStockQuantity !== null && (
          <span className="fw-bold">
            {t('quantity_on_stock', 'Quantity on stock')}: {card.lowStockQuantity}
          </span>
        )}
      </div>
    );
  };

  /**
   * Product-level timing is operational information, so it remains visible in
   * every card layout even when optional product parameters are hidden.
   */
  const renderNextDelivery = (card: MenuCardData) => {
    if (!card.nextDeliveryText) {
      return null;
    }

    return (
      <div
        data-testid="menu-card-next-delivery"
        className={`small fw-semibold mt-2 ${card.isNextDeliveryUnavailable ? 'text-danger' : 'text-success'}`}
      >
        <FaClock className="me-1" aria-hidden="true" />
        {card.nextDeliveryText}
      </div>
    );
  };

  const bottomNavLabelStyle: React.CSSProperties = {
    fontSize: '10px',
    fontWeight: 700,
    letterSpacing: '0.08em',
  };

  const bottomNavIconSlotStyle: React.CSSProperties = {
    width: '42px',
    height: '42px',
  };

  /**
   * All bottom-navigation items share the same icon slot so the cart circle and
   * badge never change the baseline or vertical rhythm of the navigation row.
   */
  const renderBottomNavButton = ({
    onClick,
    ariaLabel,
    buttonClassName,
    iconContent,
    label,
    testId,
  }: {
    onClick: () => void;
    ariaLabel?: string;
    buttonClassName: string;
    iconContent: React.ReactNode;
    label: React.ReactNode;
    testId?: string;
  }) => (
    <button
      type="button"
      data-testid={testId}
      className={`btn btn-link text-decoration-none d-flex flex-column align-items-center justify-content-end p-0 flex-fill ${buttonClassName}`}
      onClick={onClick}
      aria-label={ariaLabel}
    >
      <span
        className="position-relative d-inline-flex align-items-center justify-content-center mb-1"
        style={bottomNavIconSlotStyle}
      >
        {iconContent}
      </span>
      <span style={bottomNavLabelStyle}>{label}</span>
    </button>
  );

  /**
   * Both language switchers use the same reload-based language change so the
   * header and sticky menu toolbar cannot drift into different selected states.
   */
  const renderLanguageDropdown = (testId: string) => (
    <Dropdown align="end" data-testid={testId}>
      <Dropdown.Toggle as={LanguageToggle} ariaLabel={t('open_language_selector', 'Open language selector')}>
        <FaGlobe style={{ fontSize: '18px' }} />
        <span style={{ fontSize: '14px', fontWeight: 700, lineHeight: 1 }}>{(language || 'en').toUpperCase()}</span>
      </Dropdown.Toggle>
      <Dropdown.Menu>
        {LANGUAGES.map((option) => (
          <Dropdown.Item
            key={option.code}
            data-testid={`${testId}-${option.code}`}
            active={language === option.code}
            onClick={() => setLanguage(option.code)}
          >
            {option.label}
          </Dropdown.Item>
        ))}
      </Dropdown.Menu>
    </Dropdown>
  );

  const scrollToMenuCategory = useCallback((categoryLabel: string, options: CategoryScrollOptions = {}) => {
    const anchorId = categoryAnchorIds[categoryLabel];
    const target = anchorId ? document.getElementById(anchorId) : null;
    if (!target) {
      return false;
    }

    const stickyHeight = menuStickyToolbarRef.current?.getBoundingClientRect().height ?? 0;
    const targetTop = target.getBoundingClientRect().top + window.scrollY - stickyHeight - 12;
    window.scrollTo({ top: Math.max(targetTop, 0), behavior: options.behavior ?? 'smooth' });

    if (options.updateHash !== false) {
      const nextUrl = new URL(window.location.href);
      nextUrl.hash = anchorId;
      window.history.pushState(null, '', nextUrl.toString());
    }

    return true;
  }, [categoryAnchorIds]);

  useEffect(() => {
    const anchorId = getCategoryAnchorIdFromHash(window.location.hash);
    if (!anchorId || hasAppliedInitialCategoryHashRef.current === anchorId) {
      return undefined;
    }

    const categoryLabel = categoryByAnchorId.get(anchorId);
    if (!categoryLabel) {
      return undefined;
    }

    hasAppliedInitialCategoryHashRef.current = anchorId;
    setSelectedCategory(categoryLabel);
    const timeoutId = window.setTimeout(() => {
      scrollToMenuCategory(categoryLabel, { updateHash: false, behavior: 'auto' });
    }, 0);

    return () => window.clearTimeout(timeoutId);
  }, [categoryByAnchorId, scrollToMenuCategory]);

  if (!sessionData || !partnerData) {
    const canSwitchBackend = canSwitchTestDomainBackendToProd();

    return (
      <div className="bg-light min-vh-100 d-flex align-items-center justify-content-center" style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
        <div className="text-center">
          {canSwitchBackend ? (
            <button
              type="button"
              className="btn p-0 border-0 bg-transparent mb-3"
              aria-label="Switch to production backend"
              title="Switch to production backend"
              onClick={switchTestDomainBackendToProd}
            >
              <Spinner animation="border" className="text-success" />
            </button>
          ) : (
            <Spinner animation="border" className="text-success mb-3" />
          )}
          <div className="text-muted">{t('loading_menu_data', 'Loading menu data...')}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-light min-vh-100" style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
      <Container fluid className="px-0 pb-5" style={{ maxWidth: '1320px' }}>
        <header className="d-flex justify-content-between align-items-center px-2 py-2 border-bottom bg-white">
          <div className="d-flex align-items-center gap-2">
            <div className="rounded-circle d-flex align-items-center justify-content-center overflow-hidden bg-white" style={{ width: '46px', height: '46px' }}>
              <img
                src={partnerLogo ?? '/Assets/images/logo_ifood.png'}
                alt={partnerLogo ? partnerName : t('ifood_logo', 'i-food logo')}
                className="w-100 h-100 object-fit-contain"
              />
            </div>
            <span className="text-success-emphasis" style={{ fontSize: '18px', fontWeight: 800, letterSpacing: '-0.02em' }}>
              {partnerName}
            </span>
          </div>

          <div className="d-flex align-items-center gap-2 text-success">
            {renderLanguageDropdown('page-header-language-switcher')}
            <a
              href="https://i-food.me"
              aria-label={t('ifood_logo', 'i-food logo')}
              className="d-inline-flex align-items-center justify-content-center text-decoration-none"
              style={{ width: '38px', height: '38px' }}
            >
              <img
                src="/Assets/images/logo_ifood.png"
                alt={t('ifood_logo', 'i-food logo')}
                style={{ width: '38px', height: '38px', objectFit: 'contain' }}
              />
            </a>
          </div>
        </header>

        <main className="px-2 pt-3 pb-5">
          <Card className="border-0 rounded-5 mb-3" style={{ boxShadow: SECTION_SHADOW }}>
            <Card.Body className="p-3">
              <div className="row g-4 align-items-start">
                <div className="col-12 col-lg-5">
                  <button
                    type="button"
                    className="btn p-0 w-100 text-start border-0"
                    onClick={() => setIsGalleryOpen(true)}
                    aria-label={t('open_gallery', 'Open photo gallery')}
                  >
                    <div
                      className="position-relative rounded-5 overflow-hidden mb-3 mx-auto mx-lg-0"
                      style={{ aspectRatio: '1 / 1', maxWidth: 'min(100%, 420px)' }}
                    >
                      <PartnerPhotoGallery photos={previewPartnerPhotos} totalCount={allPartnerPhotos.length} alt={partnerName} />
                    </div>
                  </button>
                </div>
                <div className="col-12 col-lg-7">
                  <div
                    className="text-secondary mb-3"
                    style={{ fontSize: '14px', fontWeight: 400, lineHeight: 1.5 }}
                    dangerouslySetInnerHTML={{ __html: partnerDescriptionHtml }}
                  />

                  <div className="border-top border-bottom py-3 mb-3">
                    <div className="d-flex flex-wrap gap-2 justify-content-end align-items-center mb-3">
                      {partnerStatusItems.map((status) => (
                        <Badge
                          key={status.key}
                          pill
                          className={`${status.toneClassName} text-uppercase`}
                          style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.08em' }}
                        >
                          {status.label}
                        </Badge>
                      ))}
                    </div>
                    <div className="d-flex align-items-start mb-2">
                      <FaClock className="text-success me-3 mt-1 flex-shrink-0" />
                      <div className="flex-grow-1" dangerouslySetInnerHTML={{ __html: partnerScheduleHtml }} />
                    </div>
                    <div className="d-flex align-items-center mb-2">
                      <FaPhoneAlt className="text-success me-3" />
                      <span>{partnerPhone}</span>
                    </div>
                    <div className="d-flex align-items-center">
                      <FaMapMarkerAlt className="text-success me-3" />
                      <span>{partnerAddress}</span>
                    </div>
                  </div>

                  {socialLinks.length > 0 && (
                    <div className="d-flex gap-2">
                      {socialLinks.map((link) => (
                        <a
                          key={link.key}
                          href={link.href}
                          target="_blank"
                          rel="noreferrer"
                          className="btn btn-light rounded-circle border d-flex align-items-center justify-content-center"
                          style={{ width: '36px', height: '36px' }}
                          aria-label={link.label}
                        >
                          {link.icon}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </Card.Body>
          </Card>

          {SHOW_PROMO_SLIDER && (
            <Card className="border-0 rounded-5 mb-3" style={{ boxShadow: SECTION_SHADOW }}>
              <Card.Body className="p-3">
                <Carousel indicators controls={false} interval={3500} className="rounded-5 overflow-hidden">
                  {promoSlides.map((slide) => (
                    <Carousel.Item key={slide.title}>
                      <div className="position-relative rounded-5 overflow-hidden" style={{ height: '130px' }}>
                        <img src={slide.imageUrl} alt={slide.title} className="w-100 h-100 object-fit-cover" />
                        <div className="position-absolute top-0 start-0 end-0 bottom-0 p-3 d-flex flex-column justify-content-center" style={{ background: 'linear-gradient(90deg, rgba(0,0,0,0.75), transparent)' }}>
                          <h3 className="text-white mb-1" style={{ fontSize: '20px', fontWeight: 700 }}>{slide.title}</h3>
                          <p className="text-white-50 mb-2" style={{ fontSize: '12px', fontWeight: 400 }}>{slide.subtitle}</p>
                          <button type="button" className="btn btn-warning btn-sm rounded-pill align-self-start text-uppercase" style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.06em' }}>
                            {slide.buttonText}
                          </button>
                        </div>
                      </div>
                    </Carousel.Item>
                  ))}
                </Carousel>
              </Card.Body>
            </Card>
          )}
          <Card id="menu-section" data-testid="menu-loaded" className="border-0 rounded-5" style={{ boxShadow: SECTION_SHADOW }}>
            <Card.Body className="p-3">
              <div
                ref={menuStickyToolbarRef}
                data-testid="menu-sticky-toolbar"
                className="position-sticky bg-white border-bottom mb-3"
                style={{
                  top: 0,
                  zIndex: 1020,
                  margin: '-1rem -1rem 1rem',
                  padding: '1rem 1rem 0.5rem',
                  boxShadow: '0 0.25rem 0.75rem rgba(30, 30, 30, 0.06)',
                }}
              >
                <div className="d-flex justify-content-between align-items-center gap-3 mb-3 flex-wrap">
                  <button
                    type="button"
                    data-testid="menu-catalog-trigger"
                    className="btn btn-link p-0 border-0 bg-transparent text-body text-decoration-none d-flex align-items-center gap-2 flex-shrink-0"
                    onClick={() => setIsCategoryPanelOpen(true)}
                    aria-label={t('open_categories_catalog', 'Open categories catalog')}
                  >
                    <span
                      data-testid="menu-catalog-trigger-icon"
                      className="text-success d-inline-flex align-items-center justify-content-center flex-shrink-0"
                      style={{ fontSize: '20px', lineHeight: 1 }}
                      aria-hidden="true"
                    >
                      <FaTh />
                    </span>
                    <span
                      data-testid="menu-catalog-trigger-title"
                      className="fw-bold"
                      style={{ fontSize: '20px' }}
                    >
                      {menuSectionTitle}
                    </span>
                  </button>
                  <div className="d-flex align-items-center justify-content-end gap-2" style={{ minWidth: 0 }}>
                    <div className="btn-group bg-secondary-subtle p-1 rounded-pill flex-shrink-0" data-testid="menu-view-switcher">
                      <button
                        type="button"
                        className={`btn btn-sm rounded-circle d-flex align-items-center justify-content-center ${viewMode === 'compact' ? 'btn-light text-success' : 'btn-link text-secondary text-decoration-none'}`}
                        onClick={() => setViewMode('compact')}
                        title={t('single_column_mode', 'Single column mode')}
                        aria-label={t('single_column_mode', 'Single column mode')}
                      >
                        <LayoutModeIcon mode="compact" active={viewMode === 'compact'} />
                      </button>
                      <button
                        type="button"
                        className={`btn btn-sm rounded-circle d-flex align-items-center justify-content-center ${viewMode === 'grid' ? 'btn-light text-success' : 'btn-link text-secondary text-decoration-none'}`}
                        onClick={() => setViewMode('grid')}
                        title={t('two_columns_mode', 'Two columns mode')}
                        aria-label={t('two_columns_mode', 'Two columns mode')}
                      >
                        <LayoutModeIcon mode="grid" active={viewMode === 'grid'} />
                      </button>
                      <button
                        type="button"
                        className={`btn btn-sm rounded-circle d-flex align-items-center justify-content-center ${viewMode === 'list' ? 'btn-light text-success' : 'btn-link text-secondary text-decoration-none'}`}
                        onClick={() => setViewMode('list')}
                        title={t('list_mode', 'List mode')}
                        aria-label={t('list_mode', 'List mode')}
                      >
                        <LayoutModeIcon mode="list" active={viewMode === 'list'} />
                      </button>
                    </div>
                    <div className="flex-shrink-0 text-success">
                      {renderLanguageDropdown('menu-toolbar-language-switcher')}
                    </div>
                  </div>
                </div>

                <div data-testid="menu-category-row" className="d-flex align-items-center gap-2 overflow-auto pb-2">
                  {categoryTabs.map((tab) => (
                    <button
                      key={tab}
                      type="button"
                      data-testid="menu-category-tab"
                      data-category-anchor={categoryAnchorIds[tab]}
                      className={`btn rounded-pill text-nowrap flex-shrink-0 ${selectedCategory === tab ? 'btn-success px-4' : 'btn-light text-secondary px-4'}`}
                      style={{ fontSize: '14px', fontWeight: selectedCategory === tab ? 700 : 600 }}
                      onClick={() => {
                        setSelectedCategory(tab);
                        scrollToMenuCategory(tab);
                      }}
                    >
                      {tab}
                    </button>
                  ))}
                </div>
              </div>

              <div className="d-flex align-items-center gap-2 mb-3">
                <button
                  type="button"
                  data-testid="menu-filter-button"
                  className="btn btn-success rounded-circle d-flex align-items-center justify-content-center order-0 flex-shrink-0"
                  style={{
                    width: '46px',
                    height: '46px',
                    minWidth: '46px',
                    minHeight: '46px',
                    padding: 0,
                    aspectRatio: '1 / 1',
                  }}
                  onClick={() => setIsFilterModalOpen(true)}
                  aria-label={t('open_advanced_filters', 'Open advanced filters')}
                >
                  <FaSlidersH />
                </button>
                <InputGroup>
                  <Form.Control
                    type="text"
                    className="bg-secondary-subtle border-0 rounded-pill"
                    placeholder={searchPlaceholder}
                    value={searchValue}
                    onChange={(event) => setSearchValue(event.target.value)}
                    aria-label={searchPlaceholder}
                  />
                </InputGroup>
                {hasActiveSearchOrFilters && (
                  <button
                    type="button"
                    className="btn btn-outline-secondary rounded-pill px-3 flex-shrink-0"
                    onClick={() => {
                      setSearchValue('');
                      setSelectedFilters({});
                    }}
                  >
                    {t('reset', 'Reset')}
                  </button>
                )}
              </div>

              {viewMode === 'grid' && (
                <div className="d-flex flex-column gap-4">
                  {groupedCards.map((group) => (
                    <section key={group.categoryLabel} id={group.anchorId} data-category-anchor={group.anchorId}>
                      <h4
                        className="mb-3"
                        data-testid="menu-category-heading"
                        data-category-anchor={group.anchorId}
                        style={{ fontSize: '18px', fontWeight: 700 }}
                      >
                        {group.categoryLabel}
                      </h4>
                      <div className="row g-3">
                        {group.cards.map((card) => {
                          const quantityInCart = getQuantityInCart(card.productKey);
                          return (
                            <div key={card.productKey} className="col-6 col-lg-4 col-xl-3">
                              <Card data-testid="menu-product-card" data-product-key={card.productKey} className="h-100 border-0 rounded-4 overflow-hidden" style={{ boxShadow: PRODUCT_CARD_SHADOW }}>
                                <div data-testid="menu-product-image-area" className="position-relative">
                                  <button
                                    type="button"
                                    className="btn p-0 border-0 w-100 d-block bg-transparent"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      handleOpenProductImage(card.fullImageUrl, card.name);
                                    }}
                                    aria-label={t('open_product_photo', 'Open product photo')}
                                  >
                                    <ProductImageWithFallback
                                      src={card.displayImageUrl}
                                      fallbackSrc={partnerLogoFallbackUrl}
                                      alt={card.name}
                                      className="w-100"
                                      style={{ aspectRatio: '1 / 1', objectFit: 'contain', backgroundColor: '#ffffff', cursor: 'zoom-in' }}
                                      loading="lazy"
                                      decoding="async"
                                    />
                                  </button>
                                </div>
                                <Card.Body className="d-flex flex-column" onClick={() => handleCardContentClick(card.productKey)} style={{ cursor: 'pointer' }}>
                                  <Card.Title className="mb-1" style={{ fontSize: '16px', fontWeight: 700, whiteSpace: 'normal', overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                                    {card.name}
                                  </Card.Title>
                                  <ExpandableDescription
                                    html={card.description}
                                    textStyle={{ fontSize: '13px', fontWeight: 400, whiteSpace: 'normal', overflowWrap: 'anywhere', wordBreak: 'break-word', color: '#6B7280' }}
                                    buttonLabel={t('see_more', 'See More')}
                                    clampLines={3}
                                  />
                                  {renderCardMeta(card)}
                                  {renderNextDelivery(card)}
                                  <div className="mt-auto pt-2 d-flex justify-content-between align-items-center">
                                    {card.isPurchasable ? renderPrice(card, '14px') : renderUnavailableStatus(true)}
                                    {card.isPurchasable && !partnerIsTemporarilyStopped && (
                                      <button
                                        type="button"
                                        data-testid="add-to-cart-button"
                                        data-product-key={card.productKey}
                                        className="btn btn-success rounded-circle position-relative d-inline-flex align-items-center justify-content-center border-0"
                                        style={{ width: '36px', height: '36px' }}
                                        aria-label={t('add_to_cart', 'Add to cart')}
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          handleCardAdd(card.productKey);
                                        }}
                                      >
                                        <span className="fw-bold" style={{ fontSize: '1.2rem', lineHeight: 1 }}>+</span>
                                        {renderAddButtonQuantityBadge(quantityInCart)}
                                      </button>
                                    )}
                                  </div>
                                </Card.Body>
                              </Card>
                            </div>
                          );
                        })}
                      </div>
                    </section>
                  ))}
                </div>
              )}

              {viewMode === 'list' && (
                <div className="d-flex flex-column gap-4">
                  {groupedCards.map((group) => (
                    <section key={group.categoryLabel} id={group.anchorId} data-category-anchor={group.anchorId}>
                      <h4
                        className="mb-3"
                        data-testid="menu-category-heading"
                        data-category-anchor={group.anchorId}
                        style={{ fontSize: '18px', fontWeight: 700 }}
                      >
                        {group.categoryLabel}
                      </h4>
                      <div className="d-flex flex-column gap-3">
                        {group.cards.map((card) => {
                          const quantityInCart = getQuantityInCart(card.productKey);
                          return (
                    <Card key={card.productKey} data-testid="menu-product-card" data-product-key={card.productKey} className="border-0 rounded-5 p-2" style={{ boxShadow: PRODUCT_CARD_SHADOW }}>
                      <div className="d-flex align-items-center gap-3" onClick={() => handleCardContentClick(card.productKey)} style={{ cursor: 'pointer' }}>
                        <div data-testid="menu-product-image-area" className="position-relative flex-shrink-0">
                          <button
                            type="button"
                            className="btn p-0 border-0 bg-transparent rounded-4"
                            onClick={(event) => {
                              event.stopPropagation();
                              handleOpenProductImage(card.fullImageUrl, card.name);
                            }}
                            aria-label={t('open_product_photo', 'Open product photo')}
                          >
                            <ProductImageWithFallback
                              src={card.displayImageUrl}
                              fallbackSrc={partnerLogoFallbackUrl}
                              alt={card.name}
                              className="rounded-4 d-block"
                              style={{ width: '110px', height: '110px', objectFit: 'contain', backgroundColor: '#ffffff', cursor: 'zoom-in' }}
                              loading="lazy"
                              decoding="async"
                            />
                          </button>
                        </div>
                        <div className="flex-grow-1 d-flex flex-column justify-content-between" style={{ minHeight: '110px', minWidth: 0 }}>
                          <div>
                            <div style={{ fontSize: '16px', fontWeight: 700, whiteSpace: 'normal', overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                              {card.name}
                            </div>
                            <ExpandableDescription
                              html={card.description}
                              textStyle={{ fontSize: '14px', fontWeight: 400, whiteSpace: 'normal', overflowWrap: 'anywhere', wordBreak: 'break-word', color: '#6B7280' }}
                              buttonLabel={t('see_more', 'See More')}
                            />
                            {renderCardMeta(card)}
                            {renderNextDelivery(card)}
                          </div>
                          <div className="d-flex justify-content-between align-items-center mt-2">
                            <div className="d-flex align-items-center gap-2">
                              {card.isPurchasable ? renderPrice(card, '16px') : renderUnavailableStatus()}
                            </div>
                            {card.isPurchasable && !partnerIsTemporarilyStopped && (
                              <button
                                type="button"
                                data-testid="add-to-cart-button"
                                data-product-key={card.productKey}
                                className="btn btn-success rounded-pill position-relative d-inline-flex align-items-center justify-content-center border-0 px-3"
                                style={{ minWidth: '68px', height: '40px' }}
                                aria-label={t('add_to_cart', 'Add to cart')}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  handleCardAdd(card.productKey);
                                }}
                              >
                                <AddToCartGlyph cartSize={17} plusSize={15} />
                                {renderAddButtonQuantityBadge(quantityInCart)}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </Card>
                  )})}
                      </div>
                    </section>
                  ))}
                </div>
              )}

              {viewMode === 'compact' && (
                <div className="d-flex flex-column gap-4">
                  {groupedCards.map((group) => (
                    <section key={group.categoryLabel} id={group.anchorId} data-category-anchor={group.anchorId}>
                      <h4
                        className="mb-3"
                        data-testid="menu-category-heading"
                        data-category-anchor={group.anchorId}
                        style={{ fontSize: '18px', fontWeight: 700 }}
                      >
                        {group.categoryLabel}
                      </h4>
                      <div className="d-flex flex-column gap-4">
                        {group.cards.map((card) => {
                          const quantityInCart = getQuantityInCart(card.productKey);
                          return (
                    <Card key={card.productKey} data-testid="menu-product-card" data-product-key={card.productKey} className="border-0 rounded-5 overflow-hidden p-2" style={{ boxShadow: PRODUCT_CARD_SHADOW }}>
                      <div data-testid="menu-product-image-area" className="position-relative">
                        <button
                          type="button"
                          className="btn p-0 border-0 w-100 d-block bg-transparent"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleOpenProductImage(card.fullImageUrl, card.name);
                          }}
                          aria-label={t('open_product_photo', 'Open product photo')}
                        >
                          <ProductImageWithFallback
                            src={card.displayImageUrl}
                            fallbackSrc={partnerLogoFallbackUrl}
                            alt={card.name}
                            className="w-100 rounded-5"
                            style={{ height: '240px', objectFit: 'contain', backgroundColor: '#ffffff', cursor: 'zoom-in' }}
                            loading="lazy"
                            decoding="async"
                          />
                        </button>
                      </div>
                      <Card.Body className="px-2 pt-3 pb-2" onClick={() => handleCardContentClick(card.productKey)} style={{ cursor: 'pointer' }}>
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <Card.Title className="mb-0 pe-2" style={{ fontSize: '16px', fontWeight: 700, whiteSpace: 'normal', overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                            {card.name}
                          </Card.Title>
                        </div>
                        <ExpandableDescription
                          html={card.description}
                          textStyle={{ fontSize: '14px', fontWeight: 400, lineHeight: 1.45, whiteSpace: 'normal', overflowWrap: 'anywhere', wordBreak: 'break-word', color: '#6B7280' }}
                          buttonLabel={t('see_more', 'See More')}
                        />
                        {renderCardMeta(card, 'mt-2 mb-3')}
                        {renderNextDelivery(card)}
                        <div className="d-flex justify-content-between align-items-center">
                          {card.isPurchasable ? renderPrice(card, '16px') : renderUnavailableStatus()}
                          {card.isPurchasable && !partnerIsTemporarilyStopped && (
                            <button
                              type="button"
                              data-testid="add-to-cart-button"
                              data-product-key={card.productKey}
                              className="btn btn-success rounded-pill position-relative d-inline-flex align-items-center justify-content-center border-0 px-3"
                              style={{ minWidth: '72px', height: '42px' }}
                              aria-label={t('add_to_cart', 'Add to cart')}
                              onClick={(event) => {
                                event.stopPropagation();
                                handleCardAdd(card.productKey);
                              }}
                            >
                              <AddToCartGlyph cartSize={18} plusSize={16} />
                              {renderAddButtonQuantityBadge(quantityInCart)}
                            </button>
                          )}
                        </div>
                      </Card.Body>
                    </Card>
                  )})}
                      </div>
                    </section>
                  ))}
                </div>
              )}
            </Card.Body>
          </Card>
          {orderStatus && (
            <Alert variant="warning" className="mt-3 mb-0">{orderStatus}</Alert>
          )}
        </main>
        <Modal show={isFilterModalOpen} onHide={() => setIsFilterModalOpen(false)} centered contentClassName="border-0 rounded-4">
          <Modal.Header closeButton className="border-0 pb-1" style={{ backgroundColor: '#FFFFFF', borderTopLeftRadius: '1rem', borderTopRightRadius: '1rem' }}>
            <Modal.Title style={{ fontSize: '20px', fontWeight: 700, color: '#1E1E1E' }}>{t('filters', 'Filters')}</Modal.Title>
          </Modal.Header>
          <Modal.Body className="pt-0" style={{ backgroundColor: '#F7F8F5' }}>
            {availableFilterAttributes.length === 0 ? (
              <div className="text-secondary" style={{ fontSize: '14px', fontWeight: 400, color: '#6B7280' }}>
                {t('no_advanced_filters', 'No advanced filters are available for this menu.')}
              </div>
            ) : (
              <div className="d-flex flex-column gap-3" style={{ maxHeight: '55vh', overflowY: 'auto' }}>
                {availableFilterAttributes.map((attribute) => (
                  <div key={attribute.key} className="bg-white rounded-4 p-3 shadow-sm">
                    <div className="mb-2 text-uppercase text-secondary" style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.08em', color: '#6B7280' }}>
                      {attribute.label}
                    </div>
                    <div className="d-flex flex-wrap gap-2">
                      <button
                        type="button"
                        className="btn btn-sm rounded-pill"
                        style={
                          !selectedFilters[attribute.key]
                            ? { backgroundColor: '#1B7F3A', color: '#FFFFFF', borderColor: '#1B7F3A', fontSize: '14px', fontWeight: 600 }
                            : { backgroundColor: '#EEF6EF', color: '#1E1E1E', borderColor: '#EEF6EF', fontSize: '14px', fontWeight: 500 }
                        }
                        onClick={() => setSelectedFilters((prev) => ({ ...prev, [attribute.key]: undefined }))}
                      >
                        {t('any', 'Any')}
                      </button>
                      {attribute.options.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          className="btn btn-sm rounded-pill"
                          style={
                            selectedFilters[attribute.key] === option.value
                              ? { backgroundColor: '#1B7F3A', color: '#FFFFFF', borderColor: '#1B7F3A', fontSize: '14px', fontWeight: 600 }
                              : { backgroundColor: '#EEF6EF', color: '#1E1E1E', borderColor: '#EEF6EF', fontSize: '14px', fontWeight: 500 }
                          }
                          onClick={() => setSelectedFilters((prev) => ({ ...prev, [attribute.key]: option.value }))}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Modal.Body>
          <Modal.Footer className="border-0 pt-0 d-flex justify-content-between" style={{ backgroundColor: '#F7F8F5', borderBottomLeftRadius: '1rem', borderBottomRightRadius: '1rem' }}>
            <Button
              variant="light"
              onClick={() => setSelectedFilters({})}
              className="rounded-pill px-3"
              style={{ backgroundColor: '#EEF6EF', color: '#1E1E1E', borderColor: '#EEF6EF', fontSize: '14px', fontWeight: 600 }}
            >
              {t('reset', 'Reset')}
            </Button>
            <Button
              variant="success"
              onClick={() => setIsFilterModalOpen(false)}
              className="rounded-pill px-3"
              style={{ backgroundColor: '#1B7F3A', borderColor: '#1B7F3A', color: '#FFFFFF', fontSize: '14px', fontWeight: 700 }}
            >
              {t('apply', 'Apply')}
            </Button>
          </Modal.Footer>
        </Modal>

        <Offcanvas data-testid="menu-category-panel" show={isCategoryPanelOpen} onHide={() => setIsCategoryPanelOpen(false)} placement="start" className="border-0" style={{ maxWidth: '360px' }}>
          <Offcanvas.Header closeButton className="border-bottom">
            <Offcanvas.Title style={{ fontSize: '20px', fontWeight: 700 }}>{t('catalog', 'Catalog')}</Offcanvas.Title>
          </Offcanvas.Header>
          <Offcanvas.Body className="p-0">
            <div className="overflow-auto" style={{ maxHeight: '100%' }}>
              {categoryPanelItems.map((category) => (
                <button
                  key={category.key}
                  type="button"
                  data-testid="menu-category-panel-item"
                  data-category-anchor={categoryAnchorIds[category.label]}
                  className={`btn w-100 text-start d-flex align-items-center gap-3 rounded-0 border-bottom ${selectedCategory === category.label ? 'bg-success-subtle' : 'bg-white'}`}
                  onClick={() => {
                    setSelectedCategory(category.label);
                    scrollToMenuCategory(category.label);
                    setIsCategoryPanelOpen(false);
                  }}
                  style={{ padding: '12px 16px' }}
                >
                  <ProductImageWithFallback
                    src={category.displayImageUrl || DEFAULT_CATEGORY_IMAGE}
                    fallbackSrc={partnerLogoFallbackUrl}
                    alt={category.label}
                    className="rounded-3"
                    style={{ width: '56px', height: '56px', objectFit: 'contain', backgroundColor: '#ffffff' }}
                    loading="lazy"
                    decoding="async"
                  />
                  <span style={{ fontSize: '16px', fontWeight: 600 }}>
                    {category.label} ({category.count})
                  </span>
                </button>
              ))}
            </div>
          </Offcanvas.Body>
        </Offcanvas>

        <Modal show={isGalleryOpen} onHide={() => setIsGalleryOpen(false)} centered size="lg" contentClassName="border-0 rounded-4 overflow-hidden">
          <Modal.Header closeButton className="border-0">
            <Modal.Title>{partnerName}</Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-0 bg-black">
            <Carousel indicators={allPartnerPhotos.length > 1} controls={allPartnerPhotos.length > 1} interval={null}>
              {allPartnerPhotos.map((photo) => (
                <Carousel.Item key={photo}>
                  <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '60vh' }}>
                    <img src={photo} alt={partnerName} className="w-100" style={{ maxHeight: '75vh', objectFit: 'contain' }} />
                  </div>
                </Carousel.Item>
              ))}
            </Carousel>
          </Modal.Body>
        </Modal>

        <Modal
          show={Boolean(productImagePreview)}
          onHide={() => setProductImagePreview(null)}
          centered
          contentClassName="border-0 rounded-4 overflow-hidden"
        >
          <Modal.Header closeButton className="border-0">
            <Modal.Title>{productImagePreview?.alt ?? t('product_photo', 'Product photo')}</Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-0 text-center bg-white">
            {productImagePreview && (
              <div className="d-inline-block">
                <ProductImageWithFallback
                  src={productImagePreview.src}
                  fallbackSrc={partnerLogoFallbackUrl}
                  alt={productImagePreview.alt}
                  className="d-block mx-auto"
                  style={{ width: 'auto', height: 'auto', maxWidth: '100%', maxHeight: '75vh', objectFit: 'contain', cursor: 'zoom-out' }}
                  onClick={() => setProductImagePreview(null)}
                />
              </div>
            )}
          </Modal.Body>
        </Modal>

        <ProductDetailModal
          isOpen={isOptionsOpen}
          onClose={() => {
            setIsOptionsOpen(false);
            setOptionsItem(null);
          }}
          item={optionsItem}
          menu={menu}
          language={language}
          priceLevel={activePriceLevel}
          logoUrl={partnerLogo}
          translations={menuTranslations}
          labels={{
            chooseVersion: t('choose_version', 'Choose a version'),
            addExtras: t('add_extras', 'Add extras'),
            buildCombo: t('build_your_combo', 'Build your combo'),
            quantity: t('quantity', 'Quantity'),
            addToCart: t('add_to_cart', 'Add to cart'),
            cancel: t('cancel', 'Cancel'),
            selectRequired: t('select_required_combo', 'Select at least one option in each combo section.'),
          }}
        onConfirm={(cartItem) => {
          if (!optionsItem || partnerIsTemporarilyStopped) {
            return;
          }
          addCartItem(optionsItem, cartItem);
          }}
        />

        <CheckoutModal
          isOpen={isCartOpen}
          onClose={() => {
            setIsCartOpen(false);
            setCheckoutStep('cart');
            if (checkoutStep === 'receipt') {
              setReceiptHtml('');
              setReceiptNotice(null);
            }
          }}
          step={checkoutStep}
          onStepChange={(step) => {
            if (step === 'checkout' && checkoutStep === 'cart') {
              pushDataLayerEvent(
                DATA_LAYER_EVENT_NAMES.checkoutStart,
                buildCurrentDataLayerPayload(shoppingCart, receiptSummary.total)
              );
            }
            if (step === 'confirm') {
              handleProceedToReview();
              return;
            }
            setCheckoutStep(step);
          }}
          shoppingCart={shoppingCart}
          onUpdateCart={handleUpdateCart}
          menu={menu}
          partnerData={partnerData ?? null}
          isCashlessTerminalAvailable={hasCashlessTerminalForPartnerLocation(
            partnerData ?? null,
            activeDeliveryScheduleLocation,
          )}
          isDeliveryUnavailableNow={isDeliveryUnavailableNow}
          deliveryScheduleSelection={deliveryScheduleSelection}
          language={language}
          priceLevel={activePriceLevel}
          effectiveOrderChannel={effectiveOrderChannel}
          shouldForcePhoneContact={shouldForcePhoneContact}
          shouldUsePhoneOnlyTelegramContact={shouldUsePhoneOnlyTelegramContact}
          simpleOrderPlacement={simpleOrderPlacement}
          orderData={orderData}
          onUpdateOrderData={setOrderData}
          deliveryMapContext={deliveryMapContext}
          orderErrors={orderErrors}
          promoCode={promoCode}
          onPromoCodeChange={setPromoCode}
          promoData={promoData}
          onPromoDataChange={setPromoData}
          promoMessage={promoMessage}
          onPromoMessageChange={setPromoMessage}
          isApplyingPromo={isApplyingPromo}
          onApplyingPromoChange={setIsApplyingPromo}
          onConfirmOrder={handleConfirmOrder}
          isSubmittingOrder={isSubmittingOrder}
          receiptHtml={receiptHtml}
          receiptNotice={receiptNotice}
          receiptSummary={receiptSummary}
          deliveryCostState={deliveryCostState}
          orderFlowWarning={effectiveOrderFlowWarning}
          shouldScrollToOrderFlowWarning={deliveryMinimumOrderWarningHtml != null}
          unavailableCartItemNames={unavailableCartItemNames}
          isOrderingBlocked={partnerIsTemporarilyStopped}
          isReviewBlocked={partnerIsTemporarilyStopped || isDeliveryBlockedByReadiness}
          isConfirmBlocked={!isDeliveryMinimumOrderMet}
          orderingBlockedNoticeHtml={temporaryStopPopupHtml}
          fulfilmentNotice={[fulfilmentAdjustmentNotice, fulfilmentNotice].filter(Boolean).join(' ') || null}
          labels={{
            yourCart: t('your_cart', 'Your cart'),
            checkout: t('checkout', 'Checkout'),
            reviewOrder: t('review_order', 'Review order'),
            receipt: t('receipt', 'Receipt'),
            cartEmpty: t('cart_empty', 'Your cart is empty.'),
            remove: t('remove', 'Remove'),
            quantity: t('quantity', 'Quantity'),
            unitPrice: t('unit_price', 'Unit'),
            enterPromo: t('enter_promo_apply', 'Enter a promo code to apply.'),
            promoApplied: t('promo_applied', 'Promo applied.'),
            promoInvalid: t('promo_invalid', 'Promo code is not valid.'),
            promoFailed: t('promo_failed', 'Failed to validate promo code.'),
            adds: t('adds', 'Adds'),
            combo: t('combo', 'Combo'),
            promoPlaceholder: t('set_promo_code', 'Enter promo code'),
            applying: t('applying', 'Applying...'),
            apply: t('apply', 'Apply'),
            subtotal: t('total', 'Subtotal'),
            proceedToCheckout: t('proceed_to_checkout', 'Proceed to Checkout'),
            continueShopping: t('continue_shopping', 'Continue Shopping'),
            yourName: t('your_name', 'Your name'),
            preferredMethod: t('preferred_connection_method', 'Preferred contact method'),
            selectCountryCode: t('select_the_country_code', 'Select country code'),
            phoneNumber: t('phone_call', 'Phone number'),
            phoneMethod: t('phone_call', 'Phone'),
            telegramUsername: t('telegram_username', '@username'),
            countryCodeOptions: [
              { value: '+382', label: t('code382', 'Montenegro +382') },
              { value: '+380', label: t('code380', 'Ukraine +380') },
              { value: '+7', label: t('code7', 'Russia +7') },
              { value: '+49', label: t('code49', 'Germany +49') },
              { value: '+1', label: t('code1', 'USA/Canada +1') },
              { value: '+44', label: t('code44', 'United Kingdom +44') },
              { value: '+90', label: t('code90', 'Turkey +90') },
            ],
            howToGet: t('how_to_get_order', 'How would you like to get your order?'),
            selfPickup: t('self_pickup', 'Self pickup'),
            delivery: t('delivery', 'Delivery'),
            deliveryPoint: t('delivery_point', 'Delivery point'),
            tapToChoose: t('tap_to_choose_delivery', 'Tap on the map to choose a delivery point.'),
            selectDeliveryPoint: t('select_delivery_point', 'Select delivery point'),
            expandMap: t('expand_map', 'Expand to full screen'),
            collapseMap: t('collapse', 'Collapse'),
            address: t('address', 'Address'),
            entrance: t('entrance', 'Entrance'),
            code: t('entrance_code', 'Code'),
            floor: t('floor', 'Floor'),
            apartment: t('apartment', 'Apartment'),
            paymentMethod: t('what_payment_method', 'Payment method'),
            cash: t('cash', 'Cash'),
            bankCard: t('card', 'Bank card'),
            needChange: t('need_change', 'Do you need change?'),
            yes: t('yes', 'Yes'),
            no: t('no', 'No'),
            changeAmount: t('change_amount', 'Amount for change'),
            deliveryDay: t('delivery_day', 'Delivery day'),
            deliveryInterval: t('delivery_interval', 'Delivery interval'),
            pickupDay: t('delivery_day', 'Pickup day'),
            pickupInterval: t('time_interval', 'Time interval'),
            deliveryTime: t('delivery_time', 'Delivery time'),
            whenReady: t('when_ready', 'When ready'),
            atTheTime: t('at_the_time', 'At the time'),
            selectTime: t('select_time', 'Select time'),
            noDeliveryTimesAvailable: t('no_delivery_times_available', 'No delivery times are available.'),
            noPickupIntervalsAvailable: t('no_pickup_intervals_available', 'No pickup interval is available. The preparation-ready time will be used.'),
            mapLoading: t('map_loading', 'Loading map...'),
            mapLoadFailed: t('map_failed_to_load', 'Map failed to load'),
            mapUnavailableDescription: t('map_temporarily_unavailable', 'The map tiles are temporarily unavailable. Please try again.'),
            retry: t('retry', 'Retry'),
            loyaltyCardNumber: t('loyalty_card_number', 'Loyalty card number'),
            enterLoyaltyCardNumber: t('enter_loyalty_card_number', 'Enter loyalty card number'),
            additionalInstructions: t('comment', 'Сomments'),
            anythingElse: t('order_comments_placeholder', 'What else is important for us to know regarding your order?'),
            orderDetails: t('order_details', 'Order details'),
            courierInstructions: t('courier_instructions', 'Courier instructions'),
            back: t('back', 'Back'),
            confirm: t('confirm', 'Confirm'),
            deliveryCost: t('courier_delivery_cost', 'Delivery cost'),
            calculatingDeliveryCost: t('calculating_delivery_cost', 'Calculating...'),
            deliveryCostFailed: t('delivery_cost_failed', 'Failed to calculate delivery cost.'),
            deliveryCostMissingData: t('delivery_cost_missing_data', 'Delivery cost cannot be calculated without partner and delivery coordinates.'),
            managerDefining: t('manager_defining', 'Manager defining'),
            total: t('total', 'Total'),
            deliveryZone: t('delivery_zone', 'Delivery zone'),
            cartItemsUnavailableTitle: t('cart_items_unavailable_title', 'Some items are no longer available.'),
            cartItemsUnavailableBody: t('cart_items_unavailable_body', 'We removed them from your cart. Please review your order before continuing.'),
            connectWithSupport: t('connect_with_support', 'Connect with support'),
            prepareChangeAmount: t('what_amount', 'From what amount?'),
            reset: t('reset', 'Reset'),
            deliveryInstructionsPlaceholder: t('additional_instructions', 'Дополнительные инструкции для курьера'),
          }}
        />

        <ProfileAuthModal
          show={isProfileModalOpen}
          onHide={() => setIsProfileModalOpen(false)}
          labels={{
            profile: t('profile', 'Profile'),
            signIn: t('sign_in', 'Sign in'),
            email: t('email', 'Email'),
            sendMagicLink: t('send_magic_link', 'Send login link'),
            magicLinkSent: t('magic_link_sent', 'Check your email for the login link.'),
            google: t('continue_with_google', 'Continue with Google'),
            telegram: t('continue_with_telegram', 'Continue with Telegram'),
            authUnavailable: t('auth_unavailable', 'Authorization is temporarily unavailable'),
            name: t('your_name', 'Your name'),
            preferredMethod: t('preferred_connection_method', 'Preferred contact method'),
            countryCode: t('select_the_country_code', 'Country code'),
            phone: t('phone_call', 'Phone number'),
            telegramUsername: t('telegram_username', '@username'),
            language: t('language', 'Language'),
            location: t('location', 'Location'),
            address: t('address', 'Address'),
            save: t('save', 'Save'),
            logout: t('logout', 'Log out'),
            close: t('close', 'Close'),
            saving: t('saving', 'Saving...'),
          }}
        />

        <Modal show={isClosedHoursModalOpen} onHide={() => setIsClosedHoursModalOpen(false)} centered contentClassName="border-0 rounded-4">
          <Modal.Header closeButton className="border-0">
            <Modal.Title>{activeNoticePopupTitle}</Modal.Title>
          </Modal.Header>
          <Modal.Body className="pt-0">
            <div dangerouslySetInnerHTML={{ __html: activeNoticePopupHtml ?? '' }} />
          </Modal.Body>
          <Modal.Footer className="border-0 pt-0">
            <Button variant="success" className="w-100 rounded-pill py-2 fw-bold" onClick={() => setIsClosedHoursModalOpen(false)}>
              {t('close', 'Close')}
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal show={isOrdersInfoModalOpen} onHide={() => setIsOrdersInfoModalOpen(false)} centered contentClassName="border-0 rounded-4">
          <Modal.Header closeButton className="border-0">
            <Modal.Title>{t('orders', 'Orders')}</Modal.Title>
          </Modal.Header>
          <Modal.Body className="pt-0">
            {t('orders_section_coming_soon', 'This section is under development and will be available soon.')}
          </Modal.Body>
          <Modal.Footer className="border-0 pt-0">
            <Button variant="success" className="w-100 rounded-pill py-2 fw-bold" onClick={() => setIsOrdersInfoModalOpen(false)}>
              {t('close', 'Close')}
            </Button>
          </Modal.Footer>
        </Modal>

        <nav className="position-sticky bottom-0 bg-white border-top px-2 py-2">
          <div className="d-flex align-items-end text-secondary text-uppercase">
            {renderBottomNavButton({
              onClick: () => window.scrollTo({ top: 0, behavior: 'smooth' }),
              buttonClassName: 'text-success',
              iconContent: <FaHome />,
              label: t('home', 'Home'),
            })}
            {renderBottomNavButton({
              onClick: () => setIsProfileModalOpen(true),
              ariaLabel: t('profile', 'Profile'),
              buttonClassName: auth?.authStatus === 'authenticated' ? 'text-success' : 'text-secondary',
              iconContent: <FaUser />,
              label: t('profile', 'Profile'),
            })}
            {renderBottomNavButton({
              onClick: () => {
                setCheckoutStep('cart');
                setIsCartOpen(true);
              },
              ariaLabel: t('cart', 'Cart'),
              testId: 'cart-open-button',
              buttonClassName: cartItemsCount > 0 ? 'text-success' : 'text-secondary',
              iconContent: (
                <>
                  <span
                    className="d-inline-flex align-items-center justify-content-center"
                    style={{
                      width: '42px',
                      height: '42px',
                      borderRadius: '999px',
                      backgroundColor: cartItemsCount > 0 ? '#F4C20D' : 'transparent',
                      boxShadow: cartItemsCount > 0 ? '0 0.35rem 0.9rem rgba(244, 194, 13, 0.35)' : 'none',
                      color: cartItemsCount > 0 ? '#FFFFFF' : '#6B7280',
                    }}
                  >
                    <FaShoppingCart />
                  </span>
                  {cartItemsCount > 0 && (
                    <span
                      className="position-absolute top-0 start-100 translate-middle d-inline-flex align-items-center justify-content-center rounded-circle text-white fw-bold border border-white"
                      style={{
                        minWidth: '22px',
                        height: '22px',
                        padding: '0 5px',
                        backgroundColor: '#DC3545',
                        fontSize: '11px',
                        lineHeight: 1,
                      }}
                    >
                      {cartItemsCount}
                    </span>
                  )}
                </>
              ),
              label: (
                <span
                  style={{
                    color: cartItemsCount > 0 ? '#1B7F3A' : '#6B7280',
                  }}
                >
                  {t('cart', 'Cart')}
                </span>
              ),
            })}
            {renderBottomNavButton({
              onClick: () => setIsOrdersInfoModalOpen(true),
              buttonClassName: 'text-secondary',
              iconContent: <FaList />,
              label: t('orders', 'Orders'),
            })}
            {renderBottomNavButton({
              onClick: () => document.getElementById('menu-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
              buttonClassName: 'text-secondary',
              iconContent: <FaUtensils />,
              label: t('menu_button', 'Menu'),
            })}
          </div>
        </nav>
      </Container>
    </div>
  );
};

/**
 * Menu
 *
 * Fetches page-level system texts for the backend `menu` page and provides them
 * to the page through PageTextsDataContext so the nested UI can use translated
 * interface labels loaded from `/texts`.
 */
const Menu: React.FC = () => {
  const sessionData = useSessionData();
  const [pageTextsData, setPageTextsData] = useState<TranslationsType>({});

  useEffect(() => {
    fetchPageTranslations('menu', sessionData?.lang ?? 'en', undefined, setPageTextsData);
  }, [sessionData?.lang]);

  return (
    <PageTextsDataContext.Provider value={pageTextsData}>
      <MenuContent />
    </PageTextsDataContext.Provider>
  );
};

export default Menu;
